<template>
  <div class="dispatch-order-management">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <h2>派工单管理</h2>
          <div class="header-actions">
            <el-badge
              :value="pendingKickbackCount"
              :hidden="pendingKickbackCount < 1"
              type="danger"
              class="create-dispatch-badge"
            >
              <el-button
                v-if="canReviewKickback"
                type="danger"
                plain
                @click="openKickbackDrawer"
              >
                踢回配料申请
              </el-button>
            </el-badge>
            <el-badge
              :value="pendingPackagingAccessCount"
              :hidden="pendingPackagingAccessCount < 1"
              type="warning"
              class="create-dispatch-badge"
            >
              <el-button
                v-if="canReviewKickback"
                type="warning"
                plain
                @click="openPackagingAccessDrawer"
              >
                包装填写申请
              </el-button>
            </el-badge>
            <el-button @click="handleShowColumnConfig">
              <el-icon><Setting /></el-icon> 列设置
            </el-button>
            <el-tooltip
              content="按客户与订单类型预设袋型、称重与量体积；生成派工单时自动带入对应明细，减少逐行点选。"
              placement="bottom"
            >
              <el-button @click="customerPackagingRulesVisible = true">
                <el-icon><Setting /></el-icon> 客户包装要求
              </el-button>
            </el-tooltip>
            <el-button @click="loadData">
              <el-icon><Refresh /></el-icon> 刷新
            </el-button>
            <el-badge
              :value="pendingDispatchCount"
              :hidden="pendingDispatchCount < 1"
              type="warning"
              class="create-dispatch-badge"
            >
              <el-button type="primary" :disabled="!canOperateDispatch" @click="handleCreateDispatchOrder">
                <el-icon><Plus /></el-icon> 生成派工单
              </el-button>
            </el-badge>
          </div>
        </div>
      </template>

      <el-tabs v-model="activeMainTab" class="dispatch-main-tabs">
        <el-tab-pane label="派工单列表" name="list">
      <div
        v-if="pendingDispatchCount > 0"
        class="pending-dispatch-banner"
        role="status"
      >
        <div class="pending-dispatch-banner__count">{{ pendingDispatchCount }}</div>
        <div class="pending-dispatch-banner__body">
          <div class="pending-dispatch-banner__title">待生成派工单</div>
          <p class="pending-dispatch-banner__desc">
            配料已完结且已选材料、尚未加入未取消派工单的订单明细共上述条数，请打开「生成派工单」勾选并生成。
          </p>
        </div>
        <el-button type="warning" size="large" :disabled="!canOperateDispatch" @click="handleCreateDispatchOrder">
          立即生成
        </el-button>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-section">
        <el-input
          v-model="searchKeyword"
          placeholder="派工单号、订单号、客户、材料…"
          class="filter-section__search"
          clearable
          @clear="handleSearch"
          @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button @click="handleSearch">
              <el-icon><Search /></el-icon>
            </el-button>
          </template>
        </el-input>
        <el-input
          v-model="filterWarehouseManualCode"
          placeholder="仓库料号"
          class="filter-section__wh-code"
          clearable
          @clear="handleSearch"
          @keyup.enter="handleSearch"
        />
        <el-select
          v-model="filterStatus"
          placeholder="派工状态"
          clearable
          class="filter-section__ctl"
          @change="handleSearch"
        >
          <el-option label="草稿" value="draft" />
          <el-option label="已指派" value="assigned" />
          <el-option label="进行中" value="in_progress" />
          <el-option label="已完成" value="completed" />
          <el-option label="已取消" value="cancelled" />
        </el-select>
        <el-select
          v-model="filterAuditStatus"
          placeholder="派工审核"
          clearable
          class="filter-section__ctl"
          @change="handleSearch"
        >
          <el-option label="待审核" value="pending" />
          <el-option label="已通过" value="approved" />
          <el-option label="已拒绝" value="rejected" />
        </el-select>
        <el-select
          v-model="filterAssignedUserId"
          placeholder="指派人员"
          clearable
          filterable
          class="filter-section__ctl filter-section__ctl--wide"
          @change="handleSearch"
        >
          <el-option
            v-for="user in users"
            :key="user.user_id"
            :label="user.full_name || user.username"
            :value="user.user_id"
          />
        </el-select>
        <el-select
          v-model="filterPostCompletionStatus"
          placeholder="现场核销"
          clearable
          class="filter-section__ctl filter-section__ctl--wide"
          @change="handleSearch"
        >
          <el-option label="无需 / 未发起" value="na" />
          <el-option label="待核销" value="pending" />
          <el-option label="已核销" value="approved" />
          <el-option label="已退回" value="rejected" />
        </el-select>
      </div>

      <!-- 派工单列表表格 -->
      <div class="table-container dispatch-order-list-scroll">
        <el-table
          ref="tableRef"
          :data="tableData"
          v-loading="loading"
          border
          stripe
          class="dispatch-order-list-table"
          style="width: 100%"
          @row-click="handleRowClick"
        >
          <template v-for="col in visibleColumns" :key="col.prop">
            <!-- 派工单号 -->
            <el-table-column 
              v-if="col.prop === 'dispatch_order_no'"
              prop="dispatch_order_no" 
              :label="col.label" 
              :width="col.width" 
              :fixed="col.fixed"
            />
            
            <!-- 分卷确认进度（移动端「本分卷已确认」条数 / 明细条数） -->
            <el-table-column
              v-else-if="col.prop === 'mobile_slitting_progress'"
              :label="col.label"
              :width="col.width"
              align="center"
            >
              <template #default="{ row }">
                {{ formatMobileSlittingProgress(row) }}
              </template>
            </el-table-column>

            <!-- 状态 -->
            <el-table-column 
              v-else-if="col.prop === 'status'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                <el-tag
                  :type="getDispatchRowStatusTagType(row)"
                  :title="
                    dispatchSlittingProgressIncomplete(row)
                      ? '仓库流程已点完成，但移动端「本分卷确认」尚未全部完成，此处显示为进行中'
                      : ''
                  "
                >
                  {{ getDispatchRowStatusText(row) }}
                </el-tag>
              </template>
            </el-table-column>
            
            <!-- 指派人员 -->
            <el-table-column 
              v-else-if="col.prop === 'assigned_user_name'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.assigned_user_name || '-' }}
              </template>
            </el-table-column>

            <!-- 要求完成（急单/正常/普通/特急） -->
            <el-table-column
              v-else-if="col.prop === 'required_completion_summary'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                <template v-if="!row.required_completion_at && !row.required_completion_immediate">—</template>
                <div v-else class="due-summary-cell" :class="rowDueSummary(row).lineClass">
                  <el-tag
                    v-if="rowDueSummary(row).label"
                    size="small"
                    :type="rowDueSummary(row).tagType"
                  >
                    {{ rowDueSummary(row).label }}
                  </el-tag>
                  <span v-if="rowDueSummary(row).dateLine" class="due-summary-cell__date">{{
                    rowDueSummary(row).dateLine
                  }}</span>
                </div>
              </template>
            </el-table-column>

            <!-- 明细数量 -->
            <el-table-column 
              v-else-if="col.prop === 'item_count'"
              :label="col.label" 
              :width="col.width"
              align="center"
            >
              <template #default="{ row }">
                {{ row.item_count || 0 }}
              </template>
            </el-table-column>

            <!-- 派工数量 -->
            <el-table-column
              v-else-if="col.prop === 'dispatch_quantity'"
              :label="col.label"
              :width="col.width"
              align="right"
            >
              <template #default="{ row }">
                {{ formatNumber(row.dispatch_quantity || 0) }}
              </template>
            </el-table-column>

            <!-- 明细摘要（多客户/多料号等聚合，便于扫一眼） -->
            <el-table-column
              v-else-if="col.prop === 'items_list_summary'"
              :label="col.label"
              :min-width="col.width"
            >
              <template #default="{ row }">
                {{ row.items_list_summary || '—' }}
              </template>
            </el-table-column>
            
            <!-- 备注 -->
            <el-table-column 
              v-else-if="col.prop === 'notes'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.notes || '-' }}
              </template>
            </el-table-column>
            
            <!-- 创建人 -->
            <el-table-column 
              v-else-if="col.prop === 'creator_name'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.creator_name || '-' }}
              </template>
            </el-table-column>
            
            <!-- 创建时间 -->
            <el-table-column 
              v-else-if="col.prop === 'created_at'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ formatDateTime(row.created_at) }}
              </template>
            </el-table-column>
            
            <!-- 更新时间 -->
            <el-table-column 
              v-else-if="col.prop === 'updated_at'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ formatDateTime(row.updated_at) }}
              </template>
            </el-table-column>
            
            <!-- 审核状态 -->
            <el-table-column 
              v-else-if="col.prop === 'audit_status'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                <el-tag :type="getAuditStatusTagType(row.audit_status)">
                  {{ getAuditStatusText(row.audit_status) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              v-else-if="col.prop === 'post_completion_audit_status'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                <el-tag :type="getPostCompletionAuditTagType(row)">
                  {{ getPostCompletionAuditText(row) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              v-else-if="col.prop === 'packaging_audit_status'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                <el-tag :type="getPackagingAuditTagType(row.packaging_audit_status)">
                  {{ getPackagingAuditText(row.packaging_audit_status) }}
                </el-tag>
              </template>
            </el-table-column>
            
            <!-- 订单号 -->
            <el-table-column 
              v-else-if="col.prop === 'order_no'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.order_nos_display || row.order_no || '-' }}
              </template>
            </el-table-column>
            
            <!-- 客户采购单号 -->
            <el-table-column 
              v-else-if="col.prop === 'client_purchase_order'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.client_purchase_orders_display || row.client_purchase_order || '-' }}
              </template>
            </el-table-column>
            
            <!-- 订单日期 -->
            <el-table-column 
              v-else-if="col.prop === 'order_date'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ formatDate(row.order_date) }}
              </template>
            </el-table-column>
            
            <!-- 客户 -->
            <el-table-column 
              v-else-if="col.prop === 'customer_name'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.customers_display || row.customer_name || '-' }}
              </template>
            </el-table-column>
            
            <!-- 品牌 -->
            <el-table-column 
              v-else-if="col.prop === 'brand_name'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.brands_display || row.brand_name || '-' }}
              </template>
            </el-table-column>

            <!-- 季节（多明细聚合） -->
            <el-table-column
              v-else-if="col.prop === 'seasons_display'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.seasons_display || '—' }}
              </template>
            </el-table-column>

            <!-- 订单类型（多明细聚合） -->
            <el-table-column
              v-else-if="col.prop === 'order_types_display'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.order_types_display || '—' }}
              </template>
            </el-table-column>
            
            <!-- 客户英文颜色（多明细聚合） -->
            <el-table-column
              v-else-if="col.prop === 'client_colors_en_display'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.client_colors_en_display || '—' }}
              </template>
            </el-table-column>

            <!-- 已选料号 -->
            <el-table-column 
              v-else-if="col.prop === 'material_code'"
              :label="col.label" 
              :width="col.width"
            >
              <template #default="{ row }">
                {{
                  row.material_codes_display
                    || row.material_matching?.warehouse_lots_display
                    || row.material_matching?.selected_batch_display
                    || row.material_matching?.warehouse_manual_code
                    || row.material_code
                    || row.material_matching?.material_identity_code
                    || '-'
                }}
              </template>
            </el-table-column>

            <el-table-column
              v-else-if="col.prop === 'material_usage_notes'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.material_usage_notes || '-' }}
              </template>
            </el-table-column>

            <el-table-column
              v-else-if="col.prop === 'matching_remarks'"
              :label="col.label"
              :width="col.width"
            >
              <template #default="{ row }">
                {{ row.matching_remarks_summary || '-' }}
              </template>
            </el-table-column>
          </template>
          
          <!-- 操作列 -->
          <el-table-column label="操作" width="580" fixed="right">
            <template #default="{ row }">
              <div class="dispatch-list-ops">
              <el-button
                link
                type="primary"
                size="small"
                @click.stop="handleOpenPackaging(row)"
                :disabled="row.status === 'cancelled'"
              >
                包装要求
              </el-button>
              <el-button
                link
                type="warning"
                size="small"
                @click.stop="openPackagingMeasures(row)"
                :disabled="row.status === 'cancelled'"
              >
                包装实测
              </el-button>
              <el-button 
                link 
                type="primary" 
                size="small"
                @click.stop="handleEdit(row)"
                :disabled="!canOperateDispatch || row.audit_status === 'approved'"
              >
                编辑
              </el-button>
              <el-button 
                link 
                type="success" 
                size="small"
                @click.stop="handleAudit(row)"
                :disabled="!canAuditReview || row.audit_status === 'approved'"
              >
                审核
              </el-button>
              <el-button
                v-if="canRevokeAudit && (row.audit_status === 'approved' || row.audit_status === 'rejected')"
                link
                type="warning"
                size="small"
                @click.stop="handleRevokeAudit(row)"
              >
                取消审核
              </el-button>
              <el-button 
                link 
                type="primary" 
                size="small"
                @click.stop="handlePrint(row)"
              >
                打印
              </el-button>
              <el-button
                link
                type="warning"
                size="small"
                @click.stop="handlePrintBranchLabels(row)"
              >
                打印分码标签
              </el-button>
              <el-button 
                link 
                type="danger" 
                size="small"
                @click.stop="handleDelete(row)"
                :disabled="!canOperateDispatch || row.audit_status === 'approved'"
              >
                删除
              </el-button>
              <el-button
                v-if="
                  canRevokeVerification &&
                  row.status === 'completed' &&
                  (row.post_completion_audit_status || 'na') === 'approved'
                "
                link
                type="warning"
                size="small"
                :loading="postCompletionRevokeRowId === row.dispatch_order_id"
                @click.stop="handleRevokePostCompletionFromList(row)"
              >
                取消核销
              </el-button>
              <el-button
                v-if="
                  canAuditReview &&
                  row.status === 'completed' &&
                  (row.post_completion_audit_status || 'na') !== 'approved'
                "
                link
                type="warning"
                size="small"
                :loading="reopenDispatchRowId === row.dispatch_order_id"
                @click.stop="handleRequestReopenFromList(row)"
              >
                退回仓库修改
              </el-button>
              </div>
            </template>
          </el-table-column>
      </el-table>
    </div>
    
    <!-- 打印预览组件 -->
    <PrintPreview
      v-model="printPreviewVisible"
      :dialog-title="printDialogTitle"
      template-type="dispatch_order"
      :record-id="currentPrintRecordId"
      :initial-scenario-key="currentPrintScenario"
      @printed="handlePrintSuccess"
    />

      <!-- 分页控件 -->
      <div class="pagination-section">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadData"
          @current-change="loadData"
        />
      </div>
        </el-tab-pane>
        <el-tab-pane label="派工明细汇总" name="lines" lazy>
          <DispatchOrderLineDetailsTab />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-drawer
      v-model="kickbackDrawerVisible"
      title="踢回配料申请（待审批）"
      direction="rtl"
      size="min(920px, 96vw)"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      destroy-on-close
      @opened="loadKickbackPendingList"
    >
      <p class="kickback-desc">
        现场无法完成某条派工明细时提交的申请；同意后该明细会从派工单移除，对应订单行退回配料待处理。
        若申请来自「合并数量」行，同意或驳回将作用于合并组内全部订单明细（列表中同组可能出现多行，处理其一即可整组审结）。
      </p>
      <el-table
        v-loading="kickbackListLoading"
        :data="kickbackList"
        stripe
        border
        empty-text="暂无待审批申请"
        style="width: 100%"
        row-key="kickback_request_id"
      >
        <el-table-column type="expand" width="48">
          <template #default="{ row }">
            <div v-if="row.item_detail" class="kickback-item-detail">
              <el-descriptions :column="2" border size="small" title="派工材料明细">
                <el-descriptions-item label="客户采购单号">
                  {{ row.item_detail.client_purchase_order || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="品牌">
                  {{ row.item_detail.brand_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="客户材料名称">
                  {{ row.item_detail.client_material_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="订单类型">
                  {{ row.item_detail.order_type_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="客户颜色">
                  {{ formatKickbackColor(row.item_detail) }}
                </el-descriptions-item>
                <el-descriptions-item label="数量">
                  {{ formatKickbackQty(row.item_detail) }}
                </el-descriptions-item>
                <el-descriptions-item label="内部材料名称">
                  {{ row.item_detail.internal_product_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="内部颜色(中)">
                  {{ row.item_detail.internal_color_cn || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="内部颜色(英)">
                  {{ row.item_detail.internal_color_en || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="规格">
                  {{ row.item_detail.specification || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="系统料号">
                  {{ row.item_detail.material_identity_code || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="仓库料号">
                  {{ row.item_detail.warehouse_manual_code || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="系统批号">
                  {{ row.item_detail.material_batch_number || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="配料材料名称" :span="2">
                  {{ row.item_detail.material_category_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="配料颜色" :span="2">
                  {{
                    [row.item_detail.material_color_name_cn, row.item_detail.material_color_name_en]
                      .filter(Boolean)
                      .join(' / ') || '—'
                  }}
                </el-descriptions-item>
                <el-descriptions-item label="配料规格" :span="2">
                  {{ row.item_detail.material_specification || '—' }}
                </el-descriptions-item>
                <el-descriptions-item v-if="row.item_detail.matching_remarks" label="配料备注" :span="2">
                  {{ row.item_detail.matching_remarks }}
                </el-descriptions-item>
                <el-descriptions-item v-if="row.item_detail.material_usage_notes" label="用料备注" :span="2">
                  {{ row.item_detail.material_usage_notes }}
                </el-descriptions-item>
                <el-descriptions-item v-if="row.item_detail.item_notes" label="明细备注一" :span="2">
                  {{ row.item_detail.item_notes }}
                </el-descriptions-item>
                <el-descriptions-item v-if="row.item_detail.item_notes_2" label="明细备注二" :span="2">
                  {{ row.item_detail.item_notes_2 }}
                </el-descriptions-item>
                <el-descriptions-item v-if="row.item_detail.item_notes_3" label="明细备注三" :span="2">
                  {{ row.item_detail.item_notes_3 }}
                </el-descriptions-item>
                <el-descriptions-item
                  v-if="row.item_detail.branch_record_count"
                  label="分码"
                  :span="2"
                >
                  已录 {{ row.item_detail.branch_record_count }} 支
                  <template v-if="row.item_detail.branch_labels?.length">
                    （{{ row.item_detail.branch_labels.join('、') }}）
                  </template>
                </el-descriptions-item>
              </el-descriptions>
            </div>
            <span v-else class="kickback-item-detail-empty">暂无材料明细（订单行可能已删除）</span>
          </template>
        </el-table-column>
        <el-table-column prop="dispatch_order_no" label="派工单号" min-width="120" />
        <el-table-column prop="order_no" label="订单号" min-width="110">
          <template #default="{ row }">
            <div>{{ row.order_no || '—' }}</div>
            <el-tag
              v-if="(row.item_detail?.merge_group_member_count || 0) > 1"
              size="small"
              type="warning"
              class="kickback-merge-tag"
            >
              合并整组 {{ row.item_detail.merge_group_member_count }} 条
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="材料摘要" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">
            {{ formatKickbackMaterialSummary(row.item_detail) }}
          </template>
        </el-table-column>
        <el-table-column label="内部材料名称" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            {{
              row.item_detail?.internal_product_name ||
              row.item_detail?.material_category_name ||
              '—'
            }}
          </template>
        </el-table-column>
        <el-table-column label="内部颜色(中)" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.item_detail?.internal_color_cn || row.item_detail?.material_color_name_cn || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="内部颜色(英)" min-width="110" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.item_detail?.internal_color_en || row.item_detail?.material_color_name_en || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="仓库料号" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">
            {{
              row.item_detail?.warehouse_manual_code ||
              row.item_detail?.material_identity_code ||
              '—'
            }}
          </template>
        </el-table-column>
        <el-table-column prop="customer_name" label="客户" min-width="100" show-overflow-tooltip />
        <el-table-column prop="requester_name" label="申请人" width="100" />
        <el-table-column prop="requested_at" label="申请时间" width="168" />
        <el-table-column prop="reason" label="原因" min-width="200" show-overflow-tooltip />
        <el-table-column v-if="canReviewKickback" label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-button
              type="success"
              link
              :loading="kickbackActionId === row.kickback_request_id && kickbackAction === 'approve'"
              @click="approveKickback(row)"
            >
              同意移出
            </el-button>
            <el-button
              type="danger"
              link
              :loading="kickbackActionId === row.kickback_request_id && kickbackAction === 'reject'"
              @click="rejectKickback(row)"
            >
              驳回
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-drawer>

    <el-drawer
      v-model="packagingAccessDrawerVisible"
      title="包装填写权限申请（待审批）"
      direction="rtl"
      size="min(920px, 96vw)"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      destroy-on-close
      @opened="loadPackagingAccessPendingList"
    >
      <p class="kickback-desc">
        现场人员申请负责本单包装称重/尺寸填写；同意后该人员将锁定为本单包装填写人，其他人不可再改包装实测。
      </p>
      <el-table
        v-loading="packagingAccessListLoading"
        :data="packagingAccessList"
        stripe
        border
        empty-text="暂无待审批申请"
        style="width: 100%"
      >
        <el-table-column prop="dispatch_order_no" label="派工单号" min-width="120" />
        <el-table-column prop="requester_name" label="申请人" width="100" />
        <el-table-column prop="assigned_user_name" label="派工指派" width="100" />
        <el-table-column prop="current_packaging_operator_name" label="当前填写人" width="110" show-overflow-tooltip>
          <template #default="{ row }">{{ row.current_packaging_operator_name || '未锁定' }}</template>
        </el-table-column>
        <el-table-column prop="requested_at" label="申请时间" width="168" />
        <el-table-column prop="reason" label="说明" min-width="160" show-overflow-tooltip />
        <el-table-column label="快捷" width="168" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="openPackagingMeasuresFromAccessRow(row)">包装实测</el-button>
            <el-button link @click="openDispatchDetailFromPackagingAccess(row)">派工详情</el-button>
          </template>
        </el-table-column>
        <el-table-column v-if="canReviewKickback" label="审批" width="160" fixed="right">
          <template #default="{ row }">
            <el-button
              type="success"
              link
              :loading="packagingAccessActionId === row.access_request_id && packagingAccessAction === 'approve'"
              @click="approvePackagingAccess(row)"
            >
              同意指定
            </el-button>
            <el-button
              type="danger"
              link
              :loading="packagingAccessActionId === row.access_request_id && packagingAccessAction === 'reject'"
              @click="rejectPackagingAccess(row)"
            >
              驳回
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-drawer>

    <DispatchPackagingMeasuresDialog
      v-model="packagingMeasuresVisible"
      :dispatch-order-id="packagingMeasuresDispatchOrderId"
      @saved="onPackagingMeasuresSaved"
      @access-changed="onPackagingAccessChanged"
    />

    <!-- 列设置对话框 -->
    <ColumnConfigDialog
      :show="showColumnConfig"
      :column-config="columnConfig"
      :page-title="'派工单管理'"
      storage-key="dispatch_order_list"
      :local-storage-key="`${dispatchColumnConfigStorageKey}_columns_config`"
      @update:show="showColumnConfig = $event"
      @config-change="handleColumnConfigChange"
    />

    <ColumnConfigDialog
      :show="showCreateDispatchColumnSettings"
      :column-config="createDispatchColumnDialogSeed"
      :page-title="'生成派工单 · 订单明细表'"
      :local-storage-key="createDispatchItemsColumnStorageKey"
      :allow-column-reorder="false"
      dialog-width="640px"
      @update:show="showCreateDispatchColumnSettings = $event"
      @config-change="onCreateDispatchColumnConfigChange"
    />

      <!-- 生成派工单对话框 - 选择订单明细 -->
    <el-dialog 
      v-model="createDialogVisible" 
      title="生成派工单 - 选择订单明细" 
      width="90%"
      draggable
      class="erp-nonmodal-work-dialog"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @closed="onCreateDialogClosed"
      @opened="onCreateDialogOpened"
    >
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="dispatch-nonmodal-hint"
        title="可切换其他菜单查阅资料，返回本页后未填写内容会保留；请勿点击「关闭」除非确认放弃。"
      />
      <p class="create-dialog-tip">
        下列订单行已在<strong>配料管理</strong>中标记为配料完成（或行动状态为已完成）且已选择材料料号，且尚未加入未取消的派工单。打开窗口会自动加载待派工明细；也可用下方筛选后点「搜索」缩小范围。
      </p>
      <div style="margin-bottom: 20px;">
        <el-form :inline="true" :model="createFormFilter">
          <el-form-item label="订单号/客户采购单号">
            <el-input
              v-model="createFormFilter.searchKeyword"
              placeholder="搜索订单号、客户采购单号..."
              style="width: 250px"
              clearable
              @keyup.enter="loadOrderItems"
            />
          </el-form-item>
          <el-form-item label="客户">
            <el-select
              v-model="createFormFilter.customerId"
              placeholder="选择客户"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="customer in customerList"
                :key="customer.customer_id"
                :label="customer.customer_name"
                :value="customer.customer_id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="品牌">
            <el-select
              v-model="createFormFilter.brandId"
              placeholder="选择品牌"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="brand in brandList"
                :key="brand.brand_id"
                :label="brand.brand_name"
                :value="brand.brand_id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="订单类型">
            <el-select
              v-model="createFormFilter.orderTypeId"
              placeholder="全部类型"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="ot in orderTypeFilterOptions"
                :key="ot.order_type_id"
                :label="ot.type_name_cn"
                :value="ot.order_type_id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="材料名称">
            <el-select
              v-model="createFormFilter.materialCategoryName"
              placeholder="选择材料名称"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="category in materialCategoryList"
                :key="category"
                :label="category"
                :value="category"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="颜色">
            <el-select
              v-model="createFormFilter.materialColorName"
              placeholder="选择颜色"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="color in materialColorList"
                :key="color"
                :label="color"
                :value="color"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="内部英文颜色">
            <el-select
              v-model="createFormFilter.internalColorEn"
              placeholder="配料英文颜色"
              filterable
              clearable
              style="width: 200px"
            >
              <el-option
                v-for="en in materialInternalColorEnList"
                :key="en"
                :label="en"
                :value="en"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="材料代码">
            <el-input
              v-model="createFormFilter.materialCode"
              placeholder="材料代码"
              style="width: 200px"
              clearable
              @keyup.enter="loadOrderItems"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="loadOrderItems">
              <el-icon><Search /></el-icon> 搜索
            </el-button>
            <el-button @click="resetCreateFormFilter">
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <div class="create-packaging-bulk-toolbar">
        <div class="create-packaging-bulk-toolbar__row">
          <span class="create-packaging-bulk-toolbar__label">批量包装</span>
          <el-radio-group v-model="createPackagingBulkScope" size="small">
            <el-radio-button label="current">当前列表</el-radio-button>
            <el-radio-button label="pool" :disabled="sortedSelectedCreateItems.length === 0">
              已选清单（{{ sortedSelectedCreateItems.length }} 条）
            </el-radio-button>
          </el-radio-group>
        </div>
        <div class="create-packaging-bulk-toolbar__row create-packaging-bulk-toolbar__buttons">
          <span class="create-packaging-bulk-toolbar__hint">袋型</span>
          <el-button-group size="small">
            <el-button @click="applyCreateBulkPackagingType('')">未指定</el-button>
            <el-button @click="applyCreateBulkPackagingType('transparent_bag')">透明袋</el-button>
            <el-button @click="applyCreateBulkPackagingType('woven_bag')">蛇皮袋</el-button>
          </el-button-group>
          <span class="create-packaging-bulk-toolbar__hint">称重</span>
          <el-button-group size="small">
            <el-button @click="applyCreateBulkWeigh(true)">全选</el-button>
            <el-button @click="applyCreateBulkWeigh(false)">全不选</el-button>
          </el-button-group>
          <span class="create-packaging-bulk-toolbar__hint">体积</span>
          <el-button-group size="small">
            <el-button @click="applyCreateBulkVolume(true)">全选</el-button>
            <el-button @click="applyCreateBulkVolume(false)">全不选</el-button>
          </el-button-group>
        </div>
        <p class="create-packaging-bulk-toolbar__tip">
          可一键全选后再逐行取消；若多数行不需要勾选，改用「全不选」再单独勾选更快。
        </p>
      </div>

      <div class="create-merge-qty-toolbar">
        <span class="create-merge-qty-toolbar__label">合并数量</span>
        <el-button type="primary" plain size="small" @click="applyCreatePendingMerge">
          合并选中「参与合并」行
        </el-button>
        <el-button size="small" @click="splitCreatePendingMergeFromTableSelection">
          拆分合并（按表格左侧勾选）
        </el-button>
        <span class="create-merge-qty-toolbar__hint">
          在行尾勾选「参与合并」，再点合并；未合并行「合并数量」与订单数量相同。拆分按左侧勾选清除合并。
        </span>
      </div>

      <div class="create-table-appearance-toolbar">
        <span class="create-table-appearance-toolbar__label">表格外观</span>
        <el-button type="primary" plain size="small" @click="showCreateDispatchColumnSettings = true">
          <el-icon><Setting /></el-icon>
          列设置
        </el-button>
        <el-switch
          v-model="createDispatchTableCellWrap"
          inline-prompt
          active-text="换行"
          inactive-text="单行"
          @change="persistCreateDispatchTableWrap"
        />
        <el-button text size="small" type="primary" @click="resetCreateDispatchItemsColumnWidths">
          重置列宽
        </el-button>
        <span class="create-table-appearance-toolbar__hint">
          可拖动表头竖线调列宽；打开「列设置」勾选显示列并改宽度。设置保存在本机。
        </span>
      </div>

      <!-- 订单明细表格 -->
      <el-table
        ref="orderItemsTableRef"
        :data="createOrderItemsSortedForTable"
        :span-method="createMergedQtySpanMethod"
        row-key="order_item_id"
        v-loading="orderItemsLoading"
        border
        stripe
        max-height="500px"
        style="width: 100%"
        class="create-dispatch-items-table"
        :class="{ 'create-dispatch-items-table--wrap': createDispatchTableCellWrap }"
        @selection-change="handleOrderItemsSelectionChange"
        @header-dragend="handleCreateDispatchItemsHeaderDragEnd"
      >
        <template #empty>
          <div class="create-dispatch-empty">
            <template v-if="orderItemsLoading">加载中…</template>
            <template v-else>
              暂无可派工订单明细。请确认配料为「完成·待派工」且不在未取消派工单中。
              若配料池有条数而此处为空，请硬刷新或运行服务器诊断脚本
              General/Scripts/diagnose_completed_matchings.py。
            </template>
          </div>
        </template>
        <!-- 选择框列 -->
        <el-table-column type="selection" width="55" :fixed="true" :reserve-selection="true" />

        <!-- 订单号 -->
        <el-table-column
          v-if="getCreateCol('order_no').visible"
          column-key="order_no"
          prop="order_no"
          label="订单号"
          :width="getCreateCol('order_no').width"
          fixed="left"
        />

        <!-- 客户采购单号 -->
        <el-table-column
          v-if="getCreateCol('client_purchase_order').visible"
          column-key="client_purchase_order"
          prop="client_purchase_order"
          label="客户采购单号"
          :width="getCreateCol('client_purchase_order').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(a.client_purchase_order, b.client_purchase_order)"
        />

        <!-- 订单日期 -->
        <el-table-column
          v-if="getCreateCol('order_date').visible"
          column-key="order_date"
          label="订单日期"
          :width="getCreateCol('order_date').width"
        >
          <template #default="{ row }">
            {{ formatDate(row.order_date) }}
          </template>
        </el-table-column>

        <!-- 客户 -->
        <el-table-column
          v-if="getCreateCol('customer_name').visible"
          column-key="customer_name"
          prop="customer_name"
          label="客户"
          :width="getCreateCol('customer_name').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(a.customer_name, b.customer_name)"
        />

        <!-- 品牌 -->
        <el-table-column
          v-if="getCreateCol('brand_name').visible"
          column-key="brand_name"
          prop="brand_name"
          label="品牌"
          :width="getCreateCol('brand_name').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        />

        <el-table-column
          v-if="getCreateCol('season_row').visible"
          column-key="season_row"
          label="季节"
          :width="getCreateCol('season_row').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        >
          <template #default="{ row }">
            {{ formatSeasonRow(row) }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="getCreateCol('order_type_name').visible"
          column-key="order_type_name"
          label="订单类型"
          :width="getCreateCol('order_type_name').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(a.order_type_name, b.order_type_name)"
        >
          <template #default="{ row }">{{ row.order_type_name || '—' }}</template>
        </el-table-column>

        <!-- 客户材料名称 -->
        <el-table-column
          v-if="getCreateCol('client_material_name').visible"
          column-key="client_material_name"
          prop="client_material_name"
          label="客户材料名称"
          :width="getCreateCol('client_material_name').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        />

        <!-- 客户颜色 -->
        <el-table-column
          v-if="getCreateCol('client_color_cn').visible"
          column-key="client_color_cn"
          prop="client_color_cn"
          label="客户颜色"
          :width="getCreateCol('client_color_cn').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        />

        <el-table-column
          v-if="getCreateCol('client_color_en').visible"
          column-key="client_color_en"
          prop="client_color_en"
          label="客户英文颜色"
          :width="getCreateCol('client_color_en').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        />

        <!-- 内部材料名称 -->
        <el-table-column
          v-if="getCreateCol('internal_product_name').visible"
          column-key="internal_product_name"
          label="内部材料名称"
          :width="getCreateCol('internal_product_name').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(displayInternalMaterialName(a), displayInternalMaterialName(b))"
        >
          <template #default="{ row }">{{ displayInternalMaterialName(row) || '—' }}</template>
        </el-table-column>

        <!-- 内部颜色 -->
        <el-table-column
          v-if="getCreateCol('internal_color_cn').visible"
          column-key="internal_color_cn"
          prop="internal_color_cn"
          label="内部颜色"
          :width="getCreateCol('internal_color_cn').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        />

        <el-table-column
          v-if="getCreateCol('internal_color_en').visible"
          column-key="internal_color_en"
          label="内部英文颜色"
          :width="getCreateCol('internal_color_en').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(internalColorEn(a), internalColorEn(b))"
        >
          <template #default="{ row }">{{ internalColorEn(row) || '—' }}</template>
        </el-table-column>

        <!-- 规格 -->
        <el-table-column
          v-if="getCreateCol('spec').visible"
          column-key="spec"
          label="规格"
          :width="getCreateCol('spec').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        >
          <template #default="{ row }">
            {{ row.material_matching?.material_specification || row.client_style_no || '-' }}
          </template>
        </el-table-column>

        <!-- 订单数量 / 合并数量 -->
        <el-table-column
          v-if="getCreateCol('order_qty').visible"
          column-key="order_qty"
          label="订单数量"
          :width="getCreateCol('order_qty').width"
          align="right"
        >
          <template #default="{ row }">
            {{ formatNumber(row.quantity) }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="getCreateCol('merged_qty').visible"
          column-key="merged_qty"
          label="合并数量"
          :width="getCreateCol('merged_qty').width"
          align="right"
          class-name="dispatch-merged-qty-cell"
        >
          <template #default="{ row }">
            {{ formatNumber(mergedQtyPreviewForCreateRow(row)) }}
          </template>
        </el-table-column>

        <!-- 单位 -->
        <el-table-column
          v-if="getCreateCol('unit_name').visible"
          column-key="unit_name"
          prop="unit_name"
          label="单位"
          :width="getCreateCol('unit_name').width"
        />

        <!-- 已选料号 -->
        <el-table-column
          v-if="getCreateCol('warehouse_code').visible"
          column-key="warehouse_code"
          label="已选料号"
          :width="getCreateCol('warehouse_code').width"
          sortable
          :sort-method="(a, b) => cmpLocaleStr(displayWarehouseCode(a), displayWarehouseCode(b))"
        >
          <template #default="{ row }">
            {{ selectedBatchDisplay(row.material_matching) }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="getCreateCol('matching_remarks').visible"
          column-key="matching_remarks"
          label="配料备注"
          :width="getCreateCol('matching_remarks').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        >
          <template #default="{ row }">
            {{ matchingRemarksFromRow(row) || '—' }}
          </template>
        </el-table-column>

        <el-table-column
          v-if="getCreateCol('material_usage_notes').visible"
          column-key="material_usage_notes"
          label="用料备注"
          :width="getCreateCol('material_usage_notes').width"
        >
          <template #default="{ row }">
            <el-input
              v-model="row.material_usage_notes"
              type="textarea"
              :rows="2"
              placeholder="如反面当正面、指定卷号等"
              size="small"
            />
          </template>
        </el-table-column>

        <!-- 库存位置 -->
        <el-table-column
          v-if="getCreateCol('stock_location').visible"
          column-key="stock_location"
          label="库存位置"
          :width="getCreateCol('stock_location').width"
          :show-overflow-tooltip="!createDispatchTableCellWrap"
        >
          <template #default="{ row }">
            <div v-if="getMatchingLocationsForCreate(row).length > 0">
              <div v-for="(loc, index) in getMatchingLocationsForCreate(row)" :key="index" style="margin-bottom: 4px;">
                {{ formatStorageLocationLabel(loc) }}
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <!-- 库存数量 -->
        <el-table-column
          v-if="getCreateCol('stock_qty').visible"
          column-key="stock_qty"
          label="库存数量"
          :width="getCreateCol('stock_qty').width"
          align="right"
        >
          <template #default="{ row }">
            <div v-if="getMatchingLocationsForCreate(row).length > 0">
              <div v-for="(loc, index) in getMatchingLocationsForCreate(row)" :key="index" style="margin-bottom: 4px;">
                {{ formatNumber(loc.available_quantity) }}
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <!-- 总可用库存 -->
        <el-table-column
          v-if="getCreateCol('total_avail').visible"
          column-key="total_avail"
          label="总可用库存"
          :width="getCreateCol('total_avail').width"
          align="right"
        >
          <template #default="{ row }">
            <span v-if="row.material_matching?.total_available_quantity !== undefined">
              {{ formatNumber(row.material_matching.total_available_quantity) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="getCreateCol('packaging_type').visible"
          column-key="packaging_type"
          label="袋型"
          :width="getCreateCol('packaging_type').width"
        >
          <template #default="{ row }">
            <el-select v-model="row.packaging_type" placeholder="未指定" clearable size="small" style="width: 100%">
              <el-option label="未指定" value="" />
              <el-option label="透明袋" value="transparent_bag" />
              <el-option label="蛇皮袋" value="woven_bag" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          v-if="getCreateCol('weigh').visible"
          column-key="weigh"
          label="称重"
          :width="getCreateCol('weigh').width"
          align="center"
        >
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_weigh" />
          </template>
        </el-table-column>
        <el-table-column
          v-if="getCreateCol('volume').visible"
          column-key="volume"
          label="体积"
          :width="getCreateCol('volume').width"
          align="center"
        >
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_volume" />
          </template>
        </el-table-column>

        <el-table-column
          v-if="getCreateCol('merge_candidate').visible"
          column-key="merge_candidate"
          label="参与合并"
          :width="getCreateCol('merge_candidate').width"
          align="center"
          fixed="right"
        >
          <template #default="{ row }">
            <el-checkbox
              :model-value="!!mergeCandidateByOrderItemId[row.order_item_id]"
              @update:model-value="(v) => { mergeCandidateByOrderItemId[row.order_item_id] = v }"
            />
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
          v-if="getCreateCol('operations').visible"
          column-key="operations"
          label="操作"
          :width="getCreateCol('operations').width"
          fixed="right"
        >
          <template #default="{ row }">
            <el-button
              type="warning"
              size="small"
              @click="handleReturnToMatching(row)"
            >
              返回配料
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="create-selected-panel">
        <div class="create-selected-panel__head">
          <div class="create-selected-panel__title">
            已选清单（跨搜索保留）共 {{ sortedSelectedCreateItems.length }} 条
          </div>
          <div class="create-selected-panel__actions">
            <el-select v-model="createSortMode" size="small" style="width: min(420px, 100%)">
              <el-option
                v-for="opt in CREATE_SORT_OPTIONS"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
            <el-button
              type="warning"
              plain
              size="small"
              @click="clearCreateSelectedPool"
              :disabled="!sortedSelectedCreateItems.length"
            >
              清空选择
            </el-button>
          </div>
        </div>
        <div v-if="!sortedSelectedCreateItems.length" class="create-selected-panel__empty">
          暂未勾选订单明细。可切换不同搜索词继续勾选，系统会累计保留。
        </div>
        <div v-else class="create-selected-panel__list">
          <div
            v-for="row in sortedSelectedCreateItems"
            :key="`sel-${row.order_item_id}`"
            class="create-selected-panel__item"
          >
            <span class="mono">{{ row.order_no || '-' }}</span>
            <span>{{ displayInternalMaterialName(row) || row.client_material_name || '—' }}</span>
            <span class="muted-inline">{{ internalColorEn(row) || '—' }}</span>
            <span class="muted-inline">{{ displayWarehouseCode(row) || '-' }}</span>
            <span class="muted-inline">{{ row.order_type_name || '—' }}</span>
            <span>{{ formatNumber(row.quantity) }}{{ row.unit_name || '' }}</span>
            <el-button type="danger" link size="small" @click="removeCreateSelectedItem(row.order_item_id)">
              移除
            </el-button>
          </div>
        </div>
      </div>

      <!-- 合并分码 / 出库总量参考（同一材料身份） -->
      <div v-if="createMergeGroups.length" class="create-merge-panel">
        <div class="create-merge-panel__title">合并分码 / 出库总量参考</div>
        <p class="create-merge-panel__hint">
          多笔订单若材料身份相同，仓库可按<strong>总数</strong>备料、分卷（不必按订单逐卷）；若客户要求严格按订单数量分卷，请选用下方备注策略二或在本行「用料备注」中写明。
        </p>
        <el-collapse>
          <el-collapse-item
            v-for="grp in createMergeGroups"
            :key="grp.key"
            :title="`${grp.material_identity_code || grp.category_name || '材料'} · 合计 ${formatNumber(grp.totalQty)}${grp.unit_name || ''}`"
          >
            <el-table :data="grp.rows" border size="small">
              <el-table-column prop="order_no" label="订单号" width="110" />
              <el-table-column prop="order_type_name" label="订单类型" width="110" show-overflow-tooltip />
              <el-table-column label="客户英文颜色" min-width="110" show-overflow-tooltip>
                <template #default="{ row }">{{ clientColorEn(row) || '—' }}</template>
              </el-table-column>
              <el-table-column label="内部英文颜色" min-width="120" show-overflow-tooltip>
                <template #default="{ row }">{{ internalColorEn(row) || '—' }}</template>
              </el-table-column>
              <el-table-column label="仓库料号" width="120" show-overflow-tooltip>
                <template #default="{ row }">{{ displayWarehouseCode(row) || '—' }}</template>
              </el-table-column>
              <el-table-column label="数量" width="90" align="right">
                <template #default="{ row }">{{ formatNumber(row.quantity) }}{{ row.unit_name || '' }}</template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
        <div class="create-merge-panel__strategy">
          <span class="create-merge-panel__strategy-label">生成派工单备注时自动追加</span>
          <el-radio-group v-model="dispatchMergeNoteStrategy" size="small">
            <el-radio-button label="off">不追加</el-radio-button>
            <el-radio-button label="merge_total">合并总量说明（总数够即可分卷）</el-radio-button>
            <el-radio-button label="per_order_lines">按订单行数量说明（须对齐订单）</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- 表单区域 -->
      <el-form ref="createDispatchFormRef" :model="dispatchOrderForm" :rules="dispatchEditRules" label-width="100px" style="margin-top: 20px;">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="指派人员">
              <el-select
                v-model="dispatchOrderForm.assigned_user_id"
                placeholder="请选择指派人员"
                filterable
                style="width: 100%"
                clearable
              >
                <el-option
                  v-for="user in users"
                  :key="user.user_id"
                  :label="user.full_name || user.username"
                  :value="user.user_id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="已选数量">
              <span>{{ sortedSelectedCreateItems.length }} 条</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注">
          <el-input
            v-model="dispatchOrderForm.notes"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
          />
        </el-form-item>
        <el-form-item label="多料号同卷">
          <el-checkbox v-model="dispatchOrderForm.allow_multi_batch_same_roll">
            特别提醒：允许不同料号分同一卷
          </el-checkbox>
          <el-input
            v-if="dispatchOrderForm.allow_multi_batch_same_roll"
            v-model="dispatchOrderForm.multi_batch_same_roll_notes"
            type="textarea"
            :rows="2"
            class="m-mt"
            style="margin-top: 8px"
            placeholder="现场操作说明（如：可混卷、按备注分卷等）"
          />
          <p class="packaging-hint" style="margin-top: 6px">
            未勾选时：多料号须分不同卷。勾选后现场可在同一卷填写多料号用量。
          </p>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="16">
            <el-form-item label="要求完成" prop="required_completion_at" required>
              <el-date-picker
                v-model="dispatchOrderForm.required_completion_at"
                type="datetime"
                placeholder="必填：选日期，默认当天 22:00 截止（或勾选右侧特急）"
                value-format="YYYY-MM-DDTHH:mm:ss"
                format="YYYY-MM-DD HH:mm"
                :default-time="dispatchDueDefaultTime"
                style="width: 100%"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label=" ">
              <el-checkbox v-model="dispatchOrderForm.required_completion_immediate">马上完成（特急单）</el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <div v-if="siteCapacityCaps && canOperateDispatch" class="dispatch-capacity-panel">
          <p class="dispatch-capacity-panel__rule">
            现场产能：同一指派人在同一统计日内，折算码数合计不超过
            <strong>{{ Number(siteCapacityCaps.max_yards_per_assignee_per_day).toLocaleString() }}</strong>，
            派工明细条数不超过
            <strong>{{ siteCapacityCaps.max_dispatch_items_per_assignee_per_day }}</strong>
            （yd 与 m 计码，其它单位只计条数；仅未完成单计入；特急按创建日/要求完成日；已完成与已核销不计）。
            <router-link v-if="canAdjustSiteCapacity" class="dispatch-capacity-panel__link" to="/admin/dispatch-site-capacity">调整上限</router-link>
          </p>
          <div v-loading="siteCapacityPreviewLoading" class="dispatch-capacity-panel__preview">
            <template v-if="siteCapacityPreview?.applies">
              <el-alert
                :type="siteCapacityPreview.ok ? 'success' : 'error'"
                :closable="false"
                show-icon
              >
                <template #title>本单占用预览（{{ siteCapacityPreview.due_date }}）</template>
                已用：{{ Number(siteCapacityPreview.used_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.used_items }} 条；
                本单：{{ Number(siteCapacityPreview.proposed_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.proposed_items }} 条；
                合计：{{ Number(siteCapacityPreview.total_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.total_items }} 条
                （上限 {{ Number(siteCapacityPreview.cap_yards || 0).toFixed(0) }} 码、{{ siteCapacityPreview.cap_items }} 条）。
              </el-alert>
            </template>
            <p v-else-if="siteCapacityPreview?.reason" class="dispatch-capacity-panel__muted">{{ siteCapacityPreview.reason }}</p>
          </div>
        </div>
        <p class="packaging-hint" style="margin: 0 0 8px">
          包装要求按<strong>每一行明细</strong>在上方表格中设置（袋型 / 是否称重 / 是否量体积）；已配置「客户包装要求」的明细会自动带入，也可用表格上方「批量包装」整批设置再微调。仓库移动端按对应行提示执行。
        </p>
      </el-form>

      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleConfirmCreate"
          :disabled="sortedSelectedCreateItems.length === 0"
        >
          确认生成
        </el-button>
      </template>
    </el-dialog>

    <!-- 包装要求（已审核等场景也可只改此项） -->
    <el-dialog
      v-model="packagingDialogVisible"
      title="各明细包装要求"
      width="720px"
      class="erp-nonmodal-work-dialog"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @closed="onPackagingDialogClosed"
    >
      <p class="packaging-hint">按行设置袋型与现场称重/量体积；保存后仓库移动端按对应明细提示执行。</p>
      <el-table :data="packagingLines" border max-height="420">
        <el-table-column prop="order_no" label="订单号" width="100" />
        <el-table-column label="用料备注" min-width="160">
          <template #default="{ row }">
            <el-input
              v-model="row.material_usage_notes"
              type="textarea"
              :rows="2"
              placeholder="如反面当正面、指定卷号等"
              size="small"
            />
          </template>
        </el-table-column>
        <el-table-column label="袋型" min-width="140">
          <template #default="{ row }">
            <el-select v-model="row.packaging_type" placeholder="未指定" clearable size="small" style="width: 100%">
              <el-option label="未指定" value="" />
              <el-option label="透明袋" value="transparent_bag" />
              <el-option label="蛇皮袋" value="woven_bag" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="称重" width="72" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_weigh" />
          </template>
        </el-table-column>
        <el-table-column label="体积" width="72" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_volume" />
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="packagingDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="packagingSaving" @click="handleSavePackaging">保存</el-button>
      </template>
    </el-dialog>

    <!-- 派工单详情 / 派工审核（第一轮）：同屏查看全部明细后再审核 -->
    <el-dialog 
      v-model="detailDialogVisible" 
      :title="detailDialogTitle" 
      width="92%"
      top="4vh"
      class="erp-nonmodal-work-dialog"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @closed="onDetailDialogClosed"
    >
      <el-alert
        v-if="hasTaskReturnQuery(route.query)"
        type="success"
        :closable="false"
        show-icon
        class="dispatch-nonmodal-hint"
        title="从任务中心打开：关闭本窗口后将返回对应任务列表（非派工板块首页）。"
      />
      <el-alert
        v-else
        type="info"
        :closable="false"
        show-icon
        class="dispatch-nonmodal-hint"
        title="可切换其他菜单查阅资料，返回本页后内容会保留；需要对照查看时可点底部「新标签页打开」。"
      />
      <div v-loading="detailLoading">
        <div v-if="currentDispatchOrder" style="margin-bottom: 20px;">
          <!-- 派工单基本信息 -->
          <el-card shadow="never" style="margin-bottom: 20px;">
            <template #header>
              <div class="detail-items-card-header">
                <span class="detail-items-card-header__title">基本信息</span>
                <div class="detail-items-card-header__actions">
                  <el-button
                    v-if="currentDispatchOrder.status !== 'cancelled'"
                    size="small"
                    type="warning"
                    plain
                    @click="openPackagingMeasuresForCurrent"
                  >
                    包装实测
                  </el-button>
                </div>
              </div>
            </template>
            <el-descriptions :column="3" border>
              <el-descriptions-item label="派工单号">
                {{ currentDispatchOrder.dispatch_order_no }}
              </el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag
                  :type="getDispatchRowStatusTagType(currentDispatchOrder)"
                  :title="
                    dispatchSlittingProgressIncomplete(currentDispatchOrder)
                      ? '仓库流程已点完成，但移动端「本分卷确认」尚未全部完成，此处显示为进行中'
                      : ''
                  "
                >
                  {{ getDispatchRowStatusText(currentDispatchOrder) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="现场分卷确认进度" :span="2">
                <div class="dispatch-slitting-progress">
                  <span class="dispatch-slitting-progress__text">
                    {{ mobileSlittingConfirmedDisplay }}
                  </span>
                  <el-progress
                    class="dispatch-slitting-progress__bar"
                    :percentage="mobileSlittingProgressPercent"
                    :stroke-width="12"
                  />
                  <el-button
                    size="small"
                    :loading="detailLoading"
                    @click="handleRefreshDispatchDetail"
                  >
                    刷新进度
                  </el-button>
                  <span class="muted-inline dispatch-slitting-progress__hint">
                    以移动端「本分卷已确认」为准，非工单状态「已完成」。
                  </span>
                </div>
              </el-descriptions-item>
              <el-descriptions-item label="指派人员">
                {{ currentDispatchOrder.assigned_user_name || '-' }}
              </el-descriptions-item>
              <template v-if="isProcessingOutboundDispatch(currentDispatchOrder)">
                <el-descriptions-item label="加工单号">
                  {{ currentDispatchOrder.processing_number || currentDispatchOrder.processing_id || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="跟进人">
                  {{ currentDispatchOrder.processing_follow_up_user_name || '—' }}
                </el-descriptions-item>
                <el-descriptions-item label="加工用途" :span="3">
                  {{
                    currentDispatchOrder.processing_purpose_text
                      || [
                        currentDispatchOrder.processing_processor_name
                          ? `给 ${currentDispatchOrder.processing_processor_name}`
                          : '',
                        currentDispatchOrder.processing_content_name
                          ? `做 ${currentDispatchOrder.processing_content_name}`
                          : '',
                      ].filter(Boolean).join('；')
                      || '—'
                  }}
                </el-descriptions-item>
                <el-descriptions-item label="订单需求">
                  {{
                    formatNumber(
                      currentDispatchOrder.order_required_quantity
                        ?? currentDispatchOrder.dispatch_quantity
                    )
                  }}
                  <span
                    v-if="currentDispatchOrder.order_required_order_count"
                    class="muted-inline"
                  >
                    （{{ currentDispatchOrder.order_required_order_count }} 单）
                  </span>
                </el-descriptions-item>
                <el-descriptions-item label="出库目标">
                  {{ formatNumber(currentDispatchOrder.dispatch_quantity) }}
                </el-descriptions-item>
              </template>
              <el-descriptions-item label="季节">
                {{ currentDispatchOrder.seasons_display || '—' }}
              </el-descriptions-item>
              <el-descriptions-item label="订单类型">
                {{ currentDispatchOrder.order_types_display || '—' }}
              </el-descriptions-item>
              <el-descriptions-item label="创建人">
                {{ currentDispatchOrder.creator_name || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="创建时间">
                {{ formatDateTime(currentDispatchOrder.created_at) }}
              </el-descriptions-item>
              <el-descriptions-item label="更新时间">
                {{ formatDateTime(currentDispatchOrder.updated_at) }}
              </el-descriptions-item>
              <el-descriptions-item label="备注" :span="3">
                {{ currentDispatchOrder.notes || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="多料号同卷" :span="3">
                <template v-if="currentDispatchOrder.allow_multi_batch_same_roll">
                  <el-tag type="warning" size="small">已开启特别提醒</el-tag>
                  <span v-if="currentDispatchOrder.multi_batch_same_roll_notes" style="margin-left: 8px">
                    {{ currentDispatchOrder.multi_batch_same_roll_notes }}
                  </span>
                </template>
                <template v-else>未开启（多料号须分不同卷）</template>
              </el-descriptions-item>
              <el-descriptions-item label="要求完成" :span="3">
                <template
                  v-if="!currentDispatchOrder.required_completion_at && !currentDispatchOrder.required_completion_immediate"
                >
                  —
                </template>
                <template v-else>
                  <el-tag v-if="detailDueSummary.label" size="small" :type="detailDueSummary.tagType">{{
                    detailDueSummary.label
                  }}</el-tag>
                  <span
                    v-if="detailDueSummary.dateLine"
                    class="due-summary-cell__date"
                    :class="detailDueSummary.lineClass"
                  >{{ detailDueSummary.dateLine }}</span>
                </template>
              </el-descriptions-item>
              <el-descriptions-item label="派工审核（第一轮）">
                <el-tag :type="getAuditStatusTagType(currentDispatchOrder.audit_status)">
                  {{ getAuditStatusText(currentDispatchOrder.audit_status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="打码现场核销">
                <el-tag :type="getPostCompletionAuditTagType(currentDispatchOrder)">
                  {{ getPostCompletionAuditText(currentDispatchOrder) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="包装称重核销">
                <el-tag :type="getPackagingAuditTagType(currentDispatchOrder.packaging_audit_status)">
                  {{ getPackagingAuditText(currentDispatchOrder.packaging_audit_status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="整单包装(旧字段)" :span="3">
                {{ packagingTypeLabel(currentDispatchOrder.packaging_type) }}
                · 称重{{ currentDispatchOrder.packaging_require_weigh ? '是' : '否' }}
                · 体积{{ currentDispatchOrder.packaging_require_volume ? '是' : '否' }}
                <span class="muted-inline">（已改为按明细行；此处仅供兼容旧数据）</span>
              </el-descriptions-item>
              <el-descriptions-item
                v-if="hasDispatchWholeOrderPackMeasured(currentDispatchOrder)"
                label="整单包装实测(仓库)"
                :span="3"
              >
                {{ formatDispatchWholeOrderPackMeasured(currentDispatchOrder) }}
              </el-descriptions-item>
              <el-descriptions-item label="包装填写负责人">
                {{ currentDispatchOrder.packaging_operator_user_name || '未锁定（现场/业务均可填）' }}
              </el-descriptions-item>
              <el-descriptions-item label="填写人指定时间">
                {{
                  currentDispatchOrder.packaging_operator_assigned_at
                    ? formatDateTime(currentDispatchOrder.packaging_operator_assigned_at)
                    : '—'
                }}
              </el-descriptions-item>
              <el-descriptions-item label="包装实测最近保存">
                <template v-if="currentDispatchOrder.packaging_last_saved_by_name">
                  {{ currentDispatchOrder.packaging_last_saved_by_name }}
                  <template v-if="currentDispatchOrder.packaging_last_saved_at">
                    · {{ formatDateTime(currentDispatchOrder.packaging_last_saved_at) }}
                  </template>
                </template>
                <template v-else>—</template>
              </el-descriptions-item>
              <el-descriptions-item v-if="currentDispatchOrder.business_reopen_reason" label="最近退回原因" :span="3">
                {{ currentDispatchOrder.business_reopen_reason }}
              </el-descriptions-item>
            </el-descriptions>
          </el-card>

          <el-card v-if="showDetailPackagingSection" shadow="never" style="margin-bottom: 20px">
            <template #header>
              <span style="font-weight: bold">包装实测与填写权限</span>
            </template>
            <el-alert
              v-if="detailPackagingAccessPending && canReviewKickback"
              type="warning"
              :closable="false"
              show-icon
              style="margin-bottom: 12px"
              title="有待审批的包装填写申请"
            >
              <template #default>
                申请人 {{ detailPackagingAccessPending.requester_name || '—' }}
                · {{ formatDateTime(detailPackagingAccessPending.requested_at) }}
                <span v-if="detailPackagingAccessPending.reason"> · {{ detailPackagingAccessPending.reason }}</span>
                <div style="margin-top: 8px">
                  <el-button
                    type="success"
                    size="small"
                    :loading="packagingAccessActionId === detailPackagingAccessPending.access_request_id && packagingAccessAction === 'approve'"
                    @click="approvePackagingAccess(detailPackagingAccessPending)"
                  >
                    同意指定
                  </el-button>
                  <el-button
                    type="danger"
                    size="small"
                    plain
                    :loading="packagingAccessActionId === detailPackagingAccessPending.access_request_id && packagingAccessAction === 'reject'"
                    @click="rejectPackagingAccess(detailPackagingAccessPending)"
                  >
                    驳回
                  </el-button>
                  <el-button size="small" plain @click="openPackagingMeasuresForCurrent">打开包装实测</el-button>
                </div>
              </template>
            </el-alert>
            <p class="packaging-hint" style="margin-top: 0">
              分码与包装可由不同同事负责。业务可指定「包装填写负责人」；指定后仅该人（及业务）可改各支称重/外尺寸。各支录入人见分码展开或「包装实测」弹窗。
            </p>
            <div
              v-if="canReviewKickback && currentDispatchOrder.status !== 'cancelled' && !currentDispatchOrder.warehouse_completed_at"
              class="detail-pack-operator-row"
            >
              <span class="detail-pack-operator-row__label">指定填写人</span>
              <el-select
                v-model="detailPackagingOperatorPick"
                filterable
                clearable
                placeholder="选择同事（清空并保存=解除锁定）"
                style="min-width: 220px"
              >
                <el-option
                  v-for="u in users"
                  :key="u.user_id"
                  :label="u.full_name || u.username"
                  :value="u.user_id"
                />
              </el-select>
              <el-button type="primary" :loading="detailPackagingOperatorSaving" @click="saveDetailPackagingOperator">
                保存
              </el-button>
            </div>
          </el-card>

          <!-- 订单明细列表 -->
          <el-card shadow="never">
            <template #header>
              <div class="detail-items-card-header">
                <span class="detail-items-card-header__title">订单明细 ({{ currentDispatchOrder.items?.length || 0 }} 条)</span>
                <div class="detail-items-card-header__actions">
                  <template
                    v-if="detailPanelMode === 'view' && canOperateDispatch && currentDispatchOrder?.audit_status !== 'approved'"
                  >
                    <el-button
                      size="small"
                      type="primary"
                      plain
                      :loading="detailDispatchMergeLoading"
                      @click="handleDispatchDetailMergeQuantity"
                    >
                      合并选中数量
                    </el-button>
                    <el-button
                      size="small"
                      :loading="detailDispatchMergeLoading"
                      @click="handleDispatchDetailSplitMergeQuantity"
                    >
                      拆分合并
                    </el-button>
                  </template>
                  <el-button size="small" :loading="detailLoading" @click="handleRefreshDispatchDetail">
                    刷新明细
                  </el-button>
                  <el-button size="small" @click="expandAllDispatchDetailBranches">一键展开分码</el-button>
                  <el-button size="small" @click="collapseAllDispatchDetailBranches">一键收拢分码</el-button>
                  <el-button size="small" plain @click="resetDispatchDetailItemColWidths">重置列宽</el-button>
                </div>
              </div>
            </template>
            <el-table
              ref="dispatchDetailItemsTableRef"
              class="dispatch-detail-items-table"
              :data="dispatchDetailItemsSortedForTable"
              :span-method="dispatchDetailMergedQtySpanMethod"
              :row-key="detailDispatchItemRowKey"
              border
              stripe
              max-height="500px"
              style="width: 100%"
              @selection-change="handleDispatchDetailItemsSelectionChange"
              @header-dragend="handleDispatchDetailItemsHeaderDragEnd"
            >
              <el-table-column
                v-if="detailPanelMode === 'view' && canOperateDispatch && currentDispatchOrder?.audit_status !== 'approved'"
                type="selection"
                width="48"
                fixed="left"
                :reserve-selection="false"
              />
              <el-table-column
                column-key="order_no"
                prop="order_no"
                label="订单号"
                :width="dispatchDetailItemColWidths.order_no"
                fixed="left"
              />
              <el-table-column
                column-key="client_purchase_order"
                prop="client_purchase_order"
                label="客户采购单号"
                :width="dispatchDetailItemColWidths.client_purchase_order"
              />
              <el-table-column column-key="order_date" label="订单日期" :width="dispatchDetailItemColWidths.order_date">
                <template #default="{ row }">
                  {{ formatDate(row.order_date) }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="customer_name"
                prop="customer_name"
                label="客户"
                :width="dispatchDetailItemColWidths.customer_name"
              />
              <el-table-column column-key="brand_name" prop="brand_name" label="品牌" :width="dispatchDetailItemColWidths.brand_name" />
              <el-table-column column-key="season_row" label="季节" :width="dispatchDetailItemColWidths.season_row">
                <template #default="{ row }">
                  {{ formatSeasonRow(row.order_item || {}) }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="order_type_name"
                label="订单类型"
                :width="dispatchDetailItemColWidths.order_type_name"
              >
                <template #default="{ row }">
                  {{ row.order_item?.order_type_name || '—' }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="client_material_name"
                prop="order_item.client_material_name"
                label="客户材料名称"
                :width="dispatchDetailItemColWidths.client_material_name"
              />
              <el-table-column
                column-key="client_color_cn"
                prop="order_item.client_color_cn"
                label="客户颜色"
                :width="dispatchDetailItemColWidths.client_color_cn"
              />
              <el-table-column
                column-key="client_color_en"
                prop="order_item.client_color_en"
                label="客户英文颜色"
                :width="dispatchDetailItemColWidths.client_color_en"
                show-overflow-tooltip
              />
              <el-table-column
                column-key="internal_product_name"
                prop="order_item.internal_product_name"
                label="内部材料名称"
                :width="dispatchDetailItemColWidths.internal_product_name"
              />
              <el-table-column
                column-key="internal_color_cn"
                prop="order_item.internal_color_cn"
                label="内部颜色"
                :width="dispatchDetailItemColWidths.internal_color_cn"
              />
              <el-table-column
                column-key="internal_color_en"
                label="内部英文颜色"
                :width="dispatchDetailItemColWidths.internal_color_en"
                show-overflow-tooltip
              >
                <template #default="{ row }">{{ internalColorEnFromOrderItem(row.order_item) || '—' }}</template>
              </el-table-column>
              <el-table-column
                column-key="item_notes_1"
                label="明细备注一"
                :width="dispatchDetailItemColWidths.item_notes_1"
                show-overflow-tooltip
              >
                <template #default="{ row }">{{ row.order_item?.item_notes || '—' }}</template>
              </el-table-column>
              <el-table-column
                column-key="item_notes_2"
                label="明细备注二"
                :width="dispatchDetailItemColWidths.item_notes_2"
                show-overflow-tooltip
              >
                <template #default="{ row }">{{ row.order_item?.item_notes_2 || '—' }}</template>
              </el-table-column>
              <el-table-column
                column-key="item_notes_3"
                label="明细备注三"
                :width="dispatchDetailItemColWidths.item_notes_3"
                show-overflow-tooltip
              >
                <template #default="{ row }">{{ row.order_item?.item_notes_3 || '—' }}</template>
              </el-table-column>
              <el-table-column column-key="spec" label="规格" :width="dispatchDetailItemColWidths.spec">
                <template #default="{ row }">
                  {{ row.order_item?.specification || row.order_item?.client_style_no || '-' }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="order_line_qty"
                :label="isProcessingOutboundDispatch(currentDispatchOrder) ? '订单需求' : '订单数量'"
                :width="dispatchDetailItemColWidths.order_line_qty"
                align="right"
              >
                <template #default="{ row }">
                  {{ formatNumber(row.order_line_quantity ?? row.order_required_quantity ?? row.order_item?.quantity) }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="merged_qty"
                :label="isProcessingOutboundDispatch(currentDispatchOrder) ? '出库目标' : '合并数量'"
                :width="dispatchDetailItemColWidths.merged_qty"
                align="right"
                class-name="dispatch-merged-qty-cell"
              >
                <template #default="{ row }">
                  {{ formatNumber(row.merged_quantity ?? row.processing_target_quantity ?? row.order_line_quantity ?? row.order_item?.quantity) }}
                </template>
              </el-table-column>
                <el-table-column
                column-key="unit_name"
                label="单位"
                :width="dispatchDetailItemColWidths.unit_name"
              >
                <template #default="{ row }">
                  {{ row.unit_name || row.order_item?.unit_name || '—' }}
                </template>
              </el-table-column>
              <el-table-column type="expand" column-key="branch_expand" :width="dispatchDetailItemColWidths.branch_expand" :resizable="false">
                <template #default="{ row }">
                  <div class="branch-expand">
                    <div class="branch-expand__title">分码 / 包装明细</div>
                    <div v-if="!getBranchRowsForDispatchDetail(row).length" class="branch-expand__empty">
                      暂无分码记录
                    </div>
                    <div v-else class="branch-expand__rows">
                      <div
                        v-for="br in getBranchRowsForDispatchDetail(row)"
                        :key="br.branch_record_id || br.line_no"
                        class="branch-expand__row"
                      >
                        <span class="branch-expand__idx">#{{ br.line_no || '-' }}</span>
                        <span class="branch-expand__tag">{{ br.branch_label || '-' }}</span>
                        <span class="branch-expand__pack">{{ br.branch_pack_code || '-' }}</span>
                        <span class="branch-expand__qty">数量 {{ formatNumber(br.quantity) }}</span>
                        <span v-if="br.weight_kg != null" class="branch-expand__meta">称重 {{ formatPackWeightKg(br.weight_kg) }} kg</span>
                        <span v-if="formatBranchDimsOneLine(br)" class="branch-expand__meta">外尺寸 {{ formatBranchDimsOneLine(br) }} cm</span>
                        <span v-if="br.is_fold_compensation" class="branch-expand__meta">
                          折补 {{ formatNumber(br.fold_compensation_qty ?? 0) }}
                          <template v-if="br.fold_compensation_warehouse_manual_code || br.fold_compensation_batch_id">
                            → 整扣 {{ br.fold_compensation_warehouse_manual_code || ('#' + br.fold_compensation_batch_id) }}
                          </template>
                        </span>
                        <span
                          v-if="Array.isArray(br.batch_usages) && br.batch_usages.length"
                          class="branch-expand__meta"
                        >
                          用料
                          {{
                            br.batch_usages
                              .map((u) => {
                                const code = u.warehouse_manual_code || ('#' + u.material_batch_id)
                                return `${code}×${formatNumber(u.quantity)}`
                              })
                              .join('；')
                          }}
                        </span>
                        <span v-if="br.packaging_measured_by_name" class="branch-expand__meta branch-expand__measurer">
                          包装录入 {{ br.packaging_measured_by_name }}
                          <template v-if="br.packaging_measured_at">
                            · {{ formatDateTime(br.packaging_measured_at) }}
                          </template>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column column-key="warehouse_code" label="已选料号" :width="dispatchDetailItemColWidths.warehouse_code">
                <template #default="{ row }">
                  <span>
                    {{ selectedBatchDisplay(row.order_item?.material_matching) !== '—' ? selectedBatchDisplay(row.order_item?.material_matching) : (row.warehouse_manual_code || row.material_code || row.selected_batch_display || '—') }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column column-key="matching_remarks" label="配料备注" :width="dispatchDetailItemColWidths.matching_remarks">
                <template #default="{ row }">
                  {{ matchingRemarksFromDispatchItem(row) || '—' }}
                </template>
              </el-table-column>
              <el-table-column column-key="material_usage_notes" label="用料备注" :width="dispatchDetailItemColWidths.material_usage_notes">
                <template #default="{ row }">
                  {{ row.material_usage_notes || '—' }}
                </template>
              </el-table-column>
              <el-table-column column-key="stock_location" label="库存位置" :width="dispatchDetailItemColWidths.stock_location">
                <template #default="{ row }">
                  <template v-for="(loc, index) in getMatchingLocationsForView(row)" :key="index">
                    <div style="margin-bottom: 4px;">
                      {{ formatStorageLocationLabel(loc) }}
                    </div>
                  </template>
                  <span v-if="getMatchingLocationsForView(row).length === 0" style="color: gray;">-</span>
                </template>
              </el-table-column>
              <el-table-column column-key="stock_qty" label="库存数量" :width="dispatchDetailItemColWidths.stock_qty" align="right">
                <template #default="{ row }">
                  <template v-for="(loc, index) in getMatchingLocationsForView(row)" :key="index">
                    <div style="margin-bottom: 4px;">
                      {{ formatNumber(loc.available_quantity) }}
                    </div>
                  </template>
                  <span v-if="getMatchingLocationsForView(row).length === 0" style="color: gray;">-</span>
                </template>
              </el-table-column>
              <el-table-column column-key="line_packaging_type" label="袋型(本行)" :width="dispatchDetailItemColWidths.line_packaging_type">
                <template #default="{ row }">
                  {{ packagingTypeLabel(row.packaging_type || currentDispatchOrder.packaging_type) }}
                </template>
              </el-table-column>
              <el-table-column column-key="line_weigh" label="称重" :width="dispatchDetailItemColWidths.line_weigh" align="center">
                <template #default="{ row }">
                  {{ (row.packaging_require_weigh ?? currentDispatchOrder.packaging_require_weigh) ? '是' : '否' }}
                </template>
              </el-table-column>
              <el-table-column column-key="line_volume" label="体积" :width="dispatchDetailItemColWidths.line_volume" align="center">
                <template #default="{ row }">
                  {{ (row.packaging_require_volume ?? currentDispatchOrder.packaging_require_volume) ? '是' : '否' }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="branch_weight_summary"
                label="分码称重(实测)"
                :width="dispatchDetailItemColWidths.branch_weight_summary"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  {{ formatBranchWeightsMeasured(row) }}
                </template>
              </el-table-column>
              <el-table-column
                column-key="branch_dims_summary"
                label="分外尺寸(cm)"
                :width="dispatchDetailItemColWidths.branch_dims_summary"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  {{ formatBranchDimsMeasured(row) }}
                </template>
              </el-table-column>
              <el-table-column
                v-if="canVoidDispatchItem && detailPanelMode === 'view'"
                label="超级操作"
                width="110"
                fixed="right"
              >
                <template #default="{ row }">
                  <el-button
                    v-if="row.dispatch_order_item_id"
                    link
                    type="danger"
                    size="small"
                    :loading="voidDispatchItemId === row.dispatch_order_item_id"
                    @click.stop="handleVoidDispatchItem(row)"
                  >
                    作废本行
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-card>

          <el-card
            v-if="detailPanelMode === 'view' && currentDispatchOrder.status === 'completed' && currentDispatchOrder.post_completion_audit_status === 'pending'"
            shadow="never"
            style="margin-top: 16px"
          >
            <template #header>
              <span style="font-weight: bold">打码现场核销</span>
            </template>
            <el-alert
              v-if="detailPendingKickbackLinesCount > 0"
              type="error"
              :closable="false"
              show-icon
              style="margin-bottom: 12px"
              title="存在待审结的踢回配料申请"
              :description="`本单尚有 ${detailPendingKickbackLinesCount} 条踢回申请未处理，请先同意或拒绝后再核销通过；仓库亦不可在审结前提交完工（系统已拦截）。`"
            />
            <p class="packaging-hint">仓库已点打码完成，请核对分码与跑码数据后确认或退回修改。包装资料可稍后单独填写，不受本核销影响。</p>
            <div style="margin-bottom: 12px">
              <div style="font-size: 13px; color: #64748b; margin-bottom: 6px">核销备注</div>
              <el-input v-model="postCompletionNotes" type="textarea" :rows="2" placeholder="可选" />
            </div>
            <div style="display: flex; gap: 10px; flex-wrap: wrap">
              <el-button
                type="primary"
                :loading="postCompletionLoading"
                :disabled="!canAuditReview || detailPendingKickbackLinesCount > 0"
                @click="submitPostCompletionAudit('approve')"
              >
                核销通过
              </el-button>
              <el-button type="danger" :loading="postCompletionLoading" :disabled="!canAuditReview" @click="submitPostCompletionAudit('reject')">
                核销退回（恢复进行中）
              </el-button>
            </div>
          </el-card>

          <el-card
            v-if="
              detailPanelMode === 'view' &&
              canRevokeVerification &&
              currentDispatchOrder.status === 'completed' &&
              currentDispatchOrder.post_completion_audit_status === 'approved'
            "
            shadow="never"
            style="margin-top: 16px"
          >
            <template #header>
              <span style="font-weight: bold">打码现场核销（已结案）</span>
            </template>
            <p class="packaging-hint">
              若误核销或漏审踢回，具备「派工单-取消核销」权限的用户可「取消核销」：派工单恢复为进行中，可再次分码、完工与核销。
            </p>
            <el-button
              type="warning"
              :loading="postCompletionRevokeLoading"
              :disabled="!canRevokeVerification"
              @click="submitRevokePostCompletionAudit"
            >
              取消打码核销（恢复进行中）
            </el-button>
          </el-card>

          <el-card
            v-if="detailPanelMode === 'view' && detailNeedsPackagingMeasures && currentDispatchOrder.packaging_audit_status === 'pending'"
            shadow="never"
            style="margin-top: 16px"
          >
            <template #header>
              <span style="font-weight: bold">包装称重核销</span>
            </template>
            <p class="packaging-hint">现场已提交包装资料，请核对各支称重/外尺寸后确认或退回修改。</p>
            <div style="margin-bottom: 12px">
              <div style="font-size: 13px; color: #64748b; margin-bottom: 6px">核销备注</div>
              <el-input v-model="packagingAuditNotes" type="textarea" :rows="2" placeholder="可选" />
            </div>
            <div style="display: flex; gap: 10px; flex-wrap: wrap">
              <el-button
                type="primary"
                :loading="packagingAuditLoading"
                :disabled="!canAuditReview"
                @click="submitPackagingAudit('approve')"
              >
                包装核销通过
              </el-button>
              <el-button
                type="danger"
                :loading="packagingAuditLoading"
                :disabled="!canAuditReview"
                @click="submitPackagingAudit('reject')"
              >
                包装核销退回
              </el-button>
            </div>
          </el-card>

          <el-card
            v-if="detailPanelMode === 'view' && detailNeedsPackagingMeasures && currentDispatchOrder.packaging_audit_status === 'approved'"
            shadow="never"
            style="margin-top: 16px"
          >
            <template #header>
              <span style="font-weight: bold">包装称重核销（已结案）</span>
            </template>
            <p class="packaging-hint">若误核销，可取消包装核销，现场可修改包装资料后再次提交。</p>
            <el-button
              type="warning"
              :loading="packagingAuditRevokeLoading"
              :disabled="!canAuditReview"
              @click="submitRevokePackagingAudit"
            >
              取消包装核销
            </el-button>
          </el-card>

          <el-card v-if="detailPanelMode === 'audit_pre'" shadow="never" style="margin-top: 16px">
            <template #header>
              <span style="font-weight: bold">派工审核备注</span>
            </template>
            <el-input v-model="preAuditNotes" type="textarea" :rows="3" placeholder="审核说明（可选）" />
          </el-card>
        </div>
      </div>

      <template #footer>
        <el-button
          v-if="currentDispatchOrder?.dispatch_order_id"
          link
          type="primary"
          @click="openDispatchWorkInNewTab()"
        >
          新标签页打开
        </el-button>
        <el-button @click="closeDetailDialog">关闭</el-button>
        <el-button
          v-if="
            detailPanelMode === 'view' &&
            canOperateDispatch &&
            isProcessingOutboundDispatch(currentDispatchOrder) &&
            currentDispatchOrder?.audit_status === 'approved' &&
            !currentDispatchOrder?.warehouse_completed_at &&
            currentDispatchOrder?.status !== 'cancelled' &&
            currentDispatchOrder?.status !== 'completed'
          "
          type="success"
          :loading="processingOutboundCompleteLoading"
          @click="submitProcessingOutboundComplete"
        >
          确认分码完成并加工出库
        </el-button>
        <el-button
          v-if="detailPanelMode === 'view' && currentDispatchOrder?.status !== 'cancelled'"
          type="warning"
          plain
          @click="openPackagingMeasuresForCurrent"
        >
          包装实测
        </el-button>
        <el-button
          v-if="
            detailPanelMode === 'view' &&
            canRevokeVerification &&
            currentDispatchOrder?.status === 'completed' &&
            currentDispatchOrder?.post_completion_audit_status === 'approved'
          "
          type="warning"
          :loading="postCompletionRevokeLoading"
          :disabled="!canRevokeVerification"
          @click="submitRevokePostCompletionAudit"
        >
          取消核销（恢复进行中）
        </el-button>
        <el-button
          v-if="
            detailPanelMode === 'view' &&
            currentDispatchOrder?.status === 'completed' &&
            (currentDispatchOrder?.post_completion_audit_status || 'na') !== 'approved'
          "
          type="warning"
          :disabled="!canAuditReview"
          @click="openReopenDialog"
        >
          退回仓库修改
        </el-button>
        <template v-if="detailPanelMode === 'audit_pre'">
          <el-button type="danger" :loading="preAuditLoading" :disabled="!canAuditReview" @click="submitPreAudit('reject')">拒绝</el-button>
          <el-button type="primary" :loading="preAuditLoading" :disabled="!canAuditReview" @click="submitPreAudit('approve')">审核通过</el-button>
        </template>
      </template>
    </el-dialog>

    <DispatchOutboundLocationDialog
      v-model="outboundLocDialogVisible"
      :payload="outboundLocPayload"
      @confirm="onOutboundLocationPicked"
    />

    <!-- 编辑派工单对话框 -->
    <el-dialog 
      v-model="editDialogVisible" 
      title="编辑派工单" 
      width="90%"
      class="erp-nonmodal-work-dialog"
      :modal="false"
      :modal-penetrable="true"
      :lock-scroll="false"
      :close-on-click-modal="false"
      @opened="handleEditDialogOpened"
      @closed="handleEditDialogClosed"
    >
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="dispatch-nonmodal-hint"
        title="可切换其他菜单查阅资料，返回本页后未保存修改会保留；保存前请勿点「关闭」。"
      />
      <p class="create-dialog-tip" style="margin-bottom: 12px">
        仅显示<strong>本派工单已包含</strong>的订单明细；包装要求按行修改。若需增删明细行，请联系管理员或取消审核后重新生成。
      </p>

      <!-- 订单明细表格 -->
      <el-table
        ref="orderItemsTableRef"
        :data="orderItemsData"
        v-loading="orderItemsLoading"
        border
        stripe
        max-height="500px"
        style="width: 100%"
        @selection-change="handleOrderItemsSelectionChange"
      >
        <el-table-column type="selection" width="55" :fixed="true" />
        <el-table-column prop="order_no" label="订单号" width="110" fixed="left" />
        <el-table-column prop="client_purchase_order" label="客户采购单号" width="130" show-overflow-tooltip />
        <el-table-column label="订单日期" width="100">
          <template #default="{ row }">
            {{ formatDate(row.order_date) }}
          </template>
        </el-table-column>
        <el-table-column prop="customer_name" label="客户" width="120" show-overflow-tooltip />
        <el-table-column prop="brand_name" label="品牌" width="100" show-overflow-tooltip />
        <el-table-column label="季节" width="110" show-overflow-tooltip>
          <template #default="{ row }">
            {{ formatSeasonRow(row) }}
          </template>
        </el-table-column>
        <el-table-column prop="order_type_name" label="订单类型" width="110" show-overflow-tooltip />
        <el-table-column prop="client_material_name" label="客户材料名称" width="150" show-overflow-tooltip />
        <el-table-column prop="client_color_cn" label="客户颜色" width="100" />
        <el-table-column prop="client_color_en" label="客户英文颜色" width="120" show-overflow-tooltip />
        <el-table-column prop="internal_product_name" label="内部材料名称" width="150" show-overflow-tooltip />
        <el-table-column prop="internal_color_cn" label="内部颜色" width="130" />
        <el-table-column label="内部英文颜色" width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ internalColorEnFromOrderItem(row) || '—' }}</template>
        </el-table-column>
        <el-table-column label="明细备注一" width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.item_notes || '—' }}</template>
        </el-table-column>
        <el-table-column label="明细备注二" width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.item_notes_2 || '—' }}</template>
        </el-table-column>
        <el-table-column label="明细备注三" width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.item_notes_3 || '—' }}</template>
        </el-table-column>
        <el-table-column label="规格" width="100" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.specification || row.client_style_no || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="订单数量" width="88" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.order_line_quantity ?? row.quantity) }}
          </template>
        </el-table-column>
        <el-table-column label="合并数量" width="88" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.merged_quantity ?? row.order_line_quantity ?? row.quantity) }}
          </template>
        </el-table-column>
        <el-table-column prop="unit_name" label="单位" width="60" />
        <el-table-column label="已选料号" width="150">
          <template #default="{ row }">
            {{ selectedBatchDisplay(row.material_matching) !== '—' ? selectedBatchDisplay(row.material_matching) : (row.warehouse_manual_code || row.material_code || '—') }}
          </template>
        </el-table-column>
        <el-table-column label="配料备注" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">
            {{ matchingRemarksFromOrderItem(row) || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="用料备注" min-width="160">
          <template #default="{ row }">
            <el-input
              v-model="row.material_usage_notes"
              type="textarea"
              :rows="2"
              placeholder="如反面当正面、指定卷号等"
              size="small"
            />
          </template>
        </el-table-column>
        <el-table-column label="库存位置" width="200" show-overflow-tooltip>
          <template #default="{ row }">
            <template v-for="(loc, index) in getMatchingLocationsForEdit(row)" :key="index">
              <div style="margin-bottom: 4px;">
                {{ formatStorageLocationLabel(loc) }}
              </div>
            </template>
            <span v-if="getMatchingLocationsForEdit(row).length === 0" style="color: gray;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="库存数量" width="120" align="right">
          <template #default="{ row }">
            <template v-for="(loc, index) in getMatchingLocationsForEdit(row)" :key="index">
              <div style="margin-bottom: 4px;">
                {{ formatNumber(loc.available_quantity) }}
              </div>
            </template>
            <span v-if="getMatchingLocationsForEdit(row).length === 0" style="color: gray;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="总可用库存" width="120" align="right">
          <template #default="{ row }">
            <span v-if="row.material_matching?.total_available_quantity !== undefined">
              {{ formatNumber(row.material_matching.total_available_quantity) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="袋型" width="130">
          <template #default="{ row }">
            <el-select v-model="row.packaging_type" placeholder="未指定" clearable size="small" style="width: 100%">
              <el-option label="未指定" value="" />
              <el-option label="透明袋" value="transparent_bag" />
              <el-option label="蛇皮袋" value="woven_bag" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="称重" width="72" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_weigh" />
          </template>
        </el-table-column>
        <el-table-column label="体积" width="72" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.packaging_require_volume" />
          </template>
        </el-table-column>
      </el-table>

      <!-- 表单区域 -->
      <el-form ref="editForm" :model="dispatchOrderForm" :rules="dispatchEditRules" label-width="100px" style="margin-top: 20px;">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="指派人员" prop="assigned_user_id">
              <el-select
                v-model="dispatchOrderForm.assigned_user_id"
                placeholder="请选择指派人员"
                filterable
                style="width: 100%"
                clearable
              >
                <el-option
                  v-for="user in users"
                  :key="user.user_id"
                  :label="user.full_name || user.username"
                  :value="user.user_id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="已选数量">
              <span>{{ selectedOrderItems.length }} 条</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注" prop="notes">
          <el-input
            v-model="dispatchOrderForm.notes"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
          />
        </el-form-item>
        <el-form-item label="多料号同卷">
          <el-checkbox v-model="dispatchOrderForm.allow_multi_batch_same_roll">
            特别提醒：允许不同料号分同一卷
          </el-checkbox>
          <el-input
            v-if="dispatchOrderForm.allow_multi_batch_same_roll"
            v-model="dispatchOrderForm.multi_batch_same_roll_notes"
            type="textarea"
            :rows="2"
            style="margin-top: 8px"
            placeholder="现场操作说明（如：可混卷、按备注分卷等）"
          />
          <p class="packaging-hint" style="margin-top: 6px">
            未勾选时：多料号须分不同卷。勾选后现场可在同一卷填写多料号用量。
          </p>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="16">
            <el-form-item label="要求完成" prop="required_completion_at" required>
              <el-date-picker
                v-model="dispatchOrderForm.required_completion_at"
                type="datetime"
                placeholder="必填：选日期，默认当天 22:00 截止（或勾选右侧特急）"
                value-format="YYYY-MM-DDTHH:mm:ss"
                format="YYYY-MM-DD HH:mm"
                :default-time="dispatchDueDefaultTime"
                style="width: 100%"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label=" ">
              <el-checkbox v-model="dispatchOrderForm.required_completion_immediate">马上完成（特急单）</el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <div v-if="siteCapacityCaps && canOperateDispatch" class="dispatch-capacity-panel">
          <p class="dispatch-capacity-panel__rule">
            现场产能：同一指派人在同一统计日内，折算码数合计不超过
            <strong>{{ Number(siteCapacityCaps.max_yards_per_assignee_per_day).toLocaleString() }}</strong>，
            派工明细条数不超过
            <strong>{{ siteCapacityCaps.max_dispatch_items_per_assignee_per_day }}</strong>
            （yd 与 m 计码，其它单位只计条数；仅未完成单计入；特急按创建日/要求完成日；已完成与已核销不计）。
            <router-link v-if="canAdjustSiteCapacity" class="dispatch-capacity-panel__link" to="/admin/dispatch-site-capacity">调整上限</router-link>
          </p>
          <div v-loading="siteCapacityPreviewLoading" class="dispatch-capacity-panel__preview">
            <template v-if="siteCapacityPreview?.applies">
              <el-alert
                :type="siteCapacityPreview.ok ? 'success' : 'error'"
                :closable="false"
                show-icon
              >
                <template #title>本单占用预览（{{ siteCapacityPreview.due_date }}）</template>
                已用：{{ Number(siteCapacityPreview.used_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.used_items }} 条；
                本单：{{ Number(siteCapacityPreview.proposed_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.proposed_items }} 条；
                合计：{{ Number(siteCapacityPreview.total_yards || 0).toFixed(1) }} 码 / {{ siteCapacityPreview.total_items }} 条
                （上限 {{ Number(siteCapacityPreview.cap_yards || 0).toFixed(0) }} 码、{{ siteCapacityPreview.cap_items }} 条）。
              </el-alert>
            </template>
            <p v-else-if="siteCapacityPreview?.reason" class="dispatch-capacity-panel__muted">{{ siteCapacityPreview.reason }}</p>
          </div>
        </div>
      </el-form>

      <template #footer>
        <el-button
          v-if="currentDispatchOrder?.dispatch_order_id"
          link
          type="primary"
          @click="openDispatchWorkInNewTab()"
        >
          新标签页打开
        </el-button>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleConfirmEdit"
          :disabled="selectedOrderItems.length === 0"
        >
          确认保存
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="customerPackagingRulesVisible"
      title="客户包装要求维护"
      width="960px"
      destroy-on-close
      @opened="onCustomerPackagingRulesOpened"
    >
      <CustomerPackagingRequirementsPanel
        ref="customerPackagingRulesPanelRef"
        @changed="loadCustomerPackagingRequirementsMap"
      />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Search, Setting, Plus } from '@element-plus/icons-vue'
import { selectedBatchDisplay } from '@/utils/selectedBatchDisplay'
import {
  getDispatchOrders,
  createDispatchOrder,
  applyDispatchMergeQuantity,
  splitDispatchMergeQuantity,
  getDispatchOrder,
  updateDispatchOrder,
  deleteDispatchOrder,
  auditDispatchOrder,
  revokeDispatchOrderAudit,
  printDispatchOrder,
  returnOrderItemToMatching,
  getCompletedOrderItemMatchings,
  postCompletionAuditDispatchOrder,
  packagingAuditDispatchOrder,
  revokePackagingAuditDispatchOrder,
  requestReopenDispatchOrder,
  revokePostCompletionAuditDispatchOrder,
  voidDispatchOrderItem,
  warehouseCompleteDispatchOrder
} from '@/api/dispatchOrders'
import {
  getDispatchOrderItemKickbackRequests,
  approveDispatchOrderItemKickbackRequest,
  rejectDispatchOrderItemKickbackRequest
} from '@/api/dispatchKickback'
import {
  getDispatchPackagingAccessRequests,
  approveDispatchPackagingAccessRequest,
  rejectDispatchPackagingAccessRequest,
  assignDispatchPackagingOperator
} from '@/api/dispatchPackagingAccess'
import DispatchPackagingMeasuresDialog from '@/components/dispatch/DispatchPackagingMeasuresDialog.vue'
import DispatchOutboundLocationDialog from '@/components/DispatchOutboundLocationDialog.vue'
import { orderNeedsPackMeasures } from '@/utils/dispatchPackagingMeasures'
import { getDispatchSiteCapacitySettings, getDispatchSiteCapacityPreview } from '@/api/dispatchSiteCapacity'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { classifyDispatchDue, normalizeRequiredCompletionAtForSave } from '@/utils/dispatchDueUrgency'
import PrintPreview from '@/components/PrintPreview.vue'
import { getAssignableUsers } from '@/api/users'
import { getActiveCustomers } from '@/api/customers'
import { getBrands } from '@/api/brands'
import ColumnConfigDialog from '@/components/ColumnConfigDialog.vue'
import DispatchOrderLineDetailsTab from '@/views/orders/DispatchOrderLineDetailsTab.vue'
import CustomerPackagingRequirementsPanel from '@/views/orders/CustomerPackagingRequirementsPanel.vue'
import { getCustomerPackagingRequirementsResolveMap } from '@/api/customerPackagingRequirements'
import {
  applyCustomerPackagingRequirementToRow,
  packagingTypeLabel as customerPackagingTypeLabel
} from '@/utils/customerPackagingRequirements'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import { hasTaskReturnQuery, navigateTaskReturnIfPresent } from '@/utils/taskReturnNavigation'

const activeMainTab = ref('list')
const loading = ref(false)
const route = useRoute()
const router = useRouter()
const openedDispatchFromRoute = ref('')
const userStore = useUserStore()
const canOperateDispatch = computed(() => hasPermission(userStore.user, 'orders.dispatch.edit'))

const siteCapacityCaps = ref(null)
const siteCapacityPreview = ref(null)
const siteCapacityPreviewLoading = ref(false)
const canAdjustSiteCapacity = computed(() => hasPermission(userStore.user, 'global.admin'))

const loadSiteCapacitySettings = async () => {
  if (!canOperateDispatch.value) return
  try {
    const res = await getDispatchSiteCapacitySettings()
    if (res.success && res.data) siteCapacityCaps.value = res.data
  } catch {
    siteCapacityCaps.value = null
  }
}
const canAuditReview = computed(() => hasPermission(userStore.user, 'orders.dispatch.review'))
/** 取消现场核销（与审核权分离的独立权限） */
const canRevokeVerification = computed(() =>
  hasPermission(userStore.user, 'orders.dispatch.revoke_verification')
)
/** 管理员：单独作废派工明细行 */
const canVoidDispatchItem = computed(() => hasPermission(userStore.user, 'orders.dispatch.void_item'))
/** 移动端踢回申请：业务审批（审核权或派工编辑权） */
const canReviewKickback = computed(() => canAuditReview.value || canOperateDispatch.value)
const dispatchColumnConfigStorageKey = computed(() => getUserScopedStorageKey('dispatch_order_list'))

const canRevokeAudit = computed(() => hasPermission(userStore.user, 'orders.dispatch.unaudit'))
const orderItemsLoading = ref(false)
const searchKeyword = ref('')
const filterWarehouseManualCode = ref('')
const filterStatus = ref('')
/** 派工审核：pending / approved / rejected */
const filterAuditStatus = ref('')
/** 指派人员 user_id */
const filterAssignedUserId = ref(null)
/** 现场核销（完成后业务核销）：na / pending / approved / rejected */
const filterPostCompletionStatus = ref('')
const tableData = ref([])
const tableRef = ref(null)
const showColumnConfig = ref(false)
const createDialogVisible = ref(false)
const editDialogVisible = ref(false)
/** 详情侧栏 / 编辑弹窗共用的当前派工单（须在产能 watch 等之前声明，避免 TDZ） */
const currentDispatchOrder = ref(null)
/** 当前详情内待业务审结的踢回配料申请条数（与列表踢回抽屉同源） */
const detailPendingKickbackLinesCount = computed(() => {
  const items = currentDispatchOrder.value?.items
  if (!Array.isArray(items)) return 0
  return items.filter((it) => it.kickback_request_pending).length
})
const orderItemsTableRef = ref(null)
const orderItemsData = ref([])
const selectedOrderItems = ref([])
const createSelectedPoolById = ref({})
const createSortMode = ref('warehouse_code_then_order_date')
const restoringCreateSelection = ref(false)
/** 生成派工单：批量包装作用范围 — 当前筛选列表 / 已选清单（跨搜索累计） */
const createPackagingBulkScope = ref('current')
const users = ref([])
const createFormFilter = ref({
  searchKeyword: '',
  customerId: null,
  brandId: null,
  orderTypeId: null,
  materialCategoryName: '',
  materialColorName: '',
  internalColorEn: '',
  materialCode: ''
})

/** 当前待派工池中出现的订单类型（避免单独请求订单类型接口权限问题） */
const orderTypeFilterOptions = ref([])
/** 生成派工单：备注末尾自动追加的「合并分码」说明策略 */
const dispatchMergeNoteStrategy = ref('off')

/** 生成派工单：行尾「参与合并」勾选（仅生成前临时状态） */
const mergeCandidateByOrderItemId = reactive({})
/** 生成派工单：合并数量分组键（同键行合并数量=组内订单数量之和） */
const pendingMergeGroupKeyByOrderItemId = reactive({})

/** 生成派工单 · 订单明细表：列显隐与列宽（表头可拖拽，「列设置」对话框可编辑） */
const CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT = Object.freeze([
  { prop: 'order_no', label: '订单号', visible: true, width: 110 },
  { prop: 'client_purchase_order', label: '客户采购单号', visible: true, width: 130 },
  { prop: 'order_date', label: '订单日期', visible: true, width: 100 },
  { prop: 'customer_name', label: '客户', visible: true, width: 120 },
  { prop: 'brand_name', label: '品牌', visible: true, width: 100 },
  { prop: 'season_row', label: '季节', visible: true, width: 110 },
  { prop: 'order_type_name', label: '订单类型', visible: true, width: 120 },
  { prop: 'client_material_name', label: '客户材料名称', visible: true, width: 150 },
  { prop: 'client_color_cn', label: '客户颜色', visible: true, width: 100 },
  { prop: 'client_color_en', label: '客户英文颜色', visible: true, width: 120 },
  { prop: 'internal_product_name', label: '内部材料名称', visible: true, width: 150 },
  { prop: 'internal_color_cn', label: '内部颜色', visible: true, width: 130 },
  { prop: 'internal_color_en', label: '内部英文颜色', visible: true, width: 130 },
  { prop: 'spec', label: '规格', visible: true, width: 100 },
  { prop: 'order_qty', label: '订单数量', visible: true, width: 92 },
  { prop: 'merged_qty', label: '合并数量', visible: true, width: 92 },
  { prop: 'unit_name', label: '单位', visible: true, width: 60 },
  { prop: 'warehouse_code', label: '已选料号', visible: true, width: 150 },
  { prop: 'matching_remarks', label: '配料备注', visible: true, width: 160 },
  { prop: 'material_usage_notes', label: '用料备注', visible: true, width: 160 },
  { prop: 'stock_location', label: '库存位置', visible: true, width: 200 },
  { prop: 'stock_qty', label: '库存数量', visible: true, width: 120 },
  { prop: 'total_avail', label: '总可用库存', visible: true, width: 120 },
  { prop: 'packaging_type', label: '袋型', visible: true, width: 130 },
  { prop: 'weigh', label: '称重', visible: true, width: 72 },
  { prop: 'volume', label: '体积', visible: true, width: 72 },
  { prop: 'merge_candidate', label: '参与合并', visible: true, width: 92 },
  { prop: 'operations', label: '操作', visible: true, width: 120 }
])
const createDispatchColumnDialogSeed = CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT.map((c) => ({ ...c }))
const createDispatchItemsColumnStorageKey = computed(() =>
  getUserScopedStorageKey('erp_create_dispatch_order_items_columns_v1')
)
const createDispatchTableWrapStorageKey = computed(() =>
  getUserScopedStorageKey('erp_create_dispatch_order_items_wrap_v1')
)
const createDispatchItemsColumnConfig = ref(
  JSON.parse(JSON.stringify(CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT))
)
const showCreateDispatchColumnSettings = ref(false)
const createDispatchTableCellWrap = ref(false)

const createDispatchColMap = computed(() => {
  const out = {}
  for (const c of createDispatchItemsColumnConfig.value) {
    out[c.prop] = c
  }
  return out
})

const getCreateCol = (prop) => {
  const c = createDispatchColMap.value[prop]
  const def = CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT.find((d) => d.prop === prop)
  const w = Math.max(48, Math.round(Number(c?.width ?? def?.width ?? 80)))
  if (prop === 'operations') {
    return { visible: true, width: w }
  }
  return {
    visible: c ? c.visible !== false : true,
    width: w
  }
}

function hydrateCreateDispatchItemsColumnConfig() {
  try {
    const raw = localStorage.getItem(createDispatchItemsColumnStorageKey.value)
    if (!raw) {
      createDispatchItemsColumnConfig.value = JSON.parse(JSON.stringify(CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT))
      return
    }
    const parsed = JSON.parse(raw)
    const savedProps = new Set(parsed.map((col) => col.prop))
    const merged = parsed.map((savedCol) => {
      const defaultCol = CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT.find((c) => c.prop === savedCol.prop)
      return defaultCol ? { ...defaultCol, ...savedCol } : savedCol
    })
    CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT.forEach((defaultCol) => {
      if (!savedProps.has(defaultCol.prop)) {
        merged.push({ ...defaultCol })
      }
    })
    createDispatchItemsColumnConfig.value = merged
  } catch {
    createDispatchItemsColumnConfig.value = JSON.parse(JSON.stringify(CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT))
  }
}

function persistCreateDispatchItemsColumnConfig() {
  try {
    localStorage.setItem(
      createDispatchItemsColumnStorageKey.value,
      JSON.stringify(createDispatchItemsColumnConfig.value)
    )
  } catch (e) {
    console.warn('save create dispatch column config failed', e)
  }
}

function loadCreateDispatchTableWrap() {
  try {
    const raw = localStorage.getItem(createDispatchTableWrapStorageKey.value)
    if (raw === '1' || raw === 'true') createDispatchTableCellWrap.value = true
    else if (raw === '0' || raw === 'false') createDispatchTableCellWrap.value = false
  } catch {
    /* ignore */
  }
}

function persistCreateDispatchTableWrap() {
  try {
    localStorage.setItem(createDispatchTableWrapStorageKey.value, createDispatchTableCellWrap.value ? '1' : '0')
  } catch (e) {
    console.warn('save create dispatch wrap pref failed', e)
  }
}

const onCreateDispatchColumnConfigChange = (cfg) => {
  createDispatchItemsColumnConfig.value = cfg
}

const handleCreateDispatchItemsHeaderDragEnd = (newWidth, _oldWidth, column) => {
  const key = column?.columnKey || column?.property
  if (!key) return
  const w = Math.max(48, Math.round(Number(newWidth)))
  const col = createDispatchItemsColumnConfig.value.find((c) => c.prop === key)
  if (col) {
    col.width = w
    persistCreateDispatchItemsColumnConfig()
  }
}

const resetCreateDispatchItemsColumnWidths = () => {
  createDispatchItemsColumnConfig.value = createDispatchItemsColumnConfig.value.map((col) => {
    const def = CREATE_DISPATCH_ORDER_ITEMS_COLUMNS_DEFAULT.find((d) => d.prop === col.prop)
    return def ? { ...col, width: def.width } : col
  })
  persistCreateDispatchItemsColumnConfig()
  ElMessage.success('列宽已恢复默认')
}

const detailDispatchMergeLoading = ref(false)
const detailTableSelectionRefRows = ref([])

/** 避免 null/undefined 上调用 toLowerCase/includes 抛错；下拉框与接口 id 类型可能不一致，用 Number 比较 */
const fieldIncludesKeyword = (val, keywordLower) =>
  String(val ?? '').toLowerCase().includes(keywordLower)
const idEq = (a, b) => {
  if (a == null || b == null || a === '' || b === '') return false
  return Number(a) === Number(b)
}
const customerList = ref([])
const brandList = ref([])
const materialCategoryList = ref([])
const materialColorList = ref([])
/** 配料英文颜色（与「内部英文颜色」列一致），用于生成派工单筛选 */
const materialInternalColorEnList = ref([])
const dispatchOrderForm = ref({
  assigned_user_id: null,
  notes: '',
  allow_multi_batch_same_roll: false,
  multi_batch_same_roll_notes: '',
  required_completion_at: null,
  required_completion_immediate: false,
  packaging_type: '',
  packaging_require_weigh: false,
  packaging_require_volume: false
})

const dispatchEditRules = {
  required_completion_at: [
    {
      validator: (_rule, value, callback) => {
        const p = dispatchOrderForm.value
        if (p.required_completion_immediate || (value != null && String(value).trim() !== '')) {
          callback()
        } else {
          callback(new Error('请填写要求完成时间或勾选「马上完成（特急单）」'))
        }
      },
      trigger: ['change', 'blur']
    }
  ]
}

const packagingDialogVisible = ref(false)
const packagingEditId = ref(null)
const packagingSaving = ref(false)
const packagingLines = ref([])
const customerPackagingRulesVisible = ref(false)
const customerPackagingRulesPanelRef = ref(null)
const customerPackagingRequirementsNested = ref({})

const packagingTypeLabel = (v) => customerPackagingTypeLabel(v)

const rowDueSummary = (row) => classifyDispatchDue(row || {})

/** Element Plus 选日期时默认时刻为当天 22:00 */
const dispatchDueDefaultTime = new Date(2000, 0, 1, 22, 0, 0)

const CREATE_SORT_OPTIONS = [
  { value: 'warehouse_code_then_order_date', label: '仓库料号 → 订单日期 → 订单号' },
  { value: 'warehouse_code_then_qty_desc', label: '仓库料号 → 数量↓ → 订单号' },
  { value: 'order_date_then_order_no', label: '订单日期 → 订单号' },
  { value: 'client_po_then_order_no', label: '客户采购单号 → 订单号' },
  { value: 'customer_name_then_order_no', label: '客户 → 订单号' },
  {
    value: 'material_color_wh_ot_order',
    label: '内部材料名称 → 英文颜色 → 仓库料号 → 订单类型 → 订单号'
  },
  {
    value: 'order_type_material_color_wh_order',
    label: '订单类型 → 内部材料 → 英文颜色 → 仓库料号 → 订单号'
  },
  {
    value: 'wh_material_color_ot_order',
    label: '仓库料号 → 内部材料 → 英文颜色 → 订单类型 → 订单号'
  }
]

const cmpLocaleStr = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'zh-Hans-CN')

const displayInternalMaterialName = (row) =>
  String(row?.material_matching?.material_category_name || row?.internal_product_name || '').trim()

const clientColorEn = (row) => String(row?.client_color_en || '').trim()

const internalColorEn = (row) => String(row?.material_matching?.material_color_name_en || '').trim()

const matchingRemarksFromRow = (row) =>
  String(row?.material_matching?.matching_remarks || '').trim()

const matchingRemarksFromOrderItem = (row) =>
  String(row?.material_matching?.matching_remarks || row?.matching_remarks || '').trim()

const matchingRemarksFromDispatchItem = (row) =>
  String(row?.order_item?.material_matching?.matching_remarks || row?.matching_remarks || '').trim()

const internalColorEnFromOrderItem = (oi) =>
  String(oi?.internal_color_en || oi?.material_matching?.material_color_name_en || '').trim()

const displayWarehouseCode = (row) => {
  const fromMm = selectedBatchDisplay(row?.material_matching)
  if (fromMm && fromMm !== '—' && fromMm !== '待填仓库料号') return fromMm
  return String(
    row?.warehouse_lots_display ||
      row?.selected_batch_display ||
      row?.warehouse_manual_code ||
      row?.material_code ||
      ''
  ).trim()
}

const toDateMs = (v) => {
  if (!v) return 0
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? t : 0
}

/** 生成派工单：主表与已选清单共用同一套排序规则 */
function compareDispatchCreateRowsBySortMode(a, b, mode) {
  const cmpText = cmpLocaleStr
  const tieOrderNo = (x, y) => cmpText(x.order_no, y.order_no)
  const wa = displayWarehouseCode(a)
  const wb = displayWarehouseCode(b)
  const ma = displayInternalMaterialName(a)
  const mb = displayInternalMaterialName(b)
  const ca = internalColorEn(a)
  const cb = internalColorEn(b)
  const ota = String(a.order_type_name || '')
  const otb = String(b.order_type_name || '')

  if (mode === 'warehouse_code_then_qty_desc') {
    const c1 = cmpText(wa, wb)
    if (c1 !== 0) return c1
    const q = Number(b.quantity || 0) - Number(a.quantity || 0)
    if (Math.abs(q) > 1e-9) return q
    return tieOrderNo(a, b)
  }
  if (mode === 'order_date_then_order_no') {
    const d = toDateMs(a.order_date) - toDateMs(b.order_date)
    if (d !== 0) return d
    return tieOrderNo(a, b)
  }

  if (mode === 'client_po_then_order_no') {
    let x = cmpText(a.client_purchase_order, b.client_purchase_order)
    if (x !== 0) return x
    return tieOrderNo(a, b)
  }

  if (mode === 'customer_name_then_order_no') {
    let x = cmpText(a.customer_name, b.customer_name)
    if (x !== 0) return x
    return tieOrderNo(a, b)
  }

  if (mode === 'material_color_wh_ot_order') {
    let x = cmpText(ma, mb)
    if (x !== 0) return x
    x = cmpText(ca, cb)
    if (x !== 0) return x
    x = cmpText(wa, wb)
    if (x !== 0) return x
    x = cmpText(ota, otb)
    if (x !== 0) return x
    return tieOrderNo(a, b)
  }

  if (mode === 'order_type_material_color_wh_order') {
    let x = cmpText(ota, otb)
    if (x !== 0) return x
    x = cmpText(ma, mb)
    if (x !== 0) return x
    x = cmpText(ca, cb)
    if (x !== 0) return x
    x = cmpText(wa, wb)
    if (x !== 0) return x
    return tieOrderNo(a, b)
  }

  if (mode === 'wh_material_color_ot_order') {
    let x = cmpText(wa, wb)
    if (x !== 0) return x
    x = cmpText(ma, mb)
    if (x !== 0) return x
    x = cmpText(ca, cb)
    if (x !== 0) return x
    x = cmpText(ota, otb)
    if (x !== 0) return x
    return tieOrderNo(a, b)
  }

  const c1 = cmpText(wa, wb)
  if (c1 !== 0) return c1
  const d = toDateMs(a.order_date) - toDateMs(b.order_date)
  if (d !== 0) return d
  return tieOrderNo(a, b)
}

const sortedSelectedCreateItems = computed(() => {
  const list = Object.values(createSelectedPoolById.value || {})
  const mode = createSortMode.value
  return list.slice().sort((a, b) => compareDispatchCreateRowsBySortMode(a, b, mode))
})

/** 主表：先按合并分组块排序，块之间按当前排序方式；块内按 order_item_id；未合并行各占一块 */
function sortCreateRowsForMainTable(rows) {
  if (!Array.isArray(rows) || !rows.length) return []
  const mode = createSortMode.value
  const mergeGroups = new Map()
  const singleRows = []
  for (const r of rows) {
    const pk = pendingMergeGroupKeyByOrderItemId[r.order_item_id]
    if (pk) {
      if (!mergeGroups.has(pk)) mergeGroups.set(pk, [])
      mergeGroups.get(pk).push(r)
    } else {
      singleRows.push(r)
    }
  }
  for (const arr of mergeGroups.values()) {
    arr.sort((a, b) => (Number(a.order_item_id) || 0) - (Number(b.order_item_id) || 0))
  }
  const blocks = []
  for (const arr of mergeGroups.values()) {
    blocks.push({ rows: arr, rep: arr[0] })
  }
  for (const r of singleRows) {
    blocks.push({ rows: [r], rep: r })
  }
  blocks.sort((ba, bb) => compareDispatchCreateRowsBySortMode(ba.rep, bb.rep, mode))
  return blocks.flatMap((b) => b.rows)
}

function groupRowsByMaterialIdentity(rows) {
  const m = new Map()
  for (const row of rows) {
    const mid = row.material_matching?.material_identity_id
    const code = String(row.material_matching?.material_identity_code || '').trim()
    const key =
      mid != null && !Number.isNaN(Number(mid))
        ? `id:${Number(mid)}`
        : `code:${code || String(row.order_item_id)}`
    if (!m.has(key)) {
      m.set(key, {
        key,
        material_identity_id: mid != null ? Number(mid) : null,
        material_identity_code: code || null,
        category_name: row.material_matching?.material_category_name || row.internal_product_name || '',
        rows: [],
        totalQty: 0,
        unit_name: row.unit_name || ''
      })
    }
    const g = m.get(key)
    g.rows.push(row)
    g.totalQty += Number(row.quantity || 0)
    if (!g.unit_name && row.unit_name) g.unit_name = row.unit_name
  }
  return Array.from(m.values()).sort((a, b) =>
    cmpLocaleStr(a.material_identity_code || a.category_name, b.material_identity_code || b.category_name)
  )
}

const createMergeGroups = computed(() => groupRowsByMaterialIdentity(sortedSelectedCreateItems.value))

function buildMergeDispatchAppendNote(rows, strategy) {
  if (!strategy || strategy === 'off' || !rows?.length) return ''
  const groups = groupRowsByMaterialIdentity(rows)
  if (strategy === 'merge_total') {
    const lines = []
    lines.push(
      '【出库总量参考】以下为同一材料身份汇总：仓库按总数满足即可，可分一支或多支，不必按订单拆卷。'
    )
    for (const g of groups) {
      const label = g.material_identity_code || g.category_name || '—'
      const u = g.unit_name || ''
      const parts = g.rows.map((r) => `${r.order_no || '—'} ${formatNumber(r.quantity)}${u}`)
      lines.push(`· ${label} 合计 ${formatNumber(g.totalQty)}${u}：${parts.join('；')}。`)
    }
    return lines.join('\n')
  }
  if (strategy === 'per_order_lines') {
    const lines = []
    lines.push('【分卷说明】须按订单行数量分别出库/分卷（与客户约定一致时再选用此项）。')
    for (const g of groups) {
      const label = g.material_identity_code || g.category_name || '—'
      const u = g.unit_name || ''
      const parts = g.rows.map((r) => `${r.order_no || '—'} ${formatNumber(r.quantity)}${u}`)
      lines.push(`· ${label}：${parts.join('；')}。`)
    }
    return lines.join('\n')
  }
  return ''
}

watch(
  () => sortedSelectedCreateItems.value.length,
  (n) => {
    if (n === 0 && createPackagingBulkScope.value === 'pool') {
      createPackagingBulkScope.value = 'current'
    }
  }
)

const snapshotPackagingRow = (row) => ({
  ...row,
  material_usage_notes: row.material_usage_notes ?? '',
  packaging_type: row.packaging_type ?? '',
  packaging_require_weigh: !!row.packaging_require_weigh,
  packaging_require_volume: !!row.packaging_require_volume
})

const applyCreateBulkPackagingPatch = (patch) => {
  const scope = createPackagingBulkScope.value
  const pool = { ...(createSelectedPoolById.value || {}) }
  if (scope === 'current') {
    orderItemsData.value.forEach((r) => {
      if (patch.packaging_type !== undefined) r.packaging_type = patch.packaging_type
      if (patch.packaging_require_weigh !== undefined) r.packaging_require_weigh = patch.packaging_require_weigh
      if (patch.packaging_require_volume !== undefined) r.packaging_require_volume = patch.packaging_require_volume
      if (pool[r.order_item_id]) {
        pool[r.order_item_id] = snapshotPackagingRow({ ...pool[r.order_item_id], ...r })
      }
    })
  } else {
    Object.keys(pool).forEach((id) => {
      const row = { ...pool[id] }
      if (patch.packaging_type !== undefined) row.packaging_type = patch.packaging_type
      if (patch.packaging_require_weigh !== undefined) row.packaging_require_weigh = patch.packaging_require_weigh
      if (patch.packaging_require_volume !== undefined) row.packaging_require_volume = patch.packaging_require_volume
      pool[id] = snapshotPackagingRow(row)
    })
    orderItemsData.value.forEach((r) => {
      if (!pool[r.order_item_id]) return
      const p = pool[r.order_item_id]
      r.packaging_type = p.packaging_type ?? ''
      r.packaging_require_weigh = !!p.packaging_require_weigh
      r.packaging_require_volume = !!p.packaging_require_volume
    })
  }
  createSelectedPoolById.value = pool
}

const applyCreateBulkPackagingType = (v) => {
  applyCreateBulkPackagingPatch({ packaging_type: v })
}

const applyCreateBulkWeigh = (flag) => {
  applyCreateBulkPackagingPatch({ packaging_require_weigh: flag })
}

const applyCreateBulkVolume = (flag) => {
  applyCreateBulkPackagingPatch({ packaging_require_volume: flag })
}

const onPackagingDialogClosed = () => {
  packagingEditId.value = null
  packagingLines.value = []
}

const handleOpenPackaging = async (row) => {
  packagingEditId.value = row.dispatch_order_id
  packagingSaving.value = false
  try {
    const res = await getDispatchOrder(row.dispatch_order_id)
    if (!res.success || !res.data) {
      ElMessage.error(res.message || '加载失败')
      return
    }
    const d = res.data
    packagingLines.value = (d.items || []).map((it) => ({
      dispatch_order_item_id: it.dispatch_order_item_id,
      order_no: it.order_no || it.order_item?.order?.order_no || '—',
      material_usage_notes: it.material_usage_notes ?? '',
      packaging_type: it.packaging_type ?? d.packaging_type ?? '',
      packaging_require_weigh: !!(it.packaging_require_weigh ?? d.packaging_require_weigh),
      packaging_require_volume: !!(it.packaging_require_volume ?? d.packaging_require_volume)
    }))
    packagingDialogVisible.value = true
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
  }
}

const handleSavePackaging = async () => {
  if (!packagingEditId.value) return
  packagingSaving.value = true
  try {
    const res = await updateDispatchOrder(packagingEditId.value, {
      dispatch_item_packaging: packagingLines.value.map((l) => ({
        dispatch_order_item_id: l.dispatch_order_item_id,
        material_usage_notes: String(l.material_usage_notes || '').trim() || null,
        packaging_type: l.packaging_type || null,
        packaging_require_weigh: !!l.packaging_require_weigh,
        packaging_require_volume: !!l.packaging_require_volume
      }))
    })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      packagingDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    packagingSaving.value = false
  }
}

/** 配料已完成且可生成派工单的订单行数量（与 /completed-matchings 一致） */
const pendingDispatchCount = ref(0)

const loadPendingDispatchCount = async () => {
  try {
    const response = await getCompletedOrderItemMatchings({ count_only: true })
    if (response.success && response.data && typeof response.data.count === 'number') {
      pendingDispatchCount.value = response.data.count
    } else {
      pendingDispatchCount.value = 0
    }
  } catch (e) {
    console.error('loadPendingDispatchCount', e)
  }
}

const pendingKickbackCount = ref(0)
const kickbackDrawerVisible = ref(false)
const kickbackListLoading = ref(false)
const kickbackList = ref([])
const kickbackActionId = ref(null)
const kickbackAction = ref('')

const refreshKickbackCount = async () => {
  if (!canReviewKickback.value) {
    pendingKickbackCount.value = 0
    return
  }
  try {
    const res = await getDispatchOrderItemKickbackRequests({ status: 'pending' })
    if (res.success && Array.isArray(res.data)) {
      pendingKickbackCount.value = res.data.length
    } else {
      pendingKickbackCount.value = 0
    }
  } catch {
    pendingKickbackCount.value = 0
  }
}

const openKickbackDrawer = () => {
  kickbackDrawerVisible.value = true
}

const formatKickbackMaterialSummary = (d) => {
  if (!d) return '—'
  const parts = [
    d.client_material_name,
    d.client_color_cn || d.client_color_en,
    d.internal_product_name,
    d.internal_color_cn,
    d.internal_color_en,
    d.warehouse_manual_code || d.material_identity_code
  ].filter((p) => p && String(p).trim())
  return parts.length ? parts.join(' · ') : '—'
}

const formatKickbackColor = (d) => {
  if (!d) return '—'
  const cn = (d.client_color_cn || '').trim()
  const en = (d.client_color_en || '').trim()
  if (cn && en) return `${cn} / ${en}`
  return cn || en || '—'
}

const formatKickbackQty = (d) => {
  if (!d || d.quantity == null) return '—'
  const q = Number(d.quantity)
  const u = (d.unit_name || '').trim()
  if (!Number.isFinite(q)) return '—'
  const qty = Number.isInteger(q) ? String(q) : q.toFixed(4).replace(/\.?0+$/, '')
  return u ? `${qty} ${u}` : qty
}

const loadKickbackPendingList = async () => {
  if (!canReviewKickback.value) return
  kickbackListLoading.value = true
  try {
    const res = await getDispatchOrderItemKickbackRequests({ status: 'pending' })
    kickbackList.value = res.success && Array.isArray(res.data) ? res.data : []
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    kickbackList.value = []
  } finally {
    kickbackListLoading.value = false
  }
}

const approveKickback = async (row) => {
  const matLine = formatKickbackMaterialSummary(row.item_detail)
  const spec = row.item_detail?.specification
  const mergeN = Number(row.item_detail?.merge_group_member_count || 0)
  const mergeNos = Array.isArray(row.item_detail?.merge_group_order_nos)
    ? row.item_detail.merge_group_order_nos.filter(Boolean)
    : []
  const mergeHint =
    mergeN > 1
      ? `\n\n此为合并组踢回：同意后将一并移出组内全部 ${mergeN} 条派工明细并退回配料${
          mergeNos.length ? `（订单：${mergeNos.join('、')}）` : ''
        }。列表中同组其它待审行会一并结案。`
      : ''
  const detailHint =
    matLine && matLine !== '—'
      ? `\n\n材料：${matLine}${spec ? `\n规格：${spec}` : ''}`
      : ''
  try {
    await ElMessageBox.confirm(
      `确认同意？该订单明细将从派工单移除并退回配料模块，可在配料中重新处理。${mergeHint}${detailHint}`,
      '同意踢回配料',
      { type: 'warning', confirmButtonText: mergeN > 1 ? '同意整组移出' : '同意移出', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  kickbackActionId.value = row.kickback_request_id
  kickbackAction.value = 'approve'
  try {
    const res = await approveDispatchOrderItemKickbackRequest(row.kickback_request_id, {})
    if (res.success) {
      ElMessage.success(res.message || '已同意')
      await loadKickbackPendingList()
      await refreshKickbackCount()
      await loadData()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    kickbackActionId.value = null
    kickbackAction.value = ''
  }
}

const pendingPackagingAccessCount = ref(0)
const packagingAccessDrawerVisible = ref(false)
const packagingAccessListLoading = ref(false)
const packagingAccessList = ref([])
const packagingAccessActionId = ref(null)
const packagingAccessAction = ref('')
const packagingMeasuresVisible = ref(false)
const packagingMeasuresDispatchOrderId = ref(null)
const detailPackagingOperatorPick = ref(null)
const detailPackagingOperatorSaving = ref(false)

const detailPackagingAccessPending = computed(() => {
  const p = currentDispatchOrder.value?.packaging_access_request_pending
  return p?.status === 'pending' ? p : null
})

const showDetailPackagingSection = computed(() => {
  const o = currentDispatchOrder.value
  if (!o) return false
  if (detailPackagingAccessPending.value) return true
  if (o.packaging_operator_user_id) return true
  if (o.packaging_last_saved_by) return true
  return orderNeedsPackMeasures(o)
})

const detailNeedsPackagingMeasures = computed(() => orderNeedsPackMeasures(currentDispatchOrder.value))

watch(
  () => currentDispatchOrder.value?.packaging_operator_user_id,
  (v) => {
    detailPackagingOperatorPick.value = v ?? null
  }
)

const refreshPackagingAccessCount = async () => {
  if (!canReviewKickback.value) {
    pendingPackagingAccessCount.value = 0
    return
  }
  try {
    const res = await getDispatchPackagingAccessRequests({ status: 'pending' })
    if (res.success && Array.isArray(res.data)) {
      pendingPackagingAccessCount.value = res.data.length
    } else {
      pendingPackagingAccessCount.value = 0
    }
  } catch {
    pendingPackagingAccessCount.value = 0
  }
}

const openPackagingAccessDrawer = () => {
  packagingAccessDrawerVisible.value = true
}

function openPackagingMeasures(row) {
  const id = row?.dispatch_order_id
  if (!id) return
  packagingMeasuresDispatchOrderId.value = id
  packagingMeasuresVisible.value = true
}

function openPackagingMeasuresForCurrent() {
  if (!currentDispatchOrder.value) return
  openPackagingMeasures(currentDispatchOrder.value)
}

function openPackagingMeasuresFromAccessRow(row) {
  openPackagingMeasures({ dispatch_order_id: row.dispatch_order_id })
}

async function openDispatchDetailFromPackagingAccess(row) {
  packagingAccessDrawerVisible.value = false
  const id = row.dispatch_order_id
  const inList = tableData.value.find((d) => d.dispatch_order_id === id)
  detailPanelMode.value = 'view'
  postCompletionNotes.value = ''
  packagingAuditNotes.value = ''
  preAuditNotes.value = ''
  currentDispatchOrder.value = inList || { dispatch_order_id: id, dispatch_order_no: row.dispatch_order_no }
  detailDialogVisible.value = true
  await loadDispatchOrderDetail(id)
}

async function onPackagingMeasuresSaved() {
  await loadData()
  const id = packagingMeasuresDispatchOrderId.value
  if (id && detailDialogVisible.value && currentDispatchOrder.value?.dispatch_order_id === id) {
    await loadDispatchOrderDetail(id)
  }
}

async function onPackagingAccessChanged() {
  await refreshPackagingAccessCount()
  const id =
    packagingMeasuresDispatchOrderId.value || currentDispatchOrder.value?.dispatch_order_id
  if (id && detailDialogVisible.value && currentDispatchOrder.value?.dispatch_order_id === id) {
    await loadDispatchOrderDetail(id)
  }
  if (packagingAccessDrawerVisible.value) {
    await loadPackagingAccessPendingList()
  }
}

async function saveDetailPackagingOperator() {
  const o = currentDispatchOrder.value
  if (!o?.dispatch_order_id) return
  detailPackagingOperatorSaving.value = true
  try {
    const res = await assignDispatchPackagingOperator(o.dispatch_order_id, {
      user_id: detailPackagingOperatorPick.value ?? null
    })
    if (res.success) {
      ElMessage.success(res.message || '已更新')
      await loadDispatchOrderDetail(o.dispatch_order_id)
      await loadData()
      await refreshPackagingAccessCount()
    } else {
      ElMessage.error(res.message || '保存失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    detailPackagingOperatorSaving.value = false
  }
}

const loadPackagingAccessPendingList = async () => {
  if (!canReviewKickback.value) return
  packagingAccessListLoading.value = true
  try {
    const res = await getDispatchPackagingAccessRequests({ status: 'pending' })
    packagingAccessList.value = res.success && Array.isArray(res.data) ? res.data : []
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    packagingAccessList.value = []
  } finally {
    packagingAccessListLoading.value = false
  }
}

const approvePackagingAccess = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确认指定「${row.requester_name || '该同事'}」负责本单包装资料填写？同意后其他人将不能再改包装实测。`,
      '同意包装填写申请',
      { type: 'warning', confirmButtonText: '同意指定', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  packagingAccessActionId.value = row.access_request_id
  packagingAccessAction.value = 'approve'
  try {
    const res = await approveDispatchPackagingAccessRequest(row.access_request_id, {})
    if (res.success) {
      ElMessage.success(res.message || '已指定')
      await onPackagingAccessChanged()
      await loadData()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    packagingAccessActionId.value = null
    packagingAccessAction.value = ''
  }
}

const rejectPackagingAccess = async (row) => {
  let note = ''
  try {
    const { value } = await ElMessageBox.prompt('请输入驳回说明（选填）', '驳回包装填写申请', {
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
      inputPlaceholder: '选填'
    })
    note = (value || '').trim()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(String(e?.message || e))
    return
  }
  packagingAccessActionId.value = row.access_request_id
  packagingAccessAction.value = 'reject'
  try {
    const res = await rejectDispatchPackagingAccessRequest(row.access_request_id, { review_notes: note })
    if (res.success) {
      ElMessage.success(res.message || '已驳回')
      await onPackagingAccessChanged()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    packagingAccessActionId.value = null
    packagingAccessAction.value = ''
  }
}

const rejectKickback = async (row) => {
  let note = ''
  const mergeN = Number(row.item_detail?.merge_group_member_count || 0)
  try {
    const { value } = await ElMessageBox.prompt(
      mergeN > 1
        ? `请输入驳回说明（至少 2 个字）。此为合并组申请，驳回将一并结案组内全部待审行。`
        : '请输入驳回说明（至少 2 个字）',
      '驳回踢回申请',
      {
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
      inputPlaceholder: '例如：现场可继续，不同意移出',
      inputValidator: (v) => {
        const s = (v || '').trim()
        if (s.length < 2) return '至少 2 个字'
        return true
      }
    }
    )
    note = (value || '').trim()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(String(e?.message || e))
    return
  }
  kickbackActionId.value = row.kickback_request_id
  kickbackAction.value = 'reject'
  try {
    const res = await rejectDispatchOrderItemKickbackRequest(row.kickback_request_id, { review_notes: note })
    if (res.success) {
      ElMessage.success(res.message || '已驳回')
      await loadKickbackPendingList()
      await refreshKickbackCount()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '操作失败')
  } finally {
    kickbackActionId.value = null
    kickbackAction.value = ''
  }
}

// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 20,
  total: 0
})

// 默认列配置（派工单列表）
const defaultColumnConfig = [
  { prop: 'dispatch_order_no', label: '派工单号', width: 150, visible: true, fixed: 'left' },
  { prop: 'order_no', label: '订单号', width: 120, visible: true },
  { prop: 'client_purchase_order', label: '客户采购单号', width: 140, visible: true },
  { prop: 'order_date', label: '订单日期', width: 110, visible: true },
  { prop: 'customer_name', label: '客户', width: 120, visible: true },
  { prop: 'brand_name', label: '品牌', width: 100, visible: true },
  { prop: 'seasons_display', label: '季节', width: 120, visible: true },
  { prop: 'order_types_display', label: '订单类型', width: 120, visible: true },
  { prop: 'client_colors_en_display', label: '客户英文颜色', width: 140, visible: true },
  { prop: 'material_code', label: '已选料号', width: 150, visible: true },
  { prop: 'matching_remarks', label: '配料备注', width: 160, visible: false },
  { prop: 'material_usage_notes', label: '用料备注', width: 180, visible: false },
  { prop: 'mobile_slitting_progress', label: '分卷确认进度', width: 130, visible: true },
  { prop: 'status', label: '状态', width: 100, visible: true },
  { prop: 'audit_status', label: '派工审核', width: 96, visible: true },
  { prop: 'post_completion_audit_status', label: '打码核销', width: 96, visible: true },
  { prop: 'packaging_audit_status', label: '包装核销', width: 96, visible: true },
  { prop: 'assigned_user_name', label: '指派人员', width: 120, visible: true },
  { prop: 'required_completion_summary', label: '要求完成', width: 200, visible: true },
  { prop: 'item_count', label: '明细数量', width: 100, visible: true },
  { prop: 'dispatch_quantity', label: '派工数量', width: 110, visible: true },
  { prop: 'items_list_summary', label: '明细摘要', width: 320, visible: true },
  { prop: 'notes', label: '备注', width: 200, visible: false },
  { prop: 'creator_name', label: '创建人', width: 100, visible: false },
  { prop: 'created_at', label: '创建时间', width: 160, visible: true },
  { prop: 'updated_at', label: '更新时间', width: 160, visible: false }
]

// 列配置（从localStorage加载）
const columnConfig = ref([...defaultColumnConfig])

// 加载列配置（顺序以本地保存为准，与 ColumnConfigDialog 逻辑一致）
const loadColumnConfig = () => {
  try {
    const savedConfig = localStorage.getItem(`${dispatchColumnConfigStorageKey.value}_columns_config`)
    if (!savedConfig) {
      columnConfig.value = defaultColumnConfig.map((c) => ({ ...c }))
      return
    }
    const parsedConfig = JSON.parse(savedConfig)
    if (!Array.isArray(parsedConfig)) {
      columnConfig.value = defaultColumnConfig.map((c) => ({ ...c }))
      return
    }
    const savedProps = new Set(parsedConfig.map((col) => col.prop))
    const merged = parsedConfig.map((savedCol) => {
      const defaultCol = defaultColumnConfig.find((c) => c.prop === savedCol.prop)
      return defaultCol ? { ...defaultCol, ...savedCol } : { ...savedCol }
    })
    defaultColumnConfig.forEach((defaultCol) => {
      if (!savedProps.has(defaultCol.prop)) {
        merged.push({ ...defaultCol })
      }
    })
    columnConfig.value = merged
  } catch (error) {
    console.error('加载列配置失败:', error)
    columnConfig.value = defaultColumnConfig.map((c) => ({ ...c }))
  }
}

// 保存列配置
const saveColumnConfig = () => {
  try {
    localStorage.setItem(`${dispatchColumnConfigStorageKey.value}_columns_config`, JSON.stringify(columnConfig.value))
  } catch (error) {
    console.error('保存列配置失败:', error)
  }
}

// 显示列配置对话框
const handleShowColumnConfig = () => {
  showColumnConfig.value = true
}

// 列配置变化（对话框内勾选/拖拽/重置等都会触发）
const handleColumnConfigChange = (newConfig) => {
  if (!Array.isArray(newConfig)) return
  columnConfig.value = newConfig.map((c) => ({ ...c }))
  saveColumnConfig()
  nextTick(() => {
    tableRef.value?.doLayout?.()
  })
}

// 可见列
const visibleColumns = computed(() => {
  return columnConfig.value.filter(col => col.visible)
})

// 加载派工单列表数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.currentPage,
      per_page: pagination.pageSize,
      status: filterStatus.value || undefined
    }
    const kw = searchKeyword.value?.trim()
    if (kw) params.search = kw
    const wh = filterWarehouseManualCode.value?.trim()
    if (wh) params.warehouse_manual_code = wh
    if (filterAuditStatus.value) params.audit_status = filterAuditStatus.value
    if (filterAssignedUserId.value != null && filterAssignedUserId.value !== '') {
      params.assigned_user_id = Number(filterAssignedUserId.value)
    }
    if (filterPostCompletionStatus.value) {
      params.post_completion_audit_status = filterPostCompletionStatus.value
    }

    const response = await getDispatchOrders(params)
    if (response.success) {
      tableData.value = response.data || []
      pagination.total = response.pagination?.total || 0
    } else {
      ElMessage.error('加载数据失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    ElMessage.error('加载数据失败: ' + (error.response?.data?.message || error.message))
  } finally {
    loading.value = false
    loadPendingDispatchCount()
    refreshKickbackCount()
    refreshPackagingAccessCount()
  }
}

// 搜索
const handleSearch = () => {
  pagination.currentPage = 1
  loadData()
}

// 状态标签类型
const getStatusTagType = (status) => {
  const map = {
    'draft': 'info',
    'assigned': 'warning',
    'in_progress': 'primary',
    'completed': 'success',
    'cancelled': 'danger'
  }
  return map[status] || 'info'
}

// 状态文本
const getStatusText = (status) => {
  const map = {
    'draft': '草稿',
    'assigned': '已指派',
    'in_progress': '进行中',
    'completed': '已完成',
    'cancelled': '已取消'
  }
  return map[status] || status
}

/** 库内 status=completed 但本分卷确认条数未齐：列表/详情不按「已完成」展示 */
const dispatchSlittingProgressIncomplete = (o) => {
  if (!o || o.status !== 'completed') return false
  const t = Number(o.mobile_slitting_total_lines ?? o.item_count ?? 0)
  const c = Number(o.mobile_slitting_confirmed_count ?? 0)
  if (!Number.isFinite(t) || t <= 0) return false
  return c < t
}

const getDispatchRowStatusTagType = (row) => {
  return dispatchSlittingProgressIncomplete(row) ? 'warning' : getStatusTagType(row.status)
}

const getDispatchRowStatusText = (row) => {
  return dispatchSlittingProgressIncomplete(row) ? '进行中' : getStatusText(row.status)
}

// 审核状态标签类型
const getAuditStatusTagType = (auditStatus) => {
  const map = {
    'pending': 'warning',
    'approved': 'success',
    'rejected': 'danger'
  }
  return map[auditStatus] || 'info'
}

// 审核状态文本
const getAuditStatusText = (auditStatus) => {
  const map = {
    'pending': '待审核',
    'approved': '已审核',
    'rejected': '已拒绝'
  }
  return map[auditStatus] || auditStatus
}

const isProcessingOutboundDispatch = (row) =>
  !!(row && (row.dispatch_purpose === 'processing_outbound' || row.is_processing_outbound))

const getPostCompletionAuditText = (rowOrStatus) => {
  const row =
    typeof rowOrStatus === 'object' && rowOrStatus != null && 'post_completion_audit_status' in rowOrStatus
      ? rowOrStatus
      : null
  const s = row ? row.post_completion_audit_status || 'na' : rowOrStatus || 'na'
  if (row && isProcessingOutboundDispatch(row)) return '无需打码核销'
  const map = {
    na: '—',
    pending: '待打码核销',
    approved: '打码已核销',
    rejected: '打码退回'
  }
  return map[s] || s || '—'
}

const getPostCompletionAuditTagType = (rowOrStatus) => {
  const row =
    typeof rowOrStatus === 'object' && rowOrStatus != null && 'post_completion_audit_status' in rowOrStatus
      ? rowOrStatus
      : null
  if (row && isProcessingOutboundDispatch(row)) return 'info'
  const s = row ? row.post_completion_audit_status || 'na' : rowOrStatus || 'na'
  const map = { na: 'info', pending: 'warning', approved: 'success', rejected: 'danger' }
  return map[s] || 'info'
}

const getPackagingAuditText = (s) => {
  const map = {
    na: '—',
    pending: '待包装核销',
    approved: '包装已核销',
    rejected: '包装退回'
  }
  return map[s] || s || '—'
}

const getPackagingAuditTagType = (s) => {
  const map = { na: 'info', pending: 'warning', approved: 'success', rejected: 'danger' }
  return map[s] || 'info'
}

// 格式化日期时间
const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  } catch (e) {
    return dateString
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('zh-CN')
  } catch (e) {
    return dateString
  }
}

/** 订单明细行：季节名称 + 年份 */
const formatSeasonRow = (row) => {
  const name = row?.season_name
  const year = row?.season_year
  if (!name && (year === null || year === undefined || String(year).trim() === '')) return '—'
  return [name, year].filter((x) => x != null && String(x).trim() !== '').join(' ')
}

// 格式化数字
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-'
  return Number(num).toLocaleString()
}

/** 列表列：移动端本分卷已确认数 / 明细条数 */
const formatMobileSlittingProgress = (row) => {
  const c = row?.mobile_slitting_confirmed_count ?? 0
  const t = row?.mobile_slitting_total_lines ?? row?.item_count ?? 0
  return `${c}/${t}`
}

const getBranchRowsForDispatchDetail = (row) => {
  const list = Array.isArray(row?.branch_records) ? row.branch_records : []
  return list
    .slice()
    .sort((a, b) => (Number(a?.line_no) || 0) - (Number(b?.line_no) || 0))
}

/** 分码重量（与移动端包装称重页一致，单位 kg） */
const formatPackWeightKg = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  if (Number.isNaN(n)) return null
  const r = Math.round(n * 10) / 10
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

const formatBranchWeightsMeasured = (row) => {
  const brs = getBranchRowsForDispatchDetail(row)
  const parts = []
  for (const br of brs) {
    const s = formatPackWeightKg(br.weight_kg)
    if (s != null) parts.push(s)
  }
  if (!parts.length) return '—'
  if (parts.length <= 4) return `${parts.join('、')} kg`
  return `${parts.slice(0, 4).join('、')}…共${parts.length}支 kg`
}

const formatBranchDimsOneLine = (br) => {
  if (!br) return ''
  const L = br.length_cm
  const W = br.width_cm
  const H = br.height_cm
  const hasAny = [L, W, H].some((x) => x != null && x !== '' && !Number.isNaN(Number(x)))
  if (!hasAny) return ''
  const fmt = (x) => {
    if (x == null || x === '' || Number.isNaN(Number(x))) return '?'
    return String(Math.round(Number(x)))
  }
  return `${fmt(L)}×${fmt(W)}×${fmt(H)}`
}

const formatBranchDimsMeasured = (row) => {
  const brs = getBranchRowsForDispatchDetail(row)
  const lines = []
  for (const br of brs) {
    const one = formatBranchDimsOneLine(br)
    if (one) lines.push(one)
  }
  if (!lines.length) return '—'
  if (lines.length <= 2) return lines.join('； ')
  return `${lines.slice(0, 2).join('； ')}…共${lines.length}支`
}

const hasDispatchWholeOrderPackMeasured = (o) => {
  if (!o) return false
  if (o.warehouse_packed_weight_kg != null && o.warehouse_packed_weight_kg !== '') return true
  return ['warehouse_packed_length_cm', 'warehouse_packed_width_cm', 'warehouse_packed_height_cm'].some(
    (k) => o[k] != null && o[k] !== ''
  )
}

const formatDispatchWholeOrderPackMeasured = (o) => {
  if (!o) return '—'
  const parts = []
  const w = formatPackWeightKg(o.warehouse_packed_weight_kg)
  if (w != null) parts.push(`重量 ${w} kg`)
  const L = o.warehouse_packed_length_cm
  const W = o.warehouse_packed_width_cm
  const H = o.warehouse_packed_height_cm
  if ([L, W, H].every((x) => x != null && x !== '' && !Number.isNaN(Number(x)))) {
    parts.push(
      `外尺寸 ${Math.round(Number(L))}×${Math.round(Number(W))}×${Math.round(Number(H))} cm（整单）`
    )
  }
  return parts.length ? parts.join(' · ') : '—'
}

// 获取库存库位（派工明细 / 生成派工 / 编辑）
const collectInventoryLocationsFromRow = (row) => {
  const sources = [
    row?.inventory_locations,
    row?.order_item?.material_matching?.inventory_locations,
    row?.material_matching?.inventory_locations
  ]
  for (const src of sources) {
    if (!src) continue
    try {
      const arr = Array.isArray(src) ? src : [...src]
      if (arr.length) return arr
    } catch {
      /* ignore */
    }
  }
  return []
}

const formatStorageLocationLabel = (loc) => {
  if (!loc) return '—'
  const code = (loc.location_code || '').trim()
  const name = (loc.location_name || '').trim()
  if (code && name && name !== code) return `${code}(${name})`
  return code || name || '库位'
}

const filterLocationsForWarehouse = (locations, warehouseCode) => {
  if (!locations?.length) return []
  const wh = (warehouseCode || '').trim()
  if (!wh) return locations
  const matched = locations.filter((loc) => {
    const locWh = (loc.warehouse_manual_code || '').trim()
    if (!locWh || locWh === '未分配') return true
    return locWh === wh
  })
  return matched.length ? matched : locations
}

// 获取与仓库料号匹配的库存位置（用于查看对话框）
const getMatchingLocationsForView = (row) => {
  const inventoryLocations = collectInventoryLocationsFromRow(row)
  if (!inventoryLocations.length) return []

  const warehouseCode =
    row.warehouse_manual_code ||
    row.material_code ||
    row.order_item?.material_matching?.warehouse_manual_code ||
    row.material_matching?.warehouse_manual_code

  return filterLocationsForWarehouse(inventoryLocations, warehouseCode)
}

// 获取与仓库料号匹配的库存位置（用于编辑对话框）
const getMatchingLocationsForEdit = (row) => {
  const inventoryLocations = collectInventoryLocationsFromRow(row)
  if (!inventoryLocations.length) return []

  const warehouseCode =
    row.material_matching?.warehouse_manual_code ||
    row.warehouse_manual_code ||
    row.material_code

  return filterLocationsForWarehouse(inventoryLocations, warehouseCode)
}

// 获取与仓库料号匹配的库存位置（用于创建对话框）
const getMatchingLocationsForCreate = (row) => {
  const inventoryLocations = row.material_matching?.inventory_locations || []
  if (!inventoryLocations.length) return []

  const warehouseCode = row.material_matching?.warehouse_manual_code
  return filterLocationsForWarehouse(inventoryLocations, warehouseCode)
}

// 派工单详情相关
const detailDialogVisible = ref(false)
const detailPanelMode = ref('view')

const mobileSlittingConfirmedDisplay = computed(() => {
  const o = currentDispatchOrder.value
  if (!o) return '—'
  const c = o.mobile_slitting_confirmed_count ?? 0
  const t = o.mobile_slitting_total_lines ?? o.item_count ?? 0
  return `${c} / ${t} 条`
})

const mobileSlittingProgressPercent = computed(() => {
  const o = currentDispatchOrder.value
  if (!o) return 0
  const t = Number(o.mobile_slitting_total_lines ?? o.item_count ?? 0)
  const c = Number(o.mobile_slitting_confirmed_count ?? 0)
  if (!t || t <= 0) return 0
  return Math.min(100, Math.round((c / t) * 100))
})
const detailDueSummary = computed(() => classifyDispatchDue(currentDispatchOrder.value || {}))
const dispatchDetailItemsTableRef = ref(null)
const detailLoading = ref(false)

/** 派工单详情 · 订单明细表：可拖拽列宽，持久化到 localStorage */
const dispatchDetailItemColWidthsStorageKey = computed(() =>
  getUserScopedStorageKey('erp_dispatch_order_detail_items_col_widths_v1')
)
const DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT = Object.freeze({
  order_no: 110,
  client_purchase_order: 130,
  order_date: 100,
  customer_name: 120,
  brand_name: 100,
  season_row: 110,
  order_type_name: 110,
  client_material_name: 150,
  client_color_cn: 100,
  client_color_en: 120,
  internal_product_name: 150,
  internal_color_cn: 130,
  internal_color_en: 120,
  item_notes_1: 120,
  item_notes_2: 120,
  item_notes_3: 120,
  spec: 100,
  order_line_qty: 88,
  merged_qty: 88,
  unit_name: 60,
  branch_expand: 52,
  warehouse_code: 150,
  matching_remarks: 160,
  material_usage_notes: 160,
  stock_location: 200,
  stock_qty: 120,
  line_packaging_type: 110,
  line_weigh: 56,
  line_volume: 56,
  branch_weight_summary: 132,
  branch_dims_summary: 160
})
const dispatchDetailItemColWidths = ref({ ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT })

const loadDispatchDetailItemColWidths = () => {
  try {
    const raw = localStorage.getItem(dispatchDetailItemColWidthsStorageKey.value)
    if (!raw) {
      dispatchDetailItemColWidths.value = { ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT }
      return
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      dispatchDetailItemColWidths.value = { ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT }
      return
    }
    dispatchDetailItemColWidths.value = { ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT, ...parsed }
  } catch {
    dispatchDetailItemColWidths.value = { ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT }
  }
}

const persistDispatchDetailItemColWidths = () => {
  try {
    localStorage.setItem(dispatchDetailItemColWidthsStorageKey.value, JSON.stringify(dispatchDetailItemColWidths.value))
  } catch (e) {
    console.warn('save dispatch detail col widths failed', e)
  }
}

const handleDispatchDetailItemsHeaderDragEnd = (newWidth, _oldWidth, column) => {
  const key = column?.columnKey || column?.property
  if (!key || key === 'branch_expand') return
  const w = Math.max(48, Math.round(Number(newWidth)))
  dispatchDetailItemColWidths.value = { ...dispatchDetailItemColWidths.value, [key]: w }
  persistDispatchDetailItemColWidths()
}

const resetDispatchDetailItemColWidths = () => {
  localStorage.removeItem(dispatchDetailItemColWidthsStorageKey.value)
  dispatchDetailItemColWidths.value = { ...DISPATCH_DETAIL_ITEM_COL_WIDTHS_DEFAULT }
  ElMessage.success('已恢复默认列宽')
}
const preAuditNotes = ref('')
const preAuditLoading = ref(false)
const postCompletionNotes = ref('')
const postCompletionLoading = ref(false)
const processingOutboundCompleteLoading = ref(false)
const outboundLocDialogVisible = ref(false)
const outboundLocPayload = ref(null)
const postCompletionRevokeLoading = ref(false)
const packagingAuditNotes = ref('')
const packagingAuditLoading = ref(false)
const packagingAuditRevokeLoading = ref(false)
/** 列表行「取消核销」loading，存 dispatch_order_id */
const postCompletionRevokeRowId = ref(null)
/** 列表行「退回仓库修改」loading */
const reopenDispatchRowId = ref(null)
const editForm = ref(null)
const createDispatchFormRef = ref(null)
const editDialogOpenedFlag = ref(false)

const detailDialogTitle = computed(() =>
  detailPanelMode.value === 'audit_pre' ? '派工单审核（请核对下方全部明细）' : '派工单详情'
)

const setAllDispatchDetailBranchExpansion = async (expanded) => {
  const table = dispatchDetailItemsTableRef.value
  const rows = dispatchDetailItemsSortedForTable.value || []
  if (!table || !rows.length) return
  await nextTick()
  rows.forEach((row) => {
    table.toggleRowExpansion(row, expanded)
  })
}

const expandAllDispatchDetailBranches = () => setAllDispatchDetailBranchExpansion(true)
const collapseAllDispatchDetailBranches = () => setAllDispatchDetailBranchExpansion(false)

const detailDispatchItemRowKey = (row) =>
  row?.dispatch_order_item_id ?? row?.order_item_id ?? `idx-${row?.order_no || ''}-${row?.order_item?.order_item_id || ''}`

/** 合并组在表格中相邻排列，便于「合并数量」列纵向合并，只显示一组总和 */
function sortDispatchItemsByMergeGroup(items) {
  if (!Array.isArray(items) || !items.length) return []
  const list = [...items]
  list.sort((a, b) => {
    const sortKey = (r) =>
      r.merge_group_key ? `g:${r.merge_group_key}` : `s:${r.dispatch_order_item_id ?? r.order_item_id ?? ''}`
    const cmp = sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true })
    if (cmp !== 0) return cmp
    return (Number(a.dispatch_order_item_id) || 0) - (Number(b.dispatch_order_item_id) || 0)
  })
  return list
}

const dispatchDetailItemsSortedForTable = computed(() =>
  sortDispatchItemsByMergeGroup(currentDispatchOrder.value?.items || [])
)

const dispatchDetailMergedQtySpanMethod = ({ row, column, rowIndex }) => {
  if (column.columnKey !== 'merged_qty') return [1, 1]
  const rows = dispatchDetailItemsSortedForTable.value
  const mk = row.merge_group_key
  if (!mk) return [1, 1]
  let start = rowIndex
  while (start > 0 && rows[start - 1]?.merge_group_key === mk) start--
  if (rowIndex !== start) return [0, 0]
  let span = 1
  while (start + span < rows.length && rows[start + span]?.merge_group_key === mk) span++
  return [span, 1]
}

const createOrderItemsSortedForTable = computed(() => sortCreateRowsForMainTable(orderItemsData.value))

const createMergedQtySpanMethod = ({ row, column, rowIndex }) => {
  if (column.columnKey !== 'merged_qty') return [1, 1]
  const rows = createOrderItemsSortedForTable.value
  const mk = pendingMergeGroupKeyByOrderItemId[row.order_item_id]
  if (!mk) return [1, 1]
  let start = rowIndex
  while (
    start > 0 &&
    pendingMergeGroupKeyByOrderItemId[rows[start - 1]?.order_item_id] === mk
  )
    start--
  if (rowIndex !== start) return [0, 0]
  let span = 1
  while (
    start + span < rows.length &&
    pendingMergeGroupKeyByOrderItemId[rows[start + span]?.order_item_id] === mk
  )
    span++
  return [span, 1]
}

/** 新标签打开派工单（保留当前页弹窗状态，另开页对照查阅） */
const openDispatchWorkInNewTab = (row) => {
  const order = row || currentDispatchOrder.value
  const id = order?.dispatch_order_id
  if (!id) {
    ElMessage.warning('请先生成或保存派工单后再在新标签打开')
    return
  }
  const r = router.resolve({
    path: '/orders/dispatch-order',
    query: {
      open_dispatch_order_id: String(id),
      open_dispatch_order_no: order.dispatch_order_no || ''
    }
  })
  const url = new URL(r.href, window.location.origin).href
  const w = window.open(url, '_blank')
  if (!w) {
    ElMessage.warning('浏览器拦截了新窗口，请允许本站弹窗；或先切换菜单查阅，返回本页继续填写。')
  }
}

// 行点击事件
const handleRowClick = (row) => {
  detailPanelMode.value = 'view'
  postCompletionNotes.value = ''
  packagingAuditNotes.value = ''
  preAuditNotes.value = ''
  currentDispatchOrder.value = row
  detailDialogVisible.value = true
  loadDispatchOrderDetail(row.dispatch_order_id)
}

const handleRefreshDispatchDetail = async () => {
  const id = currentDispatchOrder.value?.dispatch_order_id
  if (!id) return
  const ok = await loadDispatchOrderDetail(id)
  if (ok) ElMessage.success('已刷新为最新数据')
}

const handleDispatchDetailItemsSelectionChange = (selection) => {
  detailTableSelectionRefRows.value = selection || []
}

const handleDispatchDetailMergeQuantity = async () => {
  const id = currentDispatchOrder.value?.dispatch_order_id
  if (!id) return
  const oids = (detailTableSelectionRefRows.value || []).map((r) => r.order_item_id).filter(Boolean)
  if (oids.length < 2) {
    ElMessage.warning('请至少勾选两条明细再合并数量')
    return
  }
  detailDispatchMergeLoading.value = true
  try {
    const res = await applyDispatchMergeQuantity(id, oids)
    if (res.success) {
      ElMessage.success(res.message || '已合并')
      await loadDispatchOrderDetail(id)
    } else {
      ElMessage.error(res.message || '合并失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '合并失败')
  } finally {
    detailDispatchMergeLoading.value = false
  }
}

const handleDispatchDetailSplitMergeQuantity = async () => {
  const id = currentDispatchOrder.value?.dispatch_order_id
  if (!id) return
  const oids = (detailTableSelectionRefRows.value || []).map((r) => r.order_item_id).filter(Boolean)
  if (!oids.length) {
    ElMessage.warning('请勾选要拆分合并的明细')
    return
  }
  detailDispatchMergeLoading.value = true
  try {
    const res = await splitDispatchMergeQuantity(id, oids)
    if (res.success) {
      ElMessage.success(res.message || '已拆分')
      await loadDispatchOrderDetail(id)
    } else {
      ElMessage.error(res.message || '拆分失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '拆分失败')
  } finally {
    detailDispatchMergeLoading.value = false
  }
}

const tryOpenDispatchFromRoute = async () => {
  const openId = String(route.query.open_dispatch_order_id || '').trim()
  const openNo = String(route.query.open_dispatch_order_no || '').trim()
  const openKey = `${openId}::${openNo}`
  if (!openId && !openNo) return
  if (openedDispatchFromRoute.value === openKey) return

  const pidNum = openId ? Number(openId) : NaN
  if (Number.isFinite(pidNum) && pidNum > 0) {
    openedDispatchFromRoute.value = openKey
    detailPanelMode.value = 'view'
    detailDialogVisible.value = true
    await loadDispatchOrderDetail(pidNum)
    return
  }

  const matched = (tableData.value || []).find((item) => {
    if (openId && String(item.dispatch_order_id) === openId) return true
    if (openNo && String(item.dispatch_order_no || '') === openNo) return true
    return false
  })
  if (!matched) return
  openedDispatchFromRoute.value = openKey
  handleRowClick(matched)
}

const applyOpenDispatchSearchPreset = () => {
  const openNo = String(route.query.open_dispatch_order_no || '').trim()
  if (!openNo) return
  if (searchKeyword.value !== openNo) {
    searchKeyword.value = openNo
    pagination.currentPage = 1
  }
}

// 加载派工单详情（返回是否成功，便于刷新按钮提示）
const loadDispatchOrderDetail = async (dispatchOrderId) => {
  detailLoading.value = true
  let success = false
  try {
    const response = await getDispatchOrder(dispatchOrderId)
    if (response.success) {
      currentDispatchOrder.value = response.data
      success = true
    } else {
      ElMessage.error('加载派工单详情失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    console.error('加载派工单详情失败:', error)
    ElMessage.error('加载派工单详情失败: ' + (error.response?.data?.message || error.message))
  } finally {
    detailLoading.value = false
  }
  return success
}

// 加载下拉数据
const loadFilterOptions = async () => {
  try {
    // 加载客户列表
    const customersRes = await getActiveCustomers({ page: 1, per_page: 1000 })
    if (customersRes.success) {
      customerList.value = customersRes.data || []
    }
    
    // 加载品牌列表
    const brandsRes = await getBrands()
    if (brandsRes.success) {
      brandList.value = brandsRes.data || []
    }
    
    // 材料名称和颜色列表会在loadOrderItems中动态更新
  } catch (error) {
    console.error('加载筛选选项失败:', error)
  }
}

async function loadCustomerPackagingRequirementsMap() {
  try {
    const res = await getCustomerPackagingRequirementsResolveMap()
    if (res.success) {
      customerPackagingRequirementsNested.value = res.nested || {}
      if (createDialogVisible.value && orderItemsData.value?.length) {
        orderItemsData.value = orderItemsData.value.map((row) => {
          const picked = createSelectedPoolById.value?.[row.order_item_id]
          if (picked) return row
          const withRule = applyCustomerPackagingRequirementToRow(
            row,
            customerPackagingRequirementsNested.value
          )
          return {
            ...row,
            packaging_type: withRule.packaging_type ?? '',
            packaging_require_weigh: !!withRule.packaging_require_weigh,
            packaging_require_volume: !!withRule.packaging_require_volume
          }
        })
      }
    }
  } catch (error) {
    console.error('加载客户包装要求失败:', error)
  }
}

function onCustomerPackagingRulesOpened() {
  customerPackagingRulesPanelRef.value?.loadRules?.()
}

// 重置筛选条件并重新加载待派工明细
const resetCreateFormFilter = () => {
  createFormFilter.value = {
    searchKeyword: '',
    customerId: null,
    brandId: null,
    orderTypeId: null,
    materialCategoryName: '',
    materialColorName: '',
    internalColorEn: '',
    materialCode: ''
  }
  createSelectedPoolById.value = {}
  selectedOrderItems.value = []
  nextTick(() => {
    orderItemsTableRef.value?.clearSelection?.()
  })
  loadOrderItems()
}

const onCreateDialogOpened = async () => {
  await loadOrderItems()
}

// 生成派工单 - 打开对话框
const handleCreateDispatchOrder = async () => {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有生成派工单权限，请联系管理员')
    return
  }
  createDialogVisible.value = true
  hydrateCreateDispatchItemsColumnConfig()
  loadCreateDispatchTableWrap()
  dispatchOrderForm.value = {
    assigned_user_id: null,
    notes: '',
    allow_multi_batch_same_roll: false,
    multi_batch_same_roll_notes: '',
    required_completion_at: null,
    required_completion_immediate: false,
    packaging_type: '',
    packaging_require_weigh: false,
    packaging_require_volume: false
  }
  selectedOrderItems.value = []
  createSelectedPoolById.value = {}
  createSortMode.value = 'warehouse_code_then_order_date'
  dispatchMergeNoteStrategy.value = 'off'
  Object.keys(mergeCandidateByOrderItemId).forEach((k) => delete mergeCandidateByOrderItemId[k])
  Object.keys(pendingMergeGroupKeyByOrderItemId).forEach((k) => delete pendingMergeGroupKeyByOrderItemId[k])
  resetCreateFormFilter()
  // 确保用户列表已加载
  if (!users.value || users.value.length === 0) {
    loadUsers()
  }
  // 加载筛选选项
  await loadFilterOptions()
  await loadPendingDispatchCount()
}

// 打开编辑对话框时初始化数据
const handleEdit = async (row) => {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有编辑派工单权限，请联系管理员')
    return
  }
  // 先加载完整的派工单详情，然后再打开对话框
  try {
    // 确保用户列表已加载
    if (!users.value || users.value.length === 0) {
      await loadUsers()
    }
    
    // 先加载完整的派工单详情
    const response = await getDispatchOrder(row.dispatch_order_id)
    if (response.success) {
      // 确保先设置 currentDispatchOrder
      currentDispatchOrder.value = response.data
      
      // 初始化表单数据
      dispatchOrderForm.value = {
        assigned_user_id: response.data.assigned_user_id || null,
        notes: response.data.notes || '',
        allow_multi_batch_same_roll: !!response.data.allow_multi_batch_same_roll,
        multi_batch_same_roll_notes: response.data.multi_batch_same_roll_notes || '',
        required_completion_at: normalizeRequiredCompletionAtForSave(
          response.data.required_completion_at
        ),
        required_completion_immediate: !!response.data.required_completion_immediate,
        packaging_type: response.data.packaging_type || '',
        packaging_require_weigh: !!response.data.packaging_require_weigh,
        packaging_require_volume: !!response.data.packaging_require_volume
      }
      
      selectedOrderItems.value = []

      // 设置标志，表示数据已准备好
      editDialogOpenedFlag.value = true
      
      // 打开对话框
      editDialogVisible.value = true
    } else {
      ElMessage.error('加载派工单详情失败')
    }
  } catch (error) {
    ElMessage.error('加载派工单详情失败: ' + (error.response?.data?.message || error.message))
  }
}

// 编辑对话框打开后的处理
const handleEditDialogOpened = async () => {
  // 只有在数据已准备好的情况下才加载订单明细
  if (editDialogOpenedFlag.value && currentDispatchOrder.value) {
    // 等待一个tick确保对话框已完全打开
    await nextTick()
    // 然后再加载订单明细
    await loadEditOrderItemsFromDispatchOnly()
    // 重置标志
    editDialogOpenedFlag.value = false
  }
}

// 编辑对话框关闭时的处理
const handleEditDialogClosed = () => {
  // 重置标志和状态
  editDialogOpenedFlag.value = false
  currentDispatchOrder.value = null
  orderItemsData.value = []
  selectedOrderItems.value = []
}

const syncCreateTableSelectionFromPool = async () => {
  if (!createDialogVisible.value) return
  await nextTick()
  const table = orderItemsTableRef.value
  if (!table) return
  restoringCreateSelection.value = true
  try {
    table.clearSelection()
    const set = new Set(Object.keys(createSelectedPoolById.value || {}).map((x) => Number(x)))
    orderItemsData.value.forEach((row) => {
      if (set.has(Number(row.order_item_id))) {
        table.toggleRowSelection(row, true)
      }
    })
    selectedOrderItems.value = sortedSelectedCreateItems.value
  } finally {
    await nextTick()
    restoringCreateSelection.value = false
  }
}

const clearCreateSelectedPool = async () => {
  createSelectedPoolById.value = {}
  selectedOrderItems.value = []
  if (orderItemsTableRef.value) {
    orderItemsTableRef.value.clearSelection()
  }
  await nextTick()
}

const removeCreateSelectedItem = async (orderItemId) => {
  const next = { ...(createSelectedPoolById.value || {}) }
  delete next[orderItemId]
  createSelectedPoolById.value = next
  selectedOrderItems.value = sortedSelectedCreateItems.value
  await syncCreateTableSelectionFromPool()
}

const onCreateDialogClosed = () => {
  createSelectedPoolById.value = {}
  selectedOrderItems.value = []
  dispatchMergeNoteStrategy.value = 'off'
  Object.keys(mergeCandidateByOrderItemId).forEach((k) => delete mergeCandidateByOrderItemId[k])
  Object.keys(pendingMergeGroupKeyByOrderItemId).forEach((k) => delete pendingMergeGroupKeyByOrderItemId[k])
}

function mergedQtyPreviewForCreateRow(row) {
  const key = pendingMergeGroupKeyByOrderItemId[row.order_item_id]
  const base = Number(row.quantity || 0)
  if (!key) return base
  let sum = 0
  for (const r of orderItemsData.value) {
    if (pendingMergeGroupKeyByOrderItemId[r.order_item_id] === key) {
      sum += Number(r.quantity || 0)
    }
  }
  return sum
}

async function applyCreatePendingMerge() {
  const ids = orderItemsData.value
    .filter((r) => mergeCandidateByOrderItemId[r.order_item_id])
    .map((r) => r.order_item_id)
  if (ids.length < 2) {
    ElMessage.warning('请至少勾选两行「参与合并」')
    return
  }
  const gkey = `pd_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  ids.forEach((id) => {
    pendingMergeGroupKeyByOrderItemId[id] = gkey
    mergeCandidateByOrderItemId[id] = false
  })
  ElMessage.success(
    '已设置合并数量分组（合并数量列为组内合计）；「参与合并」已清空，请重新勾选需合并的行'
  )
  await nextTick()
  await syncCreateTableSelectionFromPool()
}

async function splitCreatePendingMergeFromTableSelection() {
  const table = orderItemsTableRef.value
  const sel = table?.getSelectionRows?.() || []
  if (!sel.length) {
    ElMessage.warning('请在表格左侧勾选要拆分合并的行')
    return
  }
  const pool = { ...(createSelectedPoolById.value || {}) }
  sel.forEach((row) => {
    delete pendingMergeGroupKeyByOrderItemId[row.order_item_id]
    delete pool[row.order_item_id]
  })
  createSelectedPoolById.value = pool
  selectedOrderItems.value = sortedSelectedCreateItems.value
  ElMessage.success('已拆分合并分组；所选行已从左侧勾选与已选清单中移除')
  await nextTick()
  await syncCreateTableSelectionFromPool()
}

// 加载订单明细数据（用于生成派工单）
const loadOrderItems = async () => {
  orderItemsLoading.value = true
  try {
    const response = await getCompletedOrderItemMatchings()
    if (response.success) {
      const raw = response.data
      const allItems = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.items) ? raw.items : [])
      const queryCount = response.meta?.query_count
      // 清理已从待派工池移除的旧勾选（如被退回配料/已被他人派工）
      const alive = new Set(allItems.map((x) => Number(x.order_item_id)))
      const prunedPool = { ...(createSelectedPoolById.value || {}) }
      Object.keys(prunedPool).forEach((k) => {
        if (!alive.has(Number(k))) delete prunedPool[k]
      })
      createSelectedPoolById.value = prunedPool
      let items = allItems

      // 更新材料名称、颜色、内部英文颜色列表（从待派工全量数据中提取）
      const categorySet = new Set()
      const colorSet = new Set()
      const internalEnSet = new Set()
      allItems.forEach((item) => {
        if (item.material_matching?.material_category_name) {
          categorySet.add(item.material_matching.material_category_name)
        }
        if (item.material_matching?.material_color_name) {
          colorSet.add(item.material_matching.material_color_name)
        }
        const en = internalColorEn(item)
        if (en) internalEnSet.add(en)
      })
      materialCategoryList.value = Array.from(categorySet).sort()
      materialColorList.value = Array.from(colorSet).sort()
      materialInternalColorEnList.value = Array.from(internalEnSet).sort((a, b) =>
        a.localeCompare(b, 'en')
      )

      const otMap = new Map()
      allItems.forEach((item) => {
        if (item.order_type_id != null && item.order_type_name) {
          otMap.set(Number(item.order_type_id), item.order_type_name)
        }
      })
      orderTypeFilterOptions.value = Array.from(otMap.entries())
        .map(([order_type_id, type_name_cn]) => ({ order_type_id, type_name_cn }))
        .sort((a, b) => String(a.type_name_cn).localeCompare(String(b.type_name_cn), 'zh-CN'))
      
      // 应用搜索筛选
      if (createFormFilter.value.searchKeyword) {
        const keyword = createFormFilter.value.searchKeyword.toLowerCase()
        items = items.filter(item =>
          fieldIncludesKeyword(item.order_no, keyword) ||
          fieldIncludesKeyword(item.client_purchase_order, keyword) ||
          fieldIncludesKeyword(item.customer_name, keyword) ||
          fieldIncludesKeyword(item.client_material_name, keyword) ||
          fieldIncludesKeyword(item.material_matching?.material_identity_code, keyword) ||
          fieldIncludesKeyword(item.material_matching?.warehouse_manual_code, keyword)
        )
      }
      
      // 客户筛选
      if (createFormFilter.value.customerId != null && createFormFilter.value.customerId !== '') {
        items = items.filter(item => idEq(item.customer_id, createFormFilter.value.customerId))
      }
      
      // 品牌筛选
      if (createFormFilter.value.brandId != null && createFormFilter.value.brandId !== '') {
        items = items.filter(item => idEq(item.brand_id, createFormFilter.value.brandId))
      }

      if (createFormFilter.value.orderTypeId != null && createFormFilter.value.orderTypeId !== '') {
        items = items.filter((item) => idEq(item.order_type_id, createFormFilter.value.orderTypeId))
      }
      
      // 材料名称筛选
      if (createFormFilter.value.materialCategoryName) {
        items = items.filter(item =>
          item.material_matching?.material_category_name === createFormFilter.value.materialCategoryName
        )
      }
      
      // 颜色筛选
      if (createFormFilter.value.materialColorName) {
        items = items.filter(item =>
          item.material_matching?.material_color_name === createFormFilter.value.materialColorName
        )
      }

      // 内部英文颜色筛选（与表格「内部英文颜色」列一致）
      if (createFormFilter.value.internalColorEn) {
        const want = createFormFilter.value.internalColorEn
        items = items.filter((item) => internalColorEn(item) === want)
      }

      // 材料代码筛选
      if (createFormFilter.value.materialCode) {
        const keyword = createFormFilter.value.materialCode.toLowerCase()
        items = items.filter(item =>
          fieldIncludesKeyword(item.material_matching?.material_identity_code, keyword) ||
          fieldIncludesKeyword(item.material_matching?.warehouse_manual_code, keyword)
        )
      }
      
      items.forEach((r) => {
        if (r.packaging_type === undefined) r.packaging_type = ''
        if (r.packaging_require_weigh === undefined) r.packaging_require_weigh = false
        if (r.packaging_require_volume === undefined) r.packaging_require_volume = false
        if (r.material_usage_notes === undefined) r.material_usage_notes = ''
        const picked = createSelectedPoolById.value?.[r.order_item_id]
        if (!picked) {
          const withRule = applyCustomerPackagingRequirementToRow(
            r,
            customerPackagingRequirementsNested.value
          )
          r.packaging_type = withRule.packaging_type ?? ''
          r.packaging_require_weigh = !!withRule.packaging_require_weigh
          r.packaging_require_volume = !!withRule.packaging_require_volume
        }
        if (picked) {
          r.material_usage_notes = picked.material_usage_notes ?? r.material_usage_notes
          r.packaging_type = picked.packaging_type ?? r.packaging_type
          r.packaging_require_weigh = !!picked.packaging_require_weigh
          r.packaging_require_volume = !!picked.packaging_require_volume
        }
      })
      orderItemsData.value = items
      await syncCreateTableSelectionFromPool()
      if (createDialogVisible.value) {
        selectedOrderItems.value = sortedSelectedCreateItems.value
      }
      if (!allItems.length) {
        const pending = pendingDispatchCount.value
        if (typeof queryCount === 'number' && queryCount > 0) {
          ElMessage.warning(
            `后端查到 ${queryCount} 条待派工，但列表未能展示。请重启后端并硬刷新页面；仍无效请联系管理员查看日志。`
          )
        } else if (pending > 0) {
          ElMessage.warning(
            `待派工角标为 ${pending}，但列表为空。请确认已部署最新前后端并硬刷新（Ctrl+F5）。`
          )
        }
      }
    } else {
      ElMessage.error('加载订单明细失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    ElMessage.error('加载订单明细失败: ' + (error.response?.data?.message || error.message))
  } finally {
    orderItemsLoading.value = false
  }
}

// 订单明细选择变化
const handleOrderItemsSelectionChange = (selection) => {
  if (!createDialogVisible.value) {
    selectedOrderItems.value = selection
    return
  }
  if (restoringCreateSelection.value) {
    return
  }
  const pool = { ...(createSelectedPoolById.value || {}) }
  const currentIds = orderItemsData.value.map((r) => Number(r.order_item_id))
  // 当前页先移除，再按勾选重建，确保取消勾选生效
  currentIds.forEach((id) => {
    delete pool[id]
  })
  ;(selection || []).forEach((row) => {
    pool[row.order_item_id] = {
      ...row,
      material_usage_notes: row.material_usage_notes ?? '',
      packaging_type: row.packaging_type ?? '',
      packaging_require_weigh: !!row.packaging_require_weigh,
      packaging_require_volume: !!row.packaging_require_volume
    }
  })
  createSelectedPoolById.value = pool
  selectedOrderItems.value = sortedSelectedCreateItems.value
}

// 返回配料
const handleReturnToMatching = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要将订单明细（订单号：${row.order_no}）退回配料吗？退回后该订单明细将重新出现在配料单中，可以重新选择材料。`,
      '确认返回配料',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    const response = await returnOrderItemToMatching(row.order_item_id)
    
    if (response.success) {
      ElMessage.success('订单明细已退回配料，可在配料单中重新选择材料')
      // 重新加载订单明细列表
      loadOrderItems()
      loadPendingDispatchCount()
    } else {
      ElMessage.error('返回配料失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('返回配料失败: ' + (error.response?.data?.message || error.message))
    }
  }
}

// 确认生成派工单
const handleConfirmCreate = async () => {
  // 先把当前页已选行上的最新编辑值（备注/包装要求）回写到累计池
  const latestPool = { ...(createSelectedPoolById.value || {}) }
  orderItemsData.value.forEach((r) => {
    if (!latestPool[r.order_item_id]) return
    latestPool[r.order_item_id] = {
      ...latestPool[r.order_item_id],
      material_usage_notes: r.material_usage_notes ?? '',
      packaging_type: r.packaging_type ?? '',
      packaging_require_weigh: !!r.packaging_require_weigh,
      packaging_require_volume: !!r.packaging_require_volume
    }
  })
  createSelectedPoolById.value = latestPool
  const chosen = sortedSelectedCreateItems.value
  if (chosen.length === 0) {
    ElMessage.warning('请至少选择一条订单明细')
    return
  }

  if (createDispatchFormRef.value) {
    try {
      await createDispatchFormRef.value.validate()
    } catch {
      return
    }
  }
  
  try {
    const orderItemIds = chosen.map((row) => row.order_item_id)
    const item_packaging = chosen.map((row) => ({
      order_item_id: row.order_item_id,
      material_usage_notes: String(row.material_usage_notes || '').trim() || null,
      packaging_type: row.packaging_type || null,
      packaging_require_weigh: !!row.packaging_require_weigh,
      packaging_require_volume: !!row.packaging_require_volume
    }))

    const p = dispatchOrderForm.value
    const mergeExtra = buildMergeDispatchAppendNote(chosen, dispatchMergeNoteStrategy.value)
    let notesCombined = String(p.notes || '').trim()
    if (mergeExtra) {
      notesCombined = notesCombined ? `${notesCombined}\n\n${mergeExtra}` : mergeExtra
    }
    const mergeKeyGroups = new Map()
    for (const row of chosen) {
      const k = pendingMergeGroupKeyByOrderItemId[row.order_item_id]
      if (!k) continue
      if (!mergeKeyGroups.has(k)) mergeKeyGroups.set(k, [])
      mergeKeyGroups.get(k).push(row.order_item_id)
    }
    const merge_quantity_groups = [...mergeKeyGroups.values()].filter((g) => g.length >= 2)

    const response = await createDispatchOrder({
      order_item_ids: orderItemIds,
      assigned_user_id: p.assigned_user_id,
      notes: notesCombined,
      allow_multi_batch_same_roll: !!p.allow_multi_batch_same_roll,
      multi_batch_same_roll_notes: p.allow_multi_batch_same_roll
        ? String(p.multi_batch_same_roll_notes || '').trim() || null
        : null,
      required_completion_at: normalizeRequiredCompletionAtForSave(p.required_completion_at),
      required_completion_immediate: !!p.required_completion_immediate,
      item_packaging,
      packaging_type: p.packaging_type || null,
      packaging_require_weigh: p.packaging_require_weigh,
      packaging_require_volume: p.packaging_require_volume,
      merge_quantity_groups
    })
    
    if (response.success) {
      ElMessage.success('派工单生成成功')
      createDialogVisible.value = false
      createSelectedPoolById.value = {}
      selectedOrderItems.value = []
      await loadData() // 重新加载派工单列表（内部会刷新待派工条数）
    } else {
      ElMessage.error('生成派工单失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    const cap = error.response?.data?.capacity
    const base = error.response?.data?.message || error.message
    if (cap) {
      ElMessageBox.alert(
        `统计日：${cap.due_date}\n已用 ${Number(cap.used_yards).toFixed(1)} 码 / ${cap.used_items} 条\n本单 ${Number(cap.proposed_yards).toFixed(1)} 码 / ${cap.proposed_items} 条\n合计 ${(Number(cap.used_yards) + Number(cap.proposed_yards)).toFixed(1)} 码 / ${cap.used_items + cap.proposed_items} 条\n上限 ${cap.cap_yards} 码 / ${cap.cap_items} 条`,
        base || '现场派工产能超限',
        { type: 'warning', confirmButtonText: '知道了' }
      )
    } else {
      ElMessage.error('生成派工单失败: ' + (base || '未知错误'))
    }
  }
}

// 加载用户列表
const loadUsers = async () => {
  try {
    // 后端API期望is_active是字符串'true'或'false'
    const response = await getAssignableUsers({ per_page: 1000 })
    if (response.success) {
      users.value = response.data || []
      if (users.value.length === 0) {
        console.warn('用户列表为空')
      }
    } else {
      console.error('加载用户列表失败:', response.message)
      ElMessage.warning('加载用户列表失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    console.error('加载用户列表失败:', error)
    ElMessage.error('加载用户列表失败: ' + (error.response?.data?.message || error.message))
  }
}

// 编辑：仅展示本派工单已包含的明细（来自详情接口）
const loadEditOrderItemsFromDispatchOnly = async () => {
  orderItemsLoading.value = true
  try {
    const o = currentDispatchOrder.value
    if (!o?.items?.length) {
      orderItemsData.value = []
      selectedOrderItems.value = []
      return
    }
    const rows = []
    for (const it of o.items) {
      const oi = it.order_item || {}
      const mm = oi.material_matching
      const invLocs = it.inventory_locations || mm?.inventory_locations || []
      const totAvail =
        mm?.total_available_quantity != null
          ? mm.total_available_quantity
          : Array.isArray(invLocs)
            ? invLocs.reduce((s, loc) => s + (Number(loc.available_quantity) || 0), 0)
            : undefined
      rows.push({
        order_item_id: it.order_item_id,
        dispatch_order_item_id: it.dispatch_order_item_id,
        order_no: it.order_no,
        order_date: it.order_date,
        client_purchase_order: it.client_purchase_order,
        customer_name: it.customer_name,
        brand_name: it.brand_name,
        client_material_name: oi.client_material_name,
        client_color_cn: oi.client_color_cn,
        client_color_en: oi.client_color_en,
        internal_product_name: oi.internal_product_name,
        internal_color_cn: oi.internal_color_cn,
        internal_color_en: oi.internal_color_en,
        item_notes: oi.item_notes ?? '',
        item_notes_2: oi.item_notes_2 ?? '',
        item_notes_3: oi.item_notes_3 ?? '',
        specification: oi.specification,
        client_style_no: oi.client_style_no,
        quantity: oi.quantity,
        order_line_quantity: it.order_line_quantity ?? oi.quantity,
        merged_quantity: it.merged_quantity ?? it.order_line_quantity ?? oi.quantity,
        unit_name: oi.unit_name,
        material_matching: mm
          ? {
              ...mm,
              warehouse_manual_code: it.warehouse_manual_code || mm.warehouse_manual_code,
              inventory_locations: invLocs,
              total_available_quantity: totAvail
            }
          : {
              warehouse_manual_code: it.warehouse_manual_code,
              material_identity_code: it.material_code,
              inventory_locations: invLocs,
              total_available_quantity: totAvail
            },
        warehouse_manual_code: it.warehouse_manual_code,
        material_code: it.material_code,
        material_usage_notes: it.material_usage_notes ?? '',
        packaging_type: it.packaging_type ?? o.packaging_type ?? '',
        packaging_require_weigh: !!(it.packaging_require_weigh ?? o.packaging_require_weigh),
        packaging_require_volume: !!(it.packaging_require_volume ?? o.packaging_require_volume)
      })
    }
    orderItemsData.value = rows
    await nextTick()
    await nextTick()
    if (orderItemsTableRef.value) {
      orderItemsTableRef.value.clearSelection()
      rows.forEach((r) => orderItemsTableRef.value.toggleRowSelection(r, true))
    }
    selectedOrderItems.value = [...rows]
  } finally {
    orderItemsLoading.value = false
  }
}

// 确认编辑
const handleConfirmEdit = async () => {
  if (!editForm.value) return
  
  try {
    await editForm.value.validate()
    
    const orderItemIds = selectedOrderItems.value.map((row) => row.order_item_id)
    const item_packaging = selectedOrderItems.value.map((row) => ({
      order_item_id: row.order_item_id,
      material_usage_notes: String(row.material_usage_notes || '').trim() || null,
      packaging_type: row.packaging_type || null,
      packaging_require_weigh: !!row.packaging_require_weigh,
      packaging_require_volume: !!row.packaging_require_volume
    }))

    const p = dispatchOrderForm.value
    const response = await updateDispatchOrder(currentDispatchOrder.value.dispatch_order_id, {
      assigned_user_id: p.assigned_user_id,
      notes: p.notes,
      allow_multi_batch_same_roll: !!p.allow_multi_batch_same_roll,
      multi_batch_same_roll_notes: p.allow_multi_batch_same_roll
        ? String(p.multi_batch_same_roll_notes || '').trim() || null
        : null,
      required_completion_at: normalizeRequiredCompletionAtForSave(p.required_completion_at),
      required_completion_immediate: !!p.required_completion_immediate,
      order_item_ids: orderItemIds,
      item_packaging,
      packaging_type: p.packaging_type || null,
      packaging_require_weigh: p.packaging_require_weigh,
      packaging_require_volume: p.packaging_require_volume
    })
    
    if (response.success) {
      ElMessage.success('派工单更新成功')
      editDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error('更新派工单失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    if (error === false) return
    const cap = error.response?.data?.capacity
    const base = error.response?.data?.message || error.message
    if (cap) {
      ElMessageBox.alert(
        `统计日：${cap.due_date}\n已用 ${Number(cap.used_yards).toFixed(1)} 码 / ${cap.used_items} 条\n本单 ${Number(cap.proposed_yards).toFixed(1)} 码 / ${cap.proposed_items} 条\n合计 ${(Number(cap.used_yards) + Number(cap.proposed_yards)).toFixed(1)} 码 / ${cap.used_items + cap.proposed_items} 条\n上限 ${cap.cap_yards} 码 / ${cap.cap_items} 条`,
        base || '现场派工产能超限',
        { type: 'warning', confirmButtonText: '知道了' }
      )
    } else {
      ElMessage.error('更新派工单失败: ' + (base || '未知错误'))
    }
  }
}

const applyRouteQuickPreset = async () => {
  const quickAction = String(route.query.quick_action || '').trim()
  if (quickAction !== 'process') return

  const materialCode = String(route.query.materialCode || '').trim()
  if (!materialCode) return

  await handleCreateDispatchOrder()
  createFormFilter.value.materialCode = materialCode
  await loadOrderItems()
  ElMessage.info('已按料号打开派工创建，可直接勾选生成派工单')
}

// 删除派工单
const handleDelete = async (row) => {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有删除派工单权限，请联系管理员')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确定要删除派工单 "${row.dispatch_order_no}" 吗？此操作不可恢复。`,
      '确认删除',
      {
        type: 'warning',
        confirmButtonText: '确定删除',
        cancelButtonText: '取消'
      }
    )
    
    const response = await deleteDispatchOrder(row.dispatch_order_id)
    if (response.success) {
      ElMessage.success('派工单删除成功')
      loadData()
    } else {
      ElMessage.error('删除派工单失败: ' + (response.message || '未知错误'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除派工单失败: ' + (error.response?.data?.message || error.message))
    }
  }
}

const onDetailDialogClosed = () => {
  detailPanelMode.value = 'view'
  preAuditNotes.value = ''
  postCompletionNotes.value = ''
  if (hasTaskReturnQuery(route.query)) {
    nextTick(() => {
      navigateTaskReturnIfPresent(router, route, { replace: true })
    })
  }
}

const closeDetailDialog = () => {
  detailDialogVisible.value = false
}

// 审核派工单：打开与详情相同的大窗，核对全部明细后再通过/拒绝
const handleAudit = async (row) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有派工审核权限，请联系管理员')
    return
  }
  detailPanelMode.value = 'audit_pre'
  preAuditNotes.value = ''
  postCompletionNotes.value = ''
  detailDialogVisible.value = true
  detailLoading.value = true
  try {
    const response = await getDispatchOrder(row.dispatch_order_id)
    if (response.success && response.data) {
      currentDispatchOrder.value = response.data
    } else {
      ElMessage.error(response.message || '加载派工单失败')
      detailDialogVisible.value = false
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载派工单失败')
    detailDialogVisible.value = false
  } finally {
    detailLoading.value = false
  }
}

const submitPreAudit = async (action) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有派工审核权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  const actionText = action === 'approve' ? '审核通过' : '审核拒绝'
  preAuditLoading.value = true
  try {
    await ElMessageBox.confirm(`确定要${actionText}派工单 "${o.dispatch_order_no}" 吗？`, `确认${actionText}`, {
      type: 'warning',
      confirmButtonText: `确定${actionText}`,
      cancelButtonText: '取消'
    })
    const response = await auditDispatchOrder(o.dispatch_order_id, {
      action,
      audit_notes: preAuditNotes.value || ''
    })
    if (response.success) {
      ElMessage.success(`派工单${actionText}成功`)
      detailDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error(response.message || `${actionText}失败`)
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || error.message || '审核失败')
    }
  } finally {
    preAuditLoading.value = false
  }
}

const submitProcessingOutboundComplete = async (storageLocationId = null) => {
  if (!canOperateDispatch.value) {
    ElMessage.warning('您没有派工操作权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o?.dispatch_order_id) return
  if (!isProcessingOutboundDispatch(o)) return
  if (o.warehouse_completed_at || o.status === 'completed') {
    ElMessage.info('本单已完工')
    return
  }
  const rem = Number(o.dispatch_quantity_summary?.dispatch_remaining_quantity ?? 0)
  if (rem > 0.1) {
    ElMessage.warning(`全单剩余应打为 ${rem}，须补齐分码后再提交加工出库`)
    return
  }
  if (storageLocationId == null) {
    try {
      await ElMessageBox.confirm(
        '提交后将按分码数量扣减加工投入料库存并结案本派工单（无需打码业务核销）。',
        '确认加工出库',
        { type: 'warning', confirmButtonText: '确认提交', cancelButtonText: '取消' }
      )
    } catch {
      return
    }
  }
  processingOutboundCompleteLoading.value = true
  try {
    const payload = {}
    if (storageLocationId != null) {
      payload.storage_location_id = Number(storageLocationId)
    }
    const res = await warehouseCompleteDispatchOrder(o.dispatch_order_id, payload)
    if (res.success) {
      ElMessage.success(res.message || '已加工出库并结案')
      await handleRefreshDispatchDetail()
      loadData()
      return
    }
    if (res.need_location_selection) {
      outboundLocPayload.value = res
      outboundLocDialogVisible.value = true
      return
    }
    ElMessage.error(res.message || '无法完成')
  } catch (e) {
    const data = e.response?.data
    if (data?.need_location_selection) {
      outboundLocPayload.value = data
      outboundLocDialogVisible.value = true
      return
    }
    if (e !== 'cancel') {
      ElMessage.error(data?.message || e.message || '无法完成')
    }
  } finally {
    processingOutboundCompleteLoading.value = false
  }
}

const onOutboundLocationPicked = async (locationId) => {
  await submitProcessingOutboundComplete(locationId)
}

const submitPostCompletionAudit = async (action) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有现场核销审核权限，请联系管理员')
    return
  }
  if (action === 'approve' && detailPendingKickbackLinesCount.value > 0) {
    ElMessage.warning('存在待审结的踢回配料申请，请先处理后再核销通过')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  const rem = Number(o.dispatch_quantity_summary?.dispatch_remaining_quantity ?? 0)
  if (action === 'approve' && rem > 0.1) {
    ElMessage.warning(`全单剩余应打为 ${rem}，不可核销通过，请退回仓库补齐分码`)
    return
  }
  postCompletionLoading.value = true
  try {
    const tip =
      action === 'approve' ? '确认现场数据无误并核销通过？' : '确定退回修改？派工单将恢复为进行中。'
    await ElMessageBox.confirm(tip, action === 'approve' ? '核销通过' : '核销退回', { type: 'warning' })
    const res = await postCompletionAuditDispatchOrder(o.dispatch_order_id, {
      action,
      audit_notes: postCompletionNotes.value || ''
    })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      detailDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    postCompletionLoading.value = false
  }
}

const submitPackagingAudit = async (action) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有包装核销审核权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  packagingAuditLoading.value = true
  try {
    const tip =
      action === 'approve'
        ? '确认包装资料无误并核销通过？通过后包装实测将锁定。'
        : '确定退回修改？现场可修改包装资料后再次提交。'
    await ElMessageBox.confirm(tip, action === 'approve' ? '包装核销通过' : '包装核销退回', {
      type: 'warning'
    })
    const res = await packagingAuditDispatchOrder(o.dispatch_order_id, {
      action,
      audit_notes: packagingAuditNotes.value || ''
    })
    if (res.success) {
      ElMessage.success(res.message || '已保存')
      detailDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    packagingAuditLoading.value = false
  }
}

const submitRevokePackagingAudit = async () => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有包装核销审核权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  packagingAuditRevokeLoading.value = true
  try {
    let reason = ''
    try {
      const { value } = await ElMessageBox.prompt('可选：填写取消原因', '取消包装核销', {
        confirmButtonText: '确认取消',
        cancelButtonText: '返回',
        inputPlaceholder: '选填'
      })
      reason = (value || '').trim()
    } catch (e) {
      if (e === 'cancel') return
      throw e
    }
    const res = await revokePackagingAuditDispatchOrder(o.dispatch_order_id, { reason })
    if (res.success) {
      ElMessage.success(res.message || '已取消包装核销')
      detailDialogVisible.value = false
      loadData()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    packagingAuditRevokeLoading.value = false
  }
}

const submitRevokePostCompletionAudit = async () => {
  if (!canRevokeVerification.value) {
    ElMessage.warning('您没有「派工单-取消核销」权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  postCompletionRevokeLoading.value = true
  try {
    await runRevokePostCompletionAuditForOrder(o, { closeDetailOnSuccess: true })
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    postCompletionRevokeLoading.value = false
  }
}

/** 取消已通过现场核销：恢复进行中（列表与详情共用） */
const runRevokePostCompletionAuditForOrder = async (o, { closeDetailOnSuccess } = {}) => {
  if (!o?.dispatch_order_id) return
  let reason = ''
  try {
    const { value } = await ElMessageBox.prompt(
      '取消后派工单将恢复为「进行中」，仓库可再次修改分码并提交完成。可选填原因（将写入备注）。',
      '取消核销',
      {
        confirmButtonText: '下一步',
        cancelButtonText: '关闭',
        inputType: 'textarea',
        inputPlaceholder: '原因（可选）'
      }
    )
    reason = String(value || '').trim()
  } catch (e) {
    if (e === 'cancel') return
    throw e
  }
  try {
    await ElMessageBox.confirm(
      `确定取消派工单「${o.dispatch_order_no}」的现场核销（已通过）吗？`,
      '确认取消核销',
      { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消' }
    )
  } catch (e) {
    if (e === 'cancel') return
    throw e
  }
  const res = await revokePostCompletionAuditDispatchOrder(o.dispatch_order_id, { reason })
  if (res.success) {
    ElMessage.success(res.message || '已取消核销')
    if (closeDetailOnSuccess) {
      detailDialogVisible.value = false
    }
    loadData()
  } else {
    ElMessage.error(res.message || '操作失败')
  }
}

const handleRevokePostCompletionFromList = async (row) => {
  if (!canRevokeVerification.value) {
    ElMessage.warning('您没有「派工单-取消核销」权限，请联系管理员')
    return
  }
  if (row.status !== 'completed' || (row.post_completion_audit_status || 'na') !== 'approved') return
  postCompletionRevokeRowId.value = row.dispatch_order_id
  try {
    await runRevokePostCompletionAuditForOrder(row, { closeDetailOnSuccess: false })
    if (detailDialogVisible.value && currentDispatchOrder.value?.dispatch_order_id === row.dispatch_order_id) {
      detailDialogVisible.value = false
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '操作失败')
    }
  } finally {
    postCompletionRevokeRowId.value = null
  }
}

const voidDispatchItemId = ref(null)
const handleVoidDispatchItem = async (row) => {
  if (!canVoidDispatchItem.value) {
    ElMessage.warning('您没有「派工单-作废明细行」权限')
    return
  }
  const doId = currentDispatchOrder.value?.dispatch_order_id
  const itemId = row?.dispatch_order_item_id
  if (!doId || !itemId) return
  let reason = ''
  try {
    const { value } = await ElMessageBox.prompt(
      '将删除本条派工明细（不取消整单核销）。若虚库仍有该行对应数量会回冲；已手动红冲则跳过。有未取消送货单的行不可作废。',
      '作废派工明细行',
      {
        confirmButtonText: '下一步',
        cancelButtonText: '关闭',
        inputType: 'textarea',
        inputPlaceholder: '原因（可选）'
      }
    )
    reason = (value || '').trim()
  } catch {
    return
  }
  try {
    await ElMessageBox.confirm('确认作废该派工明细行？此操作不可恢复。', '确认作废', {
      type: 'warning',
      confirmButtonText: '确认作废',
      cancelButtonText: '取消'
    })
  } catch {
    return
  }
  voidDispatchItemId.value = itemId
  try {
    const res = await voidDispatchOrderItem(doId, itemId, { reason })
    if (res.success) {
      ElMessage.success(res.message || '已作废')
      await handleRefreshDispatchDetail()
      loadData()
    } else {
      ElMessage.error(res.message || '作废失败')
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '作废失败')
  } finally {
    voidDispatchItemId.value = null
  }
}

const handleRequestReopenFromList = async (row) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有退回仓库权限，请联系管理员')
    return
  }
  if (row.status !== 'completed' || (row.post_completion_audit_status || 'na') === 'approved') return
  reopenDispatchRowId.value = row.dispatch_order_id
  try {
    await runRequestReopenDispatchForOrder(row, { closeDetailOnSuccess: false })
    if (detailDialogVisible.value && currentDispatchOrder.value?.dispatch_order_id === row.dispatch_order_id) {
      detailDialogVisible.value = false
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '失败')
    }
  } finally {
    reopenDispatchRowId.value = null
  }
}

/** 已完成但未核销通过：退回仓库修改（与 request-reopen 接口一致） */
const runRequestReopenDispatchForOrder = async (o, { closeDetailOnSuccess } = {}) => {
  if (!o?.dispatch_order_id) return
  const { value } = await ElMessageBox.prompt(
    '请填写退回原因（仓库将按说明重新分码/填报；派工单将恢复为进行中）。',
    '退回仓库修改',
    {
      confirmButtonText: '确定退回',
      cancelButtonText: '取消',
      inputType: 'textarea',
      inputPlaceholder: '必填',
      inputValidator: (v) => {
        if (!v || !String(v).trim()) return '请填写原因'
        return true
      }
    }
  )
  const res = await requestReopenDispatchOrder(o.dispatch_order_id, { reason: String(value).trim() })
  if (res.success) {
    ElMessage.success(res.message || '已退回')
    if (closeDetailOnSuccess) {
      detailDialogVisible.value = false
    }
    loadData()
  } else {
    ElMessage.error(res.message || '失败')
  }
}

const openReopenDialog = async () => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有退回仓库权限，请联系管理员')
    return
  }
  const o = currentDispatchOrder.value
  if (!o) return
  try {
    await runRequestReopenDispatchForOrder(o, { closeDetailOnSuccess: true })
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.error(e.response?.data?.message || e.message || '失败')
    }
  }
}

// 取消审核（恢复待审核，便于再编辑）
const handleRevokeAudit = async (row) => {
  if (!canAuditReview.value) {
    ElMessage.warning('您没有取消审核权限，请联系管理员')
    return
  }
  try {
    let revoke_notes = ''
    try {
      const { value } = await ElMessageBox.prompt(
        '取消审核后该派工单可再次编辑、删除。可选填原因（将追加到备注）。',
        '取消审核',
        {
          confirmButtonText: '确定',
          cancelButtonText: '关闭',
          inputType: 'textarea',
          inputPlaceholder: '原因（可选）'
        }
      )
      revoke_notes = value || ''
    } catch {
      return
    }
    const response = await revokeDispatchOrderAudit(row.dispatch_order_id, {
      revoke_notes: revoke_notes.trim() || undefined
    })
    if (response.success) {
      ElMessage.success(response.message || '已取消审核')
      loadData()
    } else {
      ElMessage.error(response.message || '操作失败')
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '操作失败')
  }
}

// 打印预览相关
const printPreviewVisible = ref(false)
const currentPrintRecordId = ref(null)
const currentPrintScenario = ref('__all__')
const printDialogTitle = computed(() =>
  currentPrintScenario.value === 'dispatch_branch_label' ? '分码标签打印' : '派工单打印'
)

// 打印派工单
const handlePrint = async (row) => {
  currentPrintRecordId.value = row.dispatch_order_id
  currentPrintScenario.value = '__all__'
  await nextTick()
  printPreviewVisible.value = true
}

const handlePrintBranchLabels = async (row) => {
  currentPrintRecordId.value = row.dispatch_order_id
  currentPrintScenario.value = 'dispatch_branch_label'
  await nextTick()
  printPreviewVisible.value = true
}

// 打印完成回调
const handlePrintSuccess = async () => {
  try {
    // 记录打印次数
    await printDispatchOrder(currentPrintRecordId.value)
    loadData() // 刷新数据以更新打印次数
  } catch (error) {
    ElMessage.error('记录打印失败: ' + (error.response?.data?.message || error.message))
  }
}


// 现场产能预览：放在脚本后部，确保所依赖的 ref/computed 均已初始化（避免 TDZ）
const refreshCapacityPreview = async () => {
  if (!canOperateDispatch.value) return
  if (!createDialogVisible.value && !editDialogVisible.value) {
    siteCapacityPreview.value = null
    return
  }
  const p = dispatchOrderForm.value
  const orderItemIds = createDialogVisible.value
    ? sortedSelectedCreateItems.value.map((r) => r.order_item_id)
    : selectedOrderItems.value.map((r) => r.order_item_id)
  if (!p?.assigned_user_id || orderItemIds.length === 0) {
    siteCapacityPreview.value = null
    return
  }
  if (!p.required_completion_immediate && !p.required_completion_at) {
    siteCapacityPreview.value = {
      applies: false,
      reason:
        '请设置「要求完成」日期时间，或勾选「马上完成（特急单）」，系统才能按日历日汇总产能占用。'
    }
    return
  }
  siteCapacityPreviewLoading.value = true
  try {
    const excludeId =
      editDialogVisible.value && currentDispatchOrder.value?.dispatch_order_id
        ? currentDispatchOrder.value.dispatch_order_id
        : undefined
    const res = await getDispatchSiteCapacityPreview({
      assigned_user_id: p.assigned_user_id,
      required_completion_immediate: p.required_completion_immediate ? 'true' : 'false',
      required_completion_at: normalizeRequiredCompletionAtForSave(p.required_completion_at) || '',
      exclude_dispatch_order_id: excludeId ?? '',
      order_item_ids: orderItemIds.join(',')
    })
    if (res?.success) siteCapacityPreview.value = res.data
    else siteCapacityPreview.value = null
  } catch {
    siteCapacityPreview.value = null
  } finally {
    siteCapacityPreviewLoading.value = false
  }
}

const debouncedRefreshCapacityPreview = useDebounceFn(refreshCapacityPreview, 350)

watch(
  [
    createDialogVisible,
    editDialogVisible,
    () => dispatchOrderForm.value.assigned_user_id,
    () => dispatchOrderForm.value.required_completion_at,
    () => dispatchOrderForm.value.required_completion_immediate,
    sortedSelectedCreateItems,
    selectedOrderItems,
    () => currentDispatchOrder.value?.dispatch_order_id
  ],
  () => {
    debouncedRefreshCapacityPreview()
  },
  { deep: true }
)

// 生命周期
onMounted(async () => {
  userStore.initFromStorage()
  loadDispatchDetailItemColWidths()
  hydrateCreateDispatchItemsColumnConfig()
  loadCreateDispatchTableWrap()
  loadColumnConfig()
  applyOpenDispatchSearchPreset()

  const openId = String(route.query.open_dispatch_order_id || '').trim()
  const pidNum = openId ? Number(openId) : NaN
  const fromTaskDeepLink = Number.isFinite(pidNum) && pidNum > 0

  if (fromTaskDeepLink) {
    await tryOpenDispatchFromRoute()
  }

  loadData()
  loadUsers()
  loadSiteCapacitySettings()
  loadPendingDispatchCount()
  refreshKickbackCount()
  refreshPackagingAccessCount()
  loadCustomerPackagingRequirementsMap()
  await applyRouteQuickPreset()
  if (!fromTaskDeepLink) {
    await tryOpenDispatchFromRoute()
  }
})

watch(
  () => [route.query.open_dispatch_order_id, route.query.open_dispatch_order_no],
  async () => {
    openedDispatchFromRoute.value = ''
    applyOpenDispatchSearchPreset()
    const openId = String(route.query.open_dispatch_order_id || '').trim()
    const pidNum = openId ? Number(openId) : NaN
    if (Number.isFinite(pidNum) && pidNum > 0) {
      await tryOpenDispatchFromRoute()
      return
    }
    await loadData()
    tryOpenDispatchFromRoute()
  }
)
</script>

<style scoped>
.dispatch-order-management {
  padding: 20px;
}

.dispatch-nonmodal-hint {
  margin-bottom: 12px;
}

.dispatch-capacity-panel {
  margin: 12px 0 4px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.55;
}
.dispatch-capacity-panel__rule {
  margin: 0 0 8px;
  color: #606266;
}
.dispatch-capacity-panel__link {
  margin-left: 6px;
}
.dispatch-capacity-panel__preview {
  min-height: 32px;
}
.dispatch-capacity-panel__muted {
  margin: 0;
  color: #909399;
  font-size: 12px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.card-header h2 {
  margin: 0;
}

.create-dispatch-badge :deep(.el-badge__content) {
  font-size: 12px;
  font-weight: 700;
}

.kickback-desc {
  font-size: 13px;
  color: #606266;
  margin: 0 0 14px;
  line-height: 1.5;
}

.kickback-merge-tag {
  margin-top: 4px;
}

.kickback-item-detail {
  padding: 8px 12px 12px 48px;
  background: var(--el-fill-color-lighter);
}

.kickback-item-detail-empty {
  padding: 8px 12px 12px 48px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.pending-dispatch-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 14px 18px;
  background: linear-gradient(135deg, var(--el-color-warning-light-9) 0%, var(--el-fill-color-blank) 100%);
  border: 1px solid var(--el-color-warning-light-5);
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.pending-dispatch-banner__count {
  flex-shrink: 0;
  min-width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: var(--el-color-warning-dark-2);
  background: var(--el-color-warning-light-8);
  border-radius: 10px;
  border: 2px solid var(--el-color-warning);
}

.pending-dispatch-banner__body {
  flex: 1;
  min-width: 200px;
}

.pending-dispatch-banner__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  margin-bottom: 4px;
}

.pending-dispatch-banner__desc {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-regular);
  line-height: 1.5;
}

.create-dialog-tip {
  margin: 0 0 16px;
  padding: 10px 12px;
  background: var(--el-fill-color-light);
  border-radius: 4px;
  font-size: 13px;
  color: var(--el-text-color-regular);
  line-height: 1.5;
}

.create-packaging-bulk-toolbar {
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  background: var(--el-fill-color-blank);
}

.create-packaging-bulk-toolbar__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}

.create-packaging-bulk-toolbar__row:last-of-type {
  margin-bottom: 0;
}

.create-packaging-bulk-toolbar__buttons {
  padding-top: 4px;
  border-top: 1px dashed var(--el-border-color-lighter);
  margin-top: 8px;
  padding-top: 10px;
}

.create-packaging-bulk-toolbar__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-right: 4px;
}

.create-packaging-bulk-toolbar__hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-left: 6px;
}

.create-packaging-bulk-toolbar__hint:first-of-type {
  margin-left: 0;
}

.create-packaging-bulk-toolbar__tip {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.45;
}

.detail-items-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.detail-items-card-header__title {
  font-weight: bold;
}

.detail-items-card-header__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.dispatch-detail-items-table :deep(.el-table .el-table__body .cell) {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
}

.dispatch-detail-items-table :deep(.el-table .el-table__body td.el-table__cell) {
  vertical-align: top;
}

.dispatch-detail-items-table :deep(.el-table .el-table__header .cell) {
  white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.create-selected-panel {
  margin-top: 12px;
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--el-fill-color-lighter);
}

.create-selected-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.create-selected-panel__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.create-selected-panel__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.create-selected-panel__empty {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.create-selected-panel__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow: auto;
}

.create-selected-panel__item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  border-bottom: 1px dashed var(--el-border-color-lighter);
  padding-bottom: 6px;
}

.create-selected-panel__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.create-merge-panel {
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  background: var(--el-bg-color);
}

.create-merge-panel__title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

.create-merge-panel__hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.55;
}

.create-merge-panel__strategy {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.create-merge-panel__strategy-label {
  font-size: 12px;
  color: var(--el-text-color-regular);
}

.dispatch-slitting-progress {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}

.dispatch-slitting-progress__text {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.dispatch-slitting-progress__bar {
  flex: 1 1 200px;
  max-width: 320px;
  min-width: 160px;
}

.dispatch-slitting-progress__hint {
  flex-basis: 100%;
  font-size: 12px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.filter-section {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.filter-section__search {
  width: min(420px, 100%);
  flex: 1 1 280px;
}

.filter-section__wh-code {
  width: 140px;
  flex: 0 0 140px;
}

.filter-section__ctl {
  width: 150px;
}

.filter-section__ctl--wide {
  width: 170px;
}

.table-container {
  margin-top: 20px;
}

/* 派工单列表：横向滚动条加粗、橙色轨道与滑块（Element Plus 表格内置 scrollbar） */
.dispatch-order-list-scroll :deep(.el-scrollbar__bar.is-horizontal) {
  height: 14px !important;
  left: 4px !important;
  right: 10px !important;
  bottom: 4px !important;
  border-radius: 8px;
  background-color: rgba(255, 149, 51, 0.28);
  z-index: 3;
}

.dispatch-order-list-scroll :deep(.el-scrollbar__bar.is-horizontal .el-scrollbar__thumb) {
  background-color: #ff8800 !important;
  opacity: 1 !important;
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(234, 118, 0, 0.35);
}

.dispatch-order-list-scroll :deep(.el-scrollbar__bar.is-horizontal .el-scrollbar__thumb:hover) {
  background-color: #ea7600 !important;
}

/* 派工单列表：列内容随列宽自动换行，不再单行折叠省略 */
.dispatch-order-list-table :deep(.el-table__body .cell) {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  overflow: visible;
  text-overflow: unset;
}

.dispatch-order-list-table :deep(.el-table__body td.el-table__cell) {
  vertical-align: top;
}

.dispatch-order-list-table :deep(.el-table__header .cell) {
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

.dispatch-list-ops {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 0;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.packaging-inline {
  width: 100%;
}

.packaging-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 10px;
}

.detail-pack-operator-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.detail-pack-operator-row__label {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.branch-expand__measurer {
  color: var(--el-color-primary);
}

.dispatch-main-tabs {
  margin-top: 4px;
}

.dispatch-main-tabs :deep(.el-tabs__content) {
  padding-top: 8px;
}

.packaging-hint {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.branch-expand {
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 6px;
}

.branch-expand__title {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
}

.branch-expand__empty {
  color: #94a3b8;
  font-size: 12px;
}

.branch-expand__rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.branch-expand__row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  line-height: 1.5;
  padding: 4px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.branch-expand__row:last-child {
  border-bottom: none;
}

.branch-expand__idx {
  color: #475569;
  min-width: 36px;
}

.branch-expand__tag {
  font-weight: 600;
  color: #0f172a;
}

.branch-expand__pack {
  color: #1d4ed8;
}

.branch-expand__qty {
  color: #0f766e;
}

.branch-expand__meta {
  color: #334155;
}

.muted-inline {
  margin-left: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  font-weight: normal;
}

.create-table-appearance-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}

.create-table-appearance-toolbar__label {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.create-table-appearance-toolbar__hint {
  flex: 1 1 220px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.45;
}

.create-dispatch-empty {
  padding: 24px 16px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.create-dispatch-items-table--wrap :deep(.el-table .cell) {
  white-space: normal;
  word-break: break-word;
  line-height: 1.45;
}

.create-dispatch-items-table--wrap :deep(.el-table td.el-table__cell) {
  vertical-align: top;
}

.dispatch-detail-items-table :deep(td.dispatch-merged-qty-cell),
.create-dispatch-items-table :deep(td.dispatch-merged-qty-cell) {
  vertical-align: middle;
}

.due-summary-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.due-summary-cell__date {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.due-summary-cell.m-due--critical .due-summary-cell__date,
.due-summary-cell__date.m-due--critical {
  color: #7f1d1d;
  font-weight: 700;
}

.due-summary-cell.m-due--today .due-summary-cell__date,
.due-summary-cell__date.m-due--today {
  color: #e11d48;
  font-weight: 600;
}

.due-summary-cell.m-due--overdue .due-summary-cell__date,
.due-summary-cell__date.m-due--overdue {
  color: #9a3412;
  font-weight: 600;
}

.due-summary-cell.m-due--future .due-summary-cell__date,
.due-summary-cell__date.m-due--future {
  color: #166534;
  font-weight: 600;
}

.due-summary-cell.m-due--completed-ok .due-summary-cell__date,
.due-summary-cell__date.m-due--completed-ok {
  color: #166534;
  font-weight: 600;
}
</style>
